import useMotionTrackingEntitlement, { clearMotionTrackingSession } from "../hooks/useMotionTrackingEntitlement";

// 🔐 USER-SCOPED THEME KEY
const getUserThemeKey = () => {
  try {
    let email =
      localStorage.getItem("email") ||
      sessionStorage.getItem("email") ||
      "";

    if (!email) {
      const raw =
        localStorage.getItem("auth_user") ||
        sessionStorage.getItem("auth_user") ||
        localStorage.getItem("user") ||
        sessionStorage.getItem("user") ||
        "";
      if (raw) {
        const parsed = JSON.parse(raw);
        email = String(parsed?.email || parsed?.user?.email || "").toLowerCase();
      }
    }

    return email ? `ava_theme:${email.toLowerCase()}` : "ava_theme:unknown";
  } catch {
    return "ava_theme:unknown";
  }
};
import { getUnifiedToken } from "../utils/unifiedAuth";
import React, { useState, useEffect, useRef, useContext, useCallback } from "react"
import { ThemeContext } from "../context/ThemeContext";
import {
  usePreferences,
} from "../context/PreferencesContext";
import {
  getAvaaioUiCopy,
} from "../utils/avaaioUiCopy";
import ChatHistoryPanel from "./ChatHistoryPanel";
import {
  useConnectionMode,
} from "../context/ConnectionModeContext";
import {
  detectConnectionModeCommand,
  getConnectionModeCopy,
} from "../utils/connectionModeCopy";

// SAFE GUARD (auto-injected)


const getStoredAuthUserForTheme = () => {
  try {
    const raw =
      localStorage.getItem("auth_user") ||
      localStorage.getItem("ava_user") ||
      localStorage.getItem("user");

    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const authUser =
  getStoredAuthUserForTheme?.() ||
  {};

const user = authUser;



const safeMaybeAddUser = (
  maybeAddUser?: (() => void) | null
) => {
  try {
    if (typeof maybeAddUser === "function") {
      maybeAddUser();
    }
  } catch (e) {
    console.warn("[ChatBox] maybeAddUser blocked safe guard", e);
  }
};

import { detectFeatureFromNames, buildFeatureSummary } from "../config/avaFeatureBridge";
import { useNavigate } from "react-router-dom";
import "../styles/ChatDock.css";

import { hardLogout } from "../utils/auth";
import { getLatestVision } from "../services/avaVisionContext";
import { useUILayout } from "../layout/useUILayout";

const avaMobileQuickTryStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
  width: "100%",
  margin: "0 0 10px 0",
  padding: "0 4px",
  position: "relative",
};

const avaMobileQuickTryLabelStyle: React.CSSProperties = {
  fontSize: 12,
  lineHeight: "16px",
  letterSpacing: "0.06em",
  opacity: 0.78,
  flex: "0 0 auto",
  whiteSpace: "nowrap",
};

const avaMobileQuickTryButtonStyle: React.CSSProperties = {
  fontSize: 12,
  lineHeight: "16px",
  padding: "3px 10px",
  minHeight: 24,
  borderRadius: 999,
  whiteSpace: "nowrap",
};


declare global {
  interface Window {
    SpeechRecognition?: any;
    webkitSpeechRecognition?: any;
  }
}



interface Message {
  id: string;
  sender: "user" | "ava";
  text: string;
  thinking_text?: string;
  request_id?: string;
  is_thinking?: boolean;
  source?: "brain" | "openai" | "error";
  sources?: any[];
  ts: number;
}

type SttMode = "webspeech" | "offline";


function avaTtsSafeText(input: any): string {
  let s = String(input ?? "");

  // Remove emojis and pictographs before browser speech reads them aloud.
  s = s.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, "");

  // Remove markdown/decorative symbols TTS may read aloud.
  s = s.replace(/\*\*/g, "");
  s = s.replace(/\*/g, "");
  s = s.replace(/[•◆◇★☆✨]/g, "");

  // Remove literal emoji narration leaks.
  s = s.replace(/\b(smiling face|smiley face|smile face|grinning face|laughing face|winking face)\b[:,]?\s*/gi, "");
  s = s.replace(/\b(asterisk\s*){1,6}/gi, "");

  
  // Remove narration phrases that model may generate
  s = s.replace(/\b(smiling face|smiley face|smile face|laughing|chuckles|grinning)\b[:,]?\s*/gi, "");

  // =========================================
  // AVAAIO MASTER SPEECH NORMALIZATION
  // =========================================

  // Keep AVAAIO unified as a single platform name

  // Fix AmericanGlobalo pronunciation
  s = s.replace(/American\s*Globalo/gi, "American Global O");

  // =========================================
  // CAPS SOFTENER FOR NATURAL TTS
  // =========================================
  const words = s.split(/\s+/);

  const PRESERVE = new Set([
    "AVAAIO",
    "AI",
    "API",
    "USA",
    "UK",
    "GPU",
    "CPU",
    "HTML",
    "CSS",
    "JSON",
    "SQL",
    "TTS",
    "STT",
    "GPT",
    "NASA"
  ]);

  const capsWords = words.filter(
    (w) =>
      /^[A-Z0-9]+$/.test(w) &&
      w.length > 2 &&
      !PRESERVE.has(w)
  );

  const capsRatio =
    words.length > 0
      ? capsWords.length / words.length
      : 0;

  if (capsRatio > 0.45) {
    s = words
      .map((word) => {
        if (PRESERVE.has(word)) {
          return word;
        }

        if (/^[A-Z0-9]+$/.test(word) && word.length > 2) {
          return (
            word.charAt(0) +
            word.slice(1).toLowerCase()
          );
        }

        return word;
      })
      .join(" ");
  }

  // Preserve Ava identity phrasing
  s = s.replace(/\bI am Ava\b/gi, "I am Ava");
  s = s.replace(/\bI'm Ava\b/gi, "I'm Ava");

  return s.replace(/[ \t]{2,}/g, " ").trim();
}

const API_BASE = (import.meta.env.VITE_API_BASE || "http://127.0.0.1:8000").trim();
const API_BASE_LOCAL = (import.meta.env.VITE_API_BASE_LOCAL || "").trim();
const USER_ID = (import.meta.env.VITE_USER_ID || "joel").trim();
const BRAIN_BASE = (import.meta.env.VITE_BRAIN_BASE || "http://127.0.0.1:8001").trim();

// AVAAIO_CHAT_SPACE_COORDINATION_V1
// Bubble fade starts only after Ava finishes speaking.
const CHAT_SAFE_READ_DELAY_MS = 7000;

const FADE_1 = 0;
const FADE_2 = 2400;
const FADE_3 = 4800;
const FADE_4 = 7200;
const DOCK_IDLE_MS = 5000;
const MAX_MESSAGES = 24;

// === AVAAIO ACCOUNT-SCOPED CHAT HISTORY ===
const getUserChatHistoryKey = () => {
  try {
    let email =
      localStorage.getItem("email") ||
      sessionStorage.getItem("email") ||
      "";

    if (!email) {
      const raw =
        localStorage.getItem("auth_user") ||
        sessionStorage.getItem("auth_user") ||
        localStorage.getItem("ava_user") ||
        sessionStorage.getItem("ava_user") ||
        localStorage.getItem("user") ||
        sessionStorage.getItem("user") ||
        "";

      if (raw) {
        const parsed = JSON.parse(raw);
        email = String(
          parsed?.email ||
          parsed?.user?.email ||
          ""
        ).trim().toLowerCase();
      }
    }

    return email
      ? `avaaio_chat_history:${email.toLowerCase()}`
      : "avaaio_chat_history";
  } catch {
    return "avaaio_chat_history";
  }
};

const loadPersistedChatHistory = (): Message[] => {
  try {
    const raw =
      localStorage.getItem(getUserChatHistoryKey());

    if (!raw) return [];

    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter(
        (item) =>
          item &&
          typeof item.id === "string" &&
          (item.sender === "user" ||
            item.sender === "ava") &&
          typeof item.text === "string" &&
          typeof item.ts === "number" &&
          item.is_thinking !== true
      )
      .slice(-MAX_MESSAGES);
  } catch {
    return [];
  }
};

const LS_VOICE = "ava_voice_shortname_v1";
const LS_FORMAT = "ava_voice_format_v1";
const LS_REGION = "ava_voice_region_v1";

const CONSOLE_MAX = 120;
const OFFLINE_MIN_RECORD_MS = 1400;
const OFFLINE_MIN_BYTES = 8000;

const OFFLINE_WAVE_FRAMES = ["▁▂▃▂▁", "▂▃▄▃▂", "▃▄▅▄▃", "▄▅▆▅▄", "▃▄▅▄▃", "▂▃▄▃▂"] as const;

const MIC_IDLE_AUTO_OFF_MS = 180_000;
const MIC_FINAL_SEND_DEBOUNCE_MS = 650;
const MIC_SILENCE_SEND_MS = 1050;

/**
 * Merge browser speech fragments without repeating completed words
 * or losing the final interim continuation.
 */
function mergeVoiceTranscript(
  existing: string,
  incoming: string
): string {
  const base = String(existing || "")
    .replace(/\s+/g, " ")
    .trim();

  const addition = String(incoming || "")
    .replace(/\s+/g, " ")
    .trim();

  if (!base) return addition;
  if (!addition) return base;

  const normalize = (value: string) =>
    value
      .toLocaleLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, "")
      .replace(/\s+/g, " ")
      .trim();

  const normalizedBase =
    normalize(base);

  const normalizedAddition =
    normalize(addition);

  if (!normalizedAddition) return base;
  if (!normalizedBase) return addition;

  if (
    normalizedAddition ===
    normalizedBase
  ) {
    return base;
  }

  if (
    normalizedAddition.startsWith(
      normalizedBase
    )
  ) {
    return addition;
  }

  if (
    normalizedBase.endsWith(
      normalizedAddition
    )
  ) {
    return base;
  }

  const baseWords =
    base.split(/\s+/);

  const additionWords =
    addition.split(/\s+/);

  const normalizedBaseWords =
    baseWords.map(normalize);

  const normalizedAdditionWords =
    additionWords.map(normalize);

  const maximumOverlap =
    Math.min(
      normalizedBaseWords.length,
      normalizedAdditionWords.length
    );

  let overlap = 0;

  for (
    let size = maximumOverlap;
    size > 0;
    size -= 1
  ) {
    if (
      normalizedBaseWords
        .slice(-size)
        .join(" ") ===
      normalizedAdditionWords
        .slice(0, size)
        .join(" ")
    ) {
      overlap = size;
      break;
    }
  }

  return [
    ...baseWords,
    ...additionWords.slice(overlap),
  ]
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}
const WEBSPEECH_NETWORK_FAIL_LOCK_THRESHOLD = 2;

const STOP_COMMANDS = new Set([
  "stop",
  "cancel",
  "interrupt",
  "pause",
  "hold on",
  "shut up",
  "ava stop",
  "ava cancel",
  "ava interrupt",
  "ava pause",
]);

let unlocked = false;

function markUnlocked() {
  unlocked = true;
}

function ensureUserInteracted(): Promise<void> {
  if (unlocked) return Promise.resolve();

  return new Promise((resolve) => {
    const unlock = () => {
      unlocked = true;
      document.removeEventListener("mousedown", unlock);
      document.removeEventListener("keydown", unlock);
      document.removeEventListener("touchstart", unlock);
      resolve();
    };

    document.addEventListener("mousedown", unlock, { once: true });
    document.addEventListener("keydown", unlock, { once: true });
    document.addEventListener("touchstart", unlock, { once: true });
  });
}

function normalizeCmd(text: string): string {
  try {



  return (text || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, " ");
  } catch {
    return "";
  }
}

function isStopCmd(cmd: string): boolean {
  return STOP_COMMANDS.has(cmd);
}

function getMode(): string {
  try {
    return localStorage.getItem("ava_mode") || "online";
  } catch {
    return "online";
  }
}

function tryLocalMathAnswer(text: string): { ok: boolean; answer?: string } {
  try {
    const raw = String(text || "").trim();

    // Only allow PURE math input
    if (!/^[\d\s+\-*/().]+$/.test(raw)) {
      return { ok: false };
    }

    const cleaned = raw.replace(/\s+/g, "");
    if (!cleaned) return { ok: false };

    const result = Function("return (" + cleaned + ")")();

    if (typeof result === "number" && Number.isFinite(result)) {
      return { ok: true, answer: `Result: ${result}` };
    }
  } catch {}

  return { ok: false };
}










// === AVA SELF REPAIR SYSTEM ===
function isSelfRepairCommand(msg: string): boolean {
  const m = String(msg || "").toLowerCase().trim();

  // Diagnostic mode only. Do NOT catch "ava fix ..." because backend
  // self-fix queue/dev-agent must receive those requests.
  return (
    m === "diagnose yourself" ||
    m === "ava diagnose yourself" ||
    m === "run self check" ||
    m === "self check" ||
    m === "ava self check"
  );
}

async function runAvaSelfDiagnostics(): Promise<string> {
  const lines: string[] = [];
  const API_BASE =
    (import.meta as any)?.env?.VITE_API_BASE ||
    (window as any)?.VITE_API_BASE ||
    "";
  const BRAIN_BASE =
    (import.meta as any)?.env?.VITE_BRAIN_BASE ||
    (window as any)?.VITE_BRAIN_BASE ||
    "https://brain.avaaio.com";

  lines.push("🧠 Ava Self-Diagnostic Mode Activated");
  lines.push("");
  lines.push("Running live checks...");
  lines.push("");

  try {
    lines.push(`• Browser online: ${navigator.onLine ? "yes" : "no"}`);
  } catch {
    lines.push("• Browser online: unknown");
  }

  try {
    const hasToken =
      !!localStorage.getItem("token") ||
      !!localStorage.getItem("access_token") ||
      !!sessionStorage.getItem("token") ||
      !!sessionStorage.getItem("access_token");
    lines.push(`• Session token: ${hasToken ? "present" : "missing"}`);
  } catch {
    lines.push("• Session token: unknown");
  }

  try {
    const meshSeen =
      Array.isArray((window as any).__AVA_MESH_LOGS) &&
      (window as any).__AVA_MESH_LOGS.length > 0;
    lines.push(`• Visual mesh signal: ${meshSeen ? "detected" : "not detected"}`);
  } catch {
    lines.push("• Visual mesh signal: unknown");
  }

  async function probe(url: string, label: string) {
    try {
      const r = await fetch(url, { method: "GET", credentials: "include" });
      lines.push(`• ${label}: ${r.ok ? "OK" : `HTTP ${r.status}`}`);
      return r.ok;
    } catch (e: any) {
      lines.push(`• ${label}: unreachable`);
      return false;
    }
  }

  const apiOk = await probe(`${API_BASE}/api/health`, "API health");
  const brainOk = await probe(`${BRAIN_BASE}/`, "Brain endpoint");

  lines.push("");
  if (apiOk && brainOk) {
    lines.push("Status: core services reachable.");
  } else if (apiOk || brainOk) {
    lines.push("Status: partial service availability.");
  } else {
    lines.push("Status: network or service issue detected.");
  }

  lines.push("Next action: I can now report the failing layer more precisely.");
  return lines.join("\n");
}
// === END AVA SELF REPAIR SYSTEM ===



// === AVA REAL SYSTEM AWARENESS ===
function isSystemAwarenessQuery(msg: string): boolean {
  const m = String(msg || "").toLowerCase().trim();
  return (
    /\bwhat day is today\b/.test(m) ||
    /\bwhat(?:'s| is)? the date\b/.test(m) ||
    false && /\bwhat time is it\b/.test(m) ||
    /\bcurrent time\b/.test(m) ||
    /\bare you online\b/.test(m) ||
    /\bapi status\b/.test(m) ||
    /\bbrain status\b/.test(m) ||
    /\brun self check\b/.test(m) ||
    /\bself check\b/.test(m)
  );
}

async function probeStatus(url: string): Promise<"OK" | "unreachable" | `HTTP ${number}`> {
  try {
    const r = await fetch(url, { method: "GET", credentials: "include" });
    if (r.ok) return "OK";
    return `HTTP ${r.status}`;
  } catch {
    return "unreachable";
  }
}

async function buildSystemAwarenessReply(msg: string): Promise<string> {
  const m = String(msg || "").toLowerCase().trim();
  const now = new Date();

  const API_BASE =
    (import.meta as any)?.env?.VITE_API_BASE ||
    "";

  const BRAIN_BASE =
    (import.meta as any)?.env?.VITE_BRAIN_BASE ||
    "https://brain.avaaio.com";

  if (/\bwhat day is today\b/.test(m) || /\bwhat(?:'s| is)? the date\b/.test(m)) {
    const formatted = now.toLocaleDateString(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    return `Today is ${formatted}.`;
  }

  if (false) { // ⛔ disabled: force backend time handling
    const formatted = now.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });
    return `The current time is ${formatted}.`;
  }

  if (/\bare you online\b/.test(m)) {
    return `Browser online status: ${navigator.onLine ? "yes" : "no"}.`;
  }

  if (/\bapi status\b/.test(m)) {
    const api = await probeStatus(`${API_BASE}/api/health`);
    return `API status: ${api}.`;
  }

  if (/\bbrain status\b/.test(m)) {
    const brain = await probeStatus(`${BRAIN_BASE}/`);
    return `Brain status: ${brain}.`;
  }

  if (/\brun self check\b/.test(m) || /\bself check\b/.test(m)) {
    const api = await probeStatus(`${API_BASE}/api/health`);
    const brain = await probeStatus(`${BRAIN_BASE}/`);
    const online = navigator.onLine ? "yes" : "no";

    return [
      "🧠 Ava System Check",
      "",
      `• Browser online: ${online}`,
      `• API health: ${api}`,
      `• Brain endpoint: ${brain}`,
    ].join("\n");
  }

  return "";
}
// === END AVA REAL SYSTEM AWARENESS ===


// === AVA SANDBOX DIAGNOSTICS + REPAIR ROUTER ===
function isSandboxRepairCommand(msg: string): boolean {
  const m = String(msg || "").toLowerCase().trim();
  return (
    false || // backend-first: do not locally intercept ava status
    m === "ava diagnose" ||
    m === "ava diagnostics" ||
    false || // backend-first repair
    false || // backend-first repair
    false || // backend-first repair
    false || // backend-first repair
    false // backend-first repair
  );
}

function getSandboxRepairTarget(msg: string): string {
  const m = String(msg || "").toLowerCase().trim();
  if (m.includes("helmet")) return "helmet";
  if (m.includes("orb")) return "orb";
  if (m.includes("dashboard")) return "dashboard";
  if (m.includes("chat ui") || m.includes("chat")) return "chat_ui";
  return "general";
}

async function fetchAvaDiagnostics(): Promise<string> {
  const API_BASE =
    (import.meta as any)?.env?.VITE_API_BASE ||
    "";

  try {
    const r = await fetch(`${API_BASE}/api/v1/diagnostics`, {
      method: "GET",
      credentials: "include",
      headers: {
        ...(localStorage.getItem("token")
          ? { Authorization: `Bearer ${localStorage.getItem("token")}` }
          : {}),
      },
    });

    if (!r.ok) {
      return `🧠 Ava Diagnostics

Diagnostics endpoint returned HTTP ${r.status}.`;
    }

    const data = await r.json().catch(() => ({}));

    

        console.log(
          "[CHAT REQUEST SUCCESS]"
        );

const lines = [
      "🧠 Ava Diagnostics",
      "",
      `• Environment: sandbox`,
      `• API diagnostics: OK`,
      `• Diagnostics payload: ${data && typeof data === "object" ? "received" : "empty"}`,
    ];

    if (typeof data?.brain_ok !== "undefined") {
      lines.push(`• Brain ok: ${data.brain_ok ? "yes" : "no"}`);
    }
    if (typeof data?.chat_ok !== "undefined") {
      lines.push(`• Chat ok: ${data.chat_ok ? "yes" : "no"}`);
    }
    if (data?.message) {
      lines.push(`• Note: ${String(data.message)}`);
    }

    return lines.join("\n");
  } catch (e) {
    return "🧠 Ava Diagnostics\n\nDiagnostics endpoint is unreachable from sandbox.";
  }
}

async function buildSandboxRepairReply(msg: string): Promise<string> {
  const env =
    (import.meta as any)?.env?.VITE_ENVIRONMENT ||
    "unknown";

  if (String(env).toLowerCase() !== "sandbox") {
    return "Sandbox-only repair is blocked outside sandbox.";
  }

  const target = getSandboxRepairTarget(msg);

  if (
    false || // backend-first: do not intercept "ava status" locally
    msg === "ava diagnose" ||
    msg === "ava diagnostics"
  ) {
    return await fetchAvaDiagnostics();
  }

  return ''; // backend-first: safe fallback string
}
// === END AVA SANDBOX DIAGNOSTICS + REPAIR ROUTER ===


function normalizeDiagnoseFeature(msg: string): string {
  const m = String(msg || "").toLowerCase().trim();
  if (m.includes("eye")) return "eye";
  if (m.includes("mouth")) return "mouth";
  if (m.includes("horn")) return "horn";
  if (m.includes("orb")) return "orb";
  if (m.includes("helmet")) return "helmet";
  return "";
}


function normalizeProposeFeature(msg: string): string {
  const m = String(msg || "").toLowerCase();
  if (m.includes("eye")) return "eye";
  if (m.includes("horn")) return "horn";
  if (m.includes("helmet")) return "helmet";
  if (m.includes("orb")) return "orb";
  return "";
}

function buildRepairProposal(feature: string, count: number): string {
  const header = ["🧠 Ava Repair Proposal", "", `Target: ${feature}`, `Detected: ${count} components`, ""];
  
  const options = [
    "1. Preserve Theme + Enhance Glow",
    "2. Neon Cyan Upgrade",
    "3. Soft White Core",
    "4. Aggressive Hover Motion",
    "5. Stabilized Hover Mode"
  ];

  const footer = [
    "",
    "Next step:",
    `Say: apply ${feature} option <number>`
  ];

  return [...header, "Available upgrades:", "", ...options, ...footer].join("\n");
}

function buildFeatureDiagnosis(feature: string, count: number): string {
  const targetFile = "/home/ubuntu/avaai_dashboard/source/src/components/AvaFace.tsx";

  if (feature == "eye") {
    return [
      "🧠 Ava Feature Diagnosis",
      "",
      "Target feature: eye",
      `Target file: ${targetFile}`,
      `Detected count: ${count}`,
      "Safe changes:",
      "- emissive intensity",
      "- emissive color",
      "- open / close behavior",
      "User choice required:",
      "- yes for color",
      "- no for technical brightness repair",
    ].join("\n");
  }

  if (feature == "mouth") {
    return [
      "🧠 Ava Feature Diagnosis",
      "",
      "Target feature: mouth",
      `Target file: ${targetFile}`,
      `Detected count: ${count}`,
      "Safe changes:",
      "- mouth glow intensity",
      "- mouth emissive color",
      "- mouth visibility tuning",
      "User choice required:",
      "- yes for color",
      "- no for technical intensity repair",
    ].join("\n");
  }

  if (feature == "horn") {
    return [
      "🧠 Ava Feature Diagnosis",
      "",
      "Target feature: horn system",
      `Target file: ${targetFile}`,
      `Detected count: ${count}`,
      "Safe changes:",
      "- horn emissive intensity",
      "- left/right horn color",
      "- horn glow balance",
      "User choice required:",
      "- yes for color profile",
      "- no for technical emissive repair",
    ].join("\n");
  }

  if (feature == "orb") {
    return [
      "🧠 Ava Feature Diagnosis",
      "",
      "Target feature: orb",
      `Target file: ${targetFile}`,
      `Detected count: ${count}`,
      "Safe changes:",
      "- orb color",
      "- orb intensity",
      "- lens / visor visibility",
      "User choice required:",
      "- yes for style/color",
      "- no for technical visibility repair",
    ].join("\n");
  }

  if (feature == "helmet") {
    return [
      "🧠 Ava Feature Diagnosis",
      "",
      "Target feature: helmet",
      `Target file: ${targetFile}`,
      `Detected count: ${count}`,
      "Safe changes:",
      "- helmet glow intensity",
      "- panel emissive balance",
      "- hover motion direction",
      "- hover amplitude and speed",
      "User choice required:",
      "- yes for color and motion style",
      "- no for technical map/visibility repair",
    ].join("\n");
  }

  return [
    "🧠 Ava Feature Diagnosis",
    "",
    "Feature not recognized.",
    "Try: ava diagnose eye | mouth | horn | orb | helmet",
  ].join("\n");
}


function parseReply(data: any): string {
  return String(
    data?.reply ??
    data?.response ??
    data?.message ??
    data?.text ??
    "No response."
  );
}

function didUseFallback(data: any): boolean {
  return String(data?.source || "").toLowerCase() === "openai";
}

function getLocalHelpText(): string {
  return [
    "🧠 Ava Command Center",
    "",
    "Try saying things like:",
    "",
    "• help",
    "• status",
    "• commands",
    "• open profile",
    "• open account",
    "• open games",
    "• open education",
    "• open support",
    "• change theme to blue",
    "• remember my favorite color is red",
    "• what is my plan",
    "• help me learn math",
    "",
    "Tip:",
    "Talk to Ava naturally for normal conversation and assistance."
  ].join("\n");
}

function buildStatusText(info: any): string {
  const plan =
    info?.plan ||
    localStorage.getItem("ava_chat_plan") ||
    "Free";

  const memory =
    localStorage.getItem("ava_memory_learning_enabled") === "false"
      ? "Disabled"
      : "Available";

  return [
    "🧠 Ava Status",
    "",
    `Plan: ${plan}`,
    "System: Online",
    "Chat: Ready",
    `Memory: ${memory}`,
    "Learning Tools: Ready",
    "Account: Connected",
  ].join("\n");
}

function uid(): string {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function joinUrl(base: string, path: string): string {
  return `${(base || "").replace(/\/+$/, "")}/${(path || "").replace(/^\/+/, "")}`;
}

function pickApiBase(): string {
  return API_BASE_LOCAL || API_BASE;
}

function getToken(): string {
    return getUnifiedToken();
  }



const avaApplyThemeColorVars = (color: string) => {
  try {
    const root = document.documentElement;
    const aliases: Record<string, string> = {
      red: "lava",
      crimson: "lava",
      blue: "midnight",
      green: "emerald",
      orange: "amber",
      yellow: "amber",
      gold: "amber",
      pink: "aurora",
      white: "cyan",
    };

    const requested = String(color || "")
      .trim()
      .toLowerCase();

    const canonical =
      aliases[requested] || requested || "cyan";

    const themeMap: Record<string, [string, string]> = {
      cyan: ["#16cfe0", "#5ce3ed"],
      purple: ["#9b6ddb", "#c09dec"],
      emerald: ["#36b86b", "#78d89a"],
      amber: ["#c89232", "#e2bd70"],
      lava: ["#c84a52", "#e18489"],
      midnight: ["#527fbd", "#8badde"],
      aurora: ["#d85fa8", "#55d6c9"],
      black: ["#3f4856", "#606b7b"],
    };

    const safeTheme =
      canonical in themeMap
        ? canonical
        : "cyan";

    const colors = themeMap[safeTheme];

    root.style.setProperty("--ava-accent", colors[0]);
    root.style.setProperty("--ava-accent-2", colors[1]);
    root.style.setProperty("--neon-color", colors[0]);

    localStorage.setItem(getUserThemeKey(), safeTheme);
    localStorage.setItem(
      getUserThemeKey() + ":color",
      safeTheme,
    );

    window.dispatchEvent(
      new CustomEvent("ava-theme-change", {
        detail: { color: safeTheme },
      }),
    );
  } catch (e) {
    console.warn("Theme var apply failed", e);
  }
};


const avaIsThemeOrbColorCommand = (value: string): boolean => {
  const s = String(value || "").toLowerCase();
  const colors = ["red", "cyan", "blue", "purple", "green", "orange", "yellow", "pink", "white", "black"];
  return (
    colors.some((c) => new RegExp(`\\b${c}\\b`).test(s)) &&
    ["orb", "theme", "color", "colour", "eye", "glow"].some((k) => s.includes(k)) &&
    ["change", "set", "make", "turn", "switch"].some((k) => s.includes(k))
  );
};
const ChatBox: React.FC = () => {
    const { device, mode, layout } = useUILayout();

  const { themeColors, setTheme, theme } = useContext(ThemeContext);
  const { preferences } = usePreferences();

  // AVAAIO_CONNECTION_MODE_CHAT_HOOK_START
  const {
    state: connectionMode,
    setMode: setConnectionMode,
    reconcileFromChatPayload,
  } = useConnectionMode();

  const connectionModeCopy =
    getConnectionModeCopy(
      preferences.language
    );

  const connectionModeRef =
    useRef(connectionMode);

  const connectionModeCopyRef =
    useRef(connectionModeCopy);

  const preferencesLanguageRef =
    useRef(preferences.language);

  connectionModeRef.current =
    connectionMode;

  connectionModeCopyRef.current =
    connectionModeCopy;

  preferencesLanguageRef.current =
    preferences.language;

  const isConnectionVoiceInputBlocked =
    useCallback((): boolean => {
      const current =
        connectionModeRef.current;

      return (
        !current.confirmed ||
        !current.voiceInputAvailable ||
        current.cloudServicesBlocked ||
        current.requestedMode === "local" ||
        current.effectiveMode === "local" ||
        current.effectiveMode === "disconnected"
      );
    }, []);
  // AVAAIO_CONNECTION_MODE_CHAT_HOOK_END

  const interfaceCopy = getAvaaioUiCopy(
    preferences.language
  );

  const interfaceCopyRef =
    useRef(interfaceCopy);

  useEffect(() => {
    interfaceCopyRef.current =
      interfaceCopy;
  }, [interfaceCopy]);

  useEffect(() => {
    try {
      // === FREE USER THEME GUARD ===
      let savedTheme = null;
      try {
        const storedUser = getStoredAuthUserForTheme();
        const role = String(storedUser?.role || localStorage.getItem("ava_user_role") || localStorage.getItem("role") || "").toLowerCase();
        const sub = String(storedUser?.subscription_status || localStorage.getItem("subscription_status") || "").toLowerCase();
        const isAdmin = ["admin","owner","superadmin"].includes(role);
        const isPro = role === "vip" || sub === "active";

        if (isAdmin || isPro) {
          savedTheme = localStorage.getItem(getUserThemeKey()) 
            || localStorage.getItem("ava_theme") 
            || localStorage.getItem(getUserThemeKey()+":color") 
            || localStorage.getItem("ava_theme_color");
        } else {
          savedTheme = "black";
          localStorage.setItem(getUserThemeKey(), "black");
          localStorage.setItem(getUserThemeKey()+":color", "black");
          window.dispatchEvent(new CustomEvent("ava-theme-change", { detail: { color: "black" } }));
        }
      } catch {};
      if (savedTheme) avaApplyThemeColorVars(savedTheme);
    } catch {}
  }, []);

  const navigate = useNavigate();

  const handleFriendlyNavigationCommand = (msg: string): boolean => {
    const m = String(msg || "").toLowerCase().trim().replace(/^ava\s+/, "");

    if (m.includes("open profile")) {
      navigate("/profile");
      return true;
    }

    if (m.includes("open account")) {
      navigate("/account");
      return true;
    }

    if (m.includes("open games")) {
      navigate("/games");
      return true;
    }

    if (
      m.includes("open education") ||
      m === "education" ||
      m.includes("learning tools") ||
      m.includes("learn with ava") ||
      m.includes("math board") ||
      m.includes("ava tutor")
    ) {
      navigate("/education");
      return true;
    }

    if (m.includes("open support")) {
      navigate("/support");
      return true;
    }

    if (m.includes("open settings")) {
      navigate("/settings");
      return true;
    }

    return false;
  };

  // Navigation helpers (FIX: Community & Media routing)
  const goCommunity = () => {
    window.location.href = "/community";
  };

  const goMedia = () => {
    window.location.href = "/media";
  };


  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    typeof window !== "undefined" ? window.innerWidth : 1440
  );
  const [viewportHeight, setViewportHeight] = useState<number>(() =>
    typeof window !== "undefined" ? window.innerHeight : 900
  );
  const [visualViewportHeight, setVisualViewportHeight] = useState<number>(() =>
    typeof window !== "undefined"
      ? window.visualViewport?.height || window.innerHeight
      : 900
  );

  // AVAAIO MOBILE UX PHASE 2:
  // Keep the layout viewport stable while the iOS keyboard changes
  // the smaller visual viewport.
  const viewportBaselineRef = useRef<{
    orientation: "portrait" | "landscape";
    width: number;
    height: number;
  }>({
    orientation:
      typeof window !== "undefined" &&
      window.innerWidth > window.innerHeight
        ? "landscape"
        : "portrait",
    width:
      typeof window !== "undefined"
        ? window.innerWidth
        : 1440,
    height:
      typeof window !== "undefined"
        ? window.innerHeight
        : 900,
  });

  useEffect(() => {
    let syncRaf: number | null = null;

    const readOrientation = (): "portrait" | "landscape" => {
      try {
        return window.matchMedia("(orientation: landscape)").matches
          ? "landscape"
          : "portrait";
      } catch {
        return window.innerWidth > window.innerHeight
          ? "landscape"
          : "portrait";
      }
    };

    const syncSize = () => {
      if (syncRaf !== null) {
        cancelAnimationFrame(syncRaf);
      }

      syncRaf = requestAnimationFrame(() => {
        const nextWidth = window.innerWidth;
        const rawLayoutHeight = window.innerHeight;
        const nextVVH =
          window.visualViewport?.height || rawLayoutHeight;
        const nextOrientation = readOrientation();

        const baseline = viewportBaselineRef.current;

        const orientationChanged =
          baseline.orientation !== nextOrientation;

        const widthShifted =
          Math.abs(baseline.width - nextWidth) > 120;

        if (orientationChanged || widthShifted) {
          viewportBaselineRef.current = {
            orientation: nextOrientation,
            width: nextWidth,
            height: Math.max(rawLayoutHeight, nextVVH),
          };
        } else {
          const keyboardLikely =
            baseline.height - nextVVH > 100;

          baseline.orientation = nextOrientation;
          baseline.width = nextWidth;

          if (!keyboardLikely) {
            baseline.height = Math.max(
              rawLayoutHeight,
              nextVVH
            );
          }
        }

        const stableHeight =
          viewportBaselineRef.current.height;

        setViewportWidth(prev =>
          Math.abs(prev - nextWidth) < 2
            ? prev
            : nextWidth
        );

        setViewportHeight(prev =>
          Math.abs(prev - stableHeight) < 4
            ? prev
            : stableHeight
        );

        setVisualViewportHeight(prev =>
          Math.abs(prev - nextVVH) < 4
            ? prev
            : nextVVH
        );

        document.documentElement.style.setProperty(
          "--avaaio-visual-viewport-height",
          `${Math.round(nextVVH)}px`
        );

        document.documentElement.style.setProperty(
          "--avaaio-keyboard-inset",
          `${Math.max(
            0,
            Math.round(stableHeight - nextVVH)
          )}px`
        );
      });
    };

    syncSize();

    window.addEventListener("resize", syncSize);
    window.addEventListener("orientationchange", syncSize);
    window.visualViewport?.addEventListener("resize", syncSize);

    return () => {
      if (syncRaf !== null) {
        cancelAnimationFrame(syncRaf);
      }

      window.removeEventListener("resize", syncSize);
      window.removeEventListener("orientationchange", syncSize);
      window.visualViewport?.removeEventListener(
        "resize",
        syncSize
      );
    };
  }, []);

  const isPhone = viewportWidth <= 640;
  const isTablet = viewportWidth > 640 && viewportWidth <= 1024;
  const isCompact = viewportWidth <= 900;
  const isLandscape = viewportWidth > viewportHeight;

  const isPhoneLandscape =
    isLandscape &&
    (
      viewportHeight <= 430 ||
      (viewportWidth <= 932 && viewportHeight <= 480) ||
      (viewportWidth <= 960 && viewportHeight <= 500)
    );

  const keyboardThreshold = isPhoneLandscape ? 110 : 140;

  const keyboardOpen =
    (isPhone || isPhoneLandscape) &&
    visualViewportHeight < viewportHeight - keyboardThreshold;

  const iphoneLandscapeKeyboardOpen =
    isPhoneLandscape && keyboardOpen;

  const bottomDockHeight = iphoneLandscapeKeyboardOpen
    ? 60
    : keyboardOpen
    ? 58
    : isPhoneLandscape
    ? 92
    : isPhone
    ? 96
    : isTablet
    ? 98
    : 92;


  // AVAAIO MOBILE UX PHASE 2B:
  // The landscape chat shell already sits above the bottom dock.
  // Large internal bottom padding was consuming the message viewport.
  const feedBottomPadding = isPhoneLandscape
    ? 8
    : keyboardOpen
    ? bottomDockHeight + 10
    : bottomDockHeight + 12;

  // AVAAIO MOBILE UX PHASE 2:
  // Calculate conversation space from the actual visible viewport.
  const keyboardFeedMaxHeight = Math.max(
    iphoneLandscapeKeyboardOpen ? 96 : 180,
    Math.floor(
      visualViewportHeight -
      bottomDockHeight -
      (iphoneLandscapeKeyboardOpen ? 14 : 96)
    )
  );

  // AVAAIO MOBILE UX PHASE 2B:
  // Preserve real reading room in iPhone landscape.
  const landscapeFeedMaxHeight = Math.max(
    112,
    Math.floor(
      visualViewportHeight -
      bottomDockHeight -
      8
    )
  );

  useEffect(() => {
    const onMobileMic = () => {
      try {
        const micBtn = document.querySelector(".console-btn.mic") as HTMLButtonElement | null;
        micBtn?.click();
      } catch (err) {
        console.error("Mobile mic toggle failed:", err);
      }
    };

    const onMobileMute = () => {
      try {
        const muteBtn = document.querySelector(".console-btn.mute") as HTMLButtonElement | null;
        muteBtn?.click();
      } catch (err) {
        console.error("Mobile mute toggle failed:", err);
      }
    };

    const onMobileLogout = () => {
      try {
        setIsLoggingOutVisual(true);
        setShowSettings(false);
        stopAvaNow();

        // Fade the dashboard first so auth/layout collapse is not visible.
        window.setTimeout(() => {
          try {
            hardLogout();
          } catch (err) {
            console.error("Mobile logout failed:", err);
          }
        }, 180);
      } catch (err) {
        console.error("Mobile logout failed:", err);
      }
    };

    window.addEventListener("ava-mobile-mic-toggle", onMobileMic as EventListener);
    window.addEventListener("ava-mobile-mute-toggle", onMobileMute as EventListener);
    window.addEventListener("ava-mobile-logout", onMobileLogout as EventListener);

    return () => {
      window.removeEventListener("ava-mobile-mic-toggle", onMobileMic as EventListener);
      window.removeEventListener("ava-mobile-mute-toggle", onMobileMute as EventListener);
      window.removeEventListener("ava-mobile-logout", onMobileLogout as EventListener);
    };
  }, [navigate]);

  const [messages, setMessages] =
    useState<Message[]>(() => loadPersistedChatHistory());
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);

  useEffect(() => {
    try {
      const durableMessages =
        messages
          .filter(
            (message) =>
              message &&
              message.is_thinking !== true &&
              (message.sender === "user" ||
                message.sender === "ava") &&
              typeof message.text === "string" &&
              message.text.trim().length > 0
          )
          .slice(-MAX_MESSAGES);

      localStorage.setItem(
        getUserChatHistoryKey(),
        JSON.stringify(durableMessages)
      );
    } catch (err) {
      console.warn(
        "[ChatHistory] persistence failed:",
        err
      );
    }
  }, [messages]);

  const clearAllChatHistory =
    useCallback(() => {
      try {
        localStorage.removeItem(
          getUserChatHistoryKey()
        );
      } catch {}

      setMessages([]);
    }, []);
  const [chatPlan, setChatPlan] = useState<string>("free");
  const [dailyLimit, setDailyLimit] = useState<number | null>(null);
  const [wordsUsed, setWordsUsed] = useState<number>(0);
  const [chatLock, setChatLock] = useState<any>({
    limit_reached: false,
    locked_until: null,
    features_locked: {
      chat: false,
      mic: false,
      mute: false,
      eye: false,
    },
  });
  const [input, setInput] = useState("");

  const avaCommandHints = [
    "ava help",
    "ava diagnostics",
    "ava repair dashboard",
    "ava change theme to red",
    "ava status",
  ];
  const [isListening, setIsListening] = useState(false);
  const [isMicOn, setIsMicOn] = useState(false);

  // __AVA_WAKE_WORD_PERSISTENT_MIC_PHASE1__
  const [
    micConversationMode,
    setMicConversationMode,
  ] = useState<
    | "off"
    | "wake_standby"
    | "conversation_active"
    | "ava_speaking_lockout"
    | "post_speech_cooldown"
  >("off");
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // === PAUSE TIME FREEZE SYSTEM ===
  const [pauseStartedAt, setPauseStartedAt] = useState<number | null>(null);
  const [totalPausedTime, setTotalPausedTime] = useState(0);

  const [readingPausedAt, setReadingPausedAt] = useState<number | null>(null);
  const [scrollbarVisible, setScrollbarVisible] = useState(true);
  const scrollbarFadeTimerRef = useRef<number | null>(null);

  const resetChatScrollbarFade = useCallback((delayMs = 9000) => {
    setScrollbarVisible(true);
    if (scrollbarFadeTimerRef.current) {
      window.clearTimeout(scrollbarFadeTimerRef.current);
    }
    scrollbarFadeTimerRef.current = window.setTimeout(() => {
      if (!isAvaSpeakingRef.current) setScrollbarVisible(false);
    }, delayMs);
  }, []);
  const [readingPauseOffsetMs, setReadingPauseOffsetMs] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [dockVisible, setDockVisible] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [isLoggingOutVisual, setIsLoggingOutVisual] = useState(false);
  const [clock, setClock] = useState("");
  const [dateStr, setDateStr] = useState("");
  const [nowTick, setNowTick] = useState(Date.now());
  const [avaEyeOpen, setAvaEyeOpen] = useState(false);

  // =====================================================
  // AVA DEVICE MOTION LOOK CONTROL
  //
  // Apple requires DeviceOrientation permission to be
  // requested directly from a user button interaction.
  // =====================================================

  const {
    motionTrackingAllowed,
  } = useMotionTrackingEntitlement();

  const [deviceMotionEnabled, setDeviceMotionEnabled] =
    useState<boolean>(false);

  useEffect(() => {
    if (motionTrackingAllowed) return;

    clearMotionTrackingSession();
    setDeviceMotionEnabled(false);

    window.dispatchEvent(
      new CustomEvent("ava-device-motion-toggle", {
        detail: {
          enabled: false,
          recalibrate: false,
          source: "entitlement-lock",
        },
      })
    );
  }, [motionTrackingAllowed]);

  const handleDeviceMotionToggle = useCallback(async () => {
    const nextEnabled = !deviceMotionEnabled;

    if (nextEnabled && !motionTrackingAllowed) {
      clearMotionTrackingSession();
      setDeviceMotionEnabled(false);

      window.alert(
        "Ava Motion Tracking is available with a paid AVAAIO subscription."
      );

      return;
    }

    if (nextEnabled) {
      try {
        const OrientationEvent =
          (window as any).DeviceOrientationEvent;

        if (!OrientationEvent) {
          console.warn(
            "[AVA MOTION] Device orientation API unavailable"
          );

          window.alert(
            "Device motion is not available in this browser or app view."
          );

          return;
        }

        let permissionAlreadyGranted = false;

        try {
          permissionAlreadyGranted =
            sessionStorage.getItem(
              "ava_device_motion_permission_granted"
            ) === "1";
        } catch {}

        if (
          typeof OrientationEvent.requestPermission ===
            "function" &&
          !permissionAlreadyGranted
        ) {
          const permission =
            await OrientationEvent.requestPermission();

          if (permission !== "granted") {
            console.warn(
              "[AVA MOTION] Device orientation permission denied"
            );

            window.alert(
              "Ava Motion Track remains off because Motion & Orientation Access was not allowed."
            );

            return;
          }

          try {
            sessionStorage.setItem(
              "ava_device_motion_permission_granted",
              "1"
            );
          } catch {}
        }
      } catch (error) {
        console.warn(
          "[AVA MOTION] Unable to request orientation permission",
          error
        );

        return;
      }
    }

    try {
      sessionStorage.setItem(
        "ava_device_motion_look",
        nextEnabled ? "1" : "0"
      );
    } catch {
      // Continue even when session storage is unavailable.
    }

    setDeviceMotionEnabled(nextEnabled);

    window.dispatchEvent(
      new CustomEvent("ava-device-motion-toggle", {
        detail: {
          enabled: nextEnabled,
          recalibrate: nextEnabled,
        },
      })
    );
  }, [
    deviceMotionEnabled,
    motionTrackingAllowed,
  ]);

  useEffect(() => {
    const onMobileMotionToggle = (event: Event) => {
      const custom = event as CustomEvent<{
        enabled?: boolean;
        permissionGranted?: boolean;
      }>;

      const nextEnabled =
        Boolean(custom?.detail?.enabled);

      if (nextEnabled && !motionTrackingAllowed) {
        console.warn(
          "[AVA MOTION] Mobile activation blocked by entitlement."
        );

        clearMotionTrackingSession();
        setDeviceMotionEnabled(false);

        return;
      }

      // Permission was requested directly by the mobile-orb
      // button before this event was dispatched.
      if (
        nextEnabled &&
        !custom?.detail?.permissionGranted
      ) {
        console.warn(
          "[AVA MOTION] Activation blocked without permission confirmation"
        );

        return;
      }

      try {
        sessionStorage.setItem(
          "ava_device_motion_look",
          nextEnabled ? "1" : "0"
        );
      } catch {}

      setDeviceMotionEnabled(nextEnabled);

      window.dispatchEvent(
        new CustomEvent("ava-device-motion-toggle", {
          detail: {
            enabled: nextEnabled,
            recalibrate: nextEnabled,
          },
        })
      );
    };

    window.addEventListener(
      "ava-mobile-motion-toggle",
      onMobileMotionToggle as EventListener
    );

    return () => {
      window.removeEventListener(
        "ava-mobile-motion-toggle",
        onMobileMotionToggle as EventListener
      );
    };
  }, [motionTrackingAllowed]);

  const [voiceChoice, setVoiceChoice] = useState("");
  const [formatChoice, setFormatChoice] = useState("");
  const [regionChoice, setRegionChoice] = useState("uksouth");
  const [sttModeLabel, setSttModeLabel] = useState<SttMode>("webspeech");
  const [, setOfflineWaveTick] = useState(0);

  const storedRole =
    typeof window !== "undefined"
      ? (() => {
          const directRole =
            localStorage.getItem("ava_user_role") ||
            sessionStorage.getItem("ava_user_role") ||
            localStorage.getItem("role") ||
            sessionStorage.getItem("role") ||
            localStorage.getItem("ava_chat_plan") ||
            sessionStorage.getItem("ava_chat_plan") ||
            "";

          const userBlob =
            localStorage.getItem("auth_user") ||
            sessionStorage.getItem("auth_user") ||
            localStorage.getItem("ava_user") ||
            sessionStorage.getItem("ava_user") ||
            localStorage.getItem("user") ||
            sessionStorage.getItem("user") ||
            localStorage.getItem("userProfile") ||
            sessionStorage.getItem("userProfile") ||
            localStorage.getItem("profile") ||
            sessionStorage.getItem("profile") ||
            "";

          let parsedRole = "";
          let parsedEmail = "";

          if (userBlob) {
            try {
              const obj = JSON.parse(userBlob);
              parsedRole = String(obj?.role || (obj as any)?.user?.role || "").toLowerCase();
              parsedEmail = String(obj?.email || obj?.user?.email || "").toLowerCase();
            } catch {
              parsedRole = "";
              parsedEmail = "";
            }
          }

          const directEmail =
            String(
              localStorage.getItem("email") ||
              sessionStorage.getItem("email") ||
              ""
            ).toLowerCase();

          const effectiveEmail = directEmail || parsedEmail;

          return String(directRole || parsedRole || "").toLowerCase();
        })()
      : "";
  const isAdminUser = ["admin", "owner", "superadmin"].includes(String(storedRole || "").toLowerCase());
  const isFreeUser = String(chatPlan || "").toLowerCase() === "free";

  const numericDailyLimit =
    typeof dailyLimit === "number" ? dailyLimit : Number(dailyLimit || 0);

  const numericWordsUsed =
    typeof wordsUsed === "number" ? wordsUsed : Number(wordsUsed || 0);

  const hasValidDailyLimit =
  isFreeUser &&
  Number.isFinite(numericDailyLimit) &&
  numericDailyLimit >= 50;

  const wordsRemaining =
    isFreeUser && hasValidDailyLimit
      ? Math.max(0, numericDailyLimit - numericWordsUsed)
      : null;

  const limitReached =
    !isAdminUser &&
    isFreeUser &&
    hasValidDailyLimit &&
    numericWordsUsed >= numericDailyLimit;

  const chatLocked = !isAdminUser && limitReached;
  const micLocked = !isAdminUser && limitReached;
  const muteLocked = !isAdminUser && limitReached;
  const eyeLocked = !isAdminUser && limitReached;
  const sendLocked = !isAdminUser && limitReached;

  const isMutedRef = useRef(false);
  const recognitionRef = useRef<any>(null);
  const speechEndTimerRef = useRef<number | null>(null);
  const micToggleLockRef = useRef(false);
  const micStartedAtRef = useRef(0);
  const settingsRef = useRef<HTMLDivElement | null>(null);
  
const chatEndRef = useRef<HTMLDivElement | null>(null);

// =====================================================
// AVAAIO DRAG HUD SYSTEM
// =====================================================

const [hudPosition, setHudPosition] = useState(() => {
  try {
    const saved = localStorage.getItem("avaaio_landscape_ava_position");
    if (saved) return JSON.parse(saved);
  } catch {}

  return { x: 0, y: 260 };
});


const [showHudBeacon, setShowHudBeacon] = useState(true);

const [showRecoveryHint, setShowRecoveryHint] =
  useState(false);

const dragHudRef = useRef({
  active: false,
  startX: 0,
  startY: 0,
  originX: 0,
  originY: 0,
});

  const lastInteractionRef = useRef(Date.now());

  const audioRef = useRef<HTMLAudioElement | null>(null);

  // __AVA_IOS_TTS_PHASE1__
  // Keep one reusable player across repeated iPhone/iPad replies.
  const ttsPlayerRef = useRef<HTMLAudioElement | null>(null);
  const ttsPlaybackEpochRef = useRef(0);
  const ttsRequestEpochRef = useRef(0);
  const micChimeCtxRef = useRef<AudioContext | null>(null);

// 🎤 Mouth Sync System
const [mouthLevel, setMouthLevel] = useState(0);
const audioCtxRef = useRef<AudioContext | null>(null);
const analyserRef = useRef<AnalyserNode | null>(null);
const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
const mouthAnimRef = useRef<number | null>(null);

const stopMouthTracking = useCallback(() => {
  if (mouthAnimRef.current) {
    cancelAnimationFrame(mouthAnimRef.current);
    mouthAnimRef.current = null;
  }
  setMouthLevel(0);
}, []);

const startMouthTracking = useCallback((audio: HTMLAudioElement) => {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;

    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioCtx();
    }

    const ctx = audioCtxRef.current;

    if (
      ctx.state === "suspended" ||
      String(ctx.state) === "interrupted"
    ) {
      void ctx.resume().catch((error) => {
        console.warn("[Audio] context resume failed", error);
      });
    }

    try {
      sourceRef.current?.disconnect();
    } catch {}

    sourceRef.current = ctx.createMediaElementSource(audio);

    if (!analyserRef.current) {
      analyserRef.current = ctx.createAnalyser();
      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.85;
    }

    sourceRef.current.connect(analyserRef.current);
    analyserRef.current.connect(ctx.destination);

    const data = new Uint8Array(analyserRef.current.frequencyBinCount);

    const tick = () => {
      analyserRef.current?.getByteFrequencyData(data);

      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];

      const avg = sum / data.length;
      const normalized = Math.min(1, Math.max(0, (avg - 10) / 50));

      setMouthLevel(prev => prev * 0.6 + normalized * 0.4);

      mouthAnimRef.current = requestAnimationFrame(tick);
    };

    tick();
  } catch (err) {
    console.warn("[MouthSync] failed:", err);
  }
}, []);

  const audioUrlRef = useRef<string | null>(null);
  const ttsAbortRef = useRef<AbortController | null>(null);

  // =====================================================
  // AVAAIO READ-ALONG V1.7
  // =====================================================
  const [readAlongMessageId, setReadAlongMessageId] =
    useState<string | null>(null);

  const [readAlongWordIndex, setReadAlongWordIndex] =
    useState(-1);

  const readAlongBoundariesRef = useRef<
    Array<{
      audio_offset_ms: number;
      text_offset: number;
      word_length: number;
      text: string;
    }>
  >([]);

  // AVAAIO READ-ALONG V1.5:
  // Drive spoken-word timing from the actual audio clock
  // every animation frame instead of coarse timeupdate events.
  const readAlongFrameRef =
    useRef<number | null>(null);

  const clearReadAlong = useCallback(() => {
    if (readAlongFrameRef.current !== null) {
      window.cancelAnimationFrame(
        readAlongFrameRef.current
      );

      readAlongFrameRef.current = null;
    }

    readAlongBoundariesRef.current = [];
    setReadAlongWordIndex(-1);
    setReadAlongMessageId(null);
  }, []);

  const chatAbortRef = useRef<AbortController | null>(null);
  const activeReqIdRef = useRef("");



  const sendingLockRef = useRef(false);

const typingWatchdogRef = useRef<number | null>(null);

const clearTypingWatchdog = useCallback(() => {
  try {
    if (typingWatchdogRef.current) {
      window.clearTimeout(typingWatchdogRef.current);
      typingWatchdogRef.current = null;
    }
  } catch (e) {
    console.warn("[clearTypingWatchdog]", e);
  }
}, []);


  

  const beginThinkingMessage = useCallback(
    (requestId: string) => {
      const cleanRequestId =
        String(requestId || "").trim();

      if (!cleanRequestId) return;

      resetChatScrollbarFade(15000);

      setMessages((prev) => {
        const alreadyPresent =
          prev.some(
            (message) =>
              message.request_id ===
                cleanRequestId &&
              message.is_thinking === true
          );

        if (alreadyPresent) {
          return prev;
        }

                  const thinkingMessage = {
            id:
              `avaaio-thinking-${cleanRequestId}`,
            sender: "ava",
            text:
              interfaceCopyRef.current.chat.thinking,
            thinking_text:
              interfaceCopyRef.current.chat.thinkingTitle,
            request_id:
              cleanRequestId,
            is_thinking: true,
            source: "brain",
            ts: Date.now(),

            // Existing renderer displays the moving
            // Thinking line from this property.
            thinking:
              interfaceCopyRef.current.chat.thinkingTitle,
          } as Message;

        const next = [
          ...prev,
          thinkingMessage,
        ];

        return next.length > MAX_MESSAGES
          ? next.slice(
              next.length - MAX_MESSAGES
            )
          : next;
      });

      console.log(
        "[THINKING PLACEHOLDER CREATED]",
        {
          requestId: cleanRequestId,
          ts: Date.now(),
        }
      );
    },
    [resetChatScrollbarFade]
  );

  const settleThinkingMessage = useCallback(
    (
      requestId: string,
      finalMessage: Message
    ) => {
      const cleanRequestId =
        String(requestId || "").trim();

      setMessages((prev) => {
        let replaced = false;

        const next = prev.map(
          (message) => {
            if (
              cleanRequestId &&
              message.request_id ===
                cleanRequestId &&
              message.is_thinking === true
            ) {
              replaced = true;

              return {
                ...finalMessage,

                // Preserve the same bubble identity
                // and position in the conversation.
                id: message.id,

                request_id:
                  cleanRequestId,

                is_thinking:
                  false,
              };
            }

            return message;
          }
        );

        if (!replaced) {
          next.push({
            ...finalMessage,

            request_id:
              cleanRequestId ||
              finalMessage.request_id,

            is_thinking:
              false,
          });
        }

        return next.length > MAX_MESSAGES
          ? next.slice(
              next.length - MAX_MESSAGES
            )
          : next;
      });

      console.log(
        "[THINKING PLACEHOLDER SETTLED]",
        {
          requestId:
            cleanRequestId || null,
          ts: Date.now(),
        }
      );
    },
    []
  );

  const removeThinkingMessage = useCallback(
    (requestId?: string) => {
      const cleanRequestId =
        String(requestId || "").trim();

      setMessages((prev) =>
        prev.filter((message) => {
          if (
            message.is_thinking !== true
          ) {
            return true;
          }

          if (!cleanRequestId) {
            return false;
          }

          return (
            message.request_id !==
            cleanRequestId
          );
        })
      );
    },
    []
  );


const forceUnlockSend = useCallback((reason = "unknown") => {

  console.log("[AVA] force unlock send:", reason);

  try {

    sendingLockRef.current = false;
    activeReqIdRef.current = "";

    setIsSending(false);
    console.log("[TYPING CLEAR]", new Error().stack);
    setIsTyping(false);
    removeThinkingMessage();

    clearTypingWatchdog?.();

    console.log("[AVA FORCE UNLOCK APPLIED]");

  } catch (e) {

    console.warn("[forceUnlockSend]", e);

  }

}, [removeThinkingMessage]);
  const audioPrimedRef = useRef(false);
  const micPermGrantedRef = useRef(false);
  const micPermAskedRef = useRef(false);
  const voiceDraftRef = useRef("");
  const consoleLinesRef = useRef<string[]>([]);
  const consoleWrappedRef = useRef(false);

  const sttAbortRef = useRef<AbortController | null>(null);
  const sttRecorderRef = useRef<MediaRecorder | null>(null);
  const sttStreamRef = useRef<MediaStream | null>(null);
  const sttChunksRef = useRef<BlobPart[]>([]);
  const sttModeRef = useRef<SttMode>("webspeech");
  const sttStartedAtRef = useRef(0);
  const sttStopTimerRef = useRef<number | null>(null);
  const sttRetryOnceRef = useRef(false);
  const offlineAutoStopTimerRef = useRef<number | null>(null);
  const offlineUserStopRef = useRef(false);
  const webspeechUserStopRef = useRef(false);
const welcomeShownRef = useRef(false);

  const micArmedRef = useRef(false);

  // Wake-word mode uses the existing authorized recognition stream.
  // It does not create a second microphone or recorder.
  const wakeWordEnabledRef =
    useRef(false);

  const wakeStandbyRef =
    useRef(false);

  const conversationActiveRef =
    useRef(false);

  const wakeActivationInFlightRef =
    useRef(false);

  const lastWakePhraseAtRef =
    useRef(0);

  const postSpeechCooldownUntilRef =
    useRef(0);

  const avaSelfHearingCooldownMsRef =
    useRef(1800);

  const normalizeAvaMicPhrase =
    useCallback(
      (value: string): string => {
        return String(value || "")
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      },
      []
    );

  const isAvaWakePhrase =
    useCallback(
      (value: string): boolean => {
        const normalized =
          normalizeAvaMicPhrase(value);

        return (
          /\bava\s+can\s+you\s+hear\s+me\b/.test(
            normalized
          ) ||
          /\bava\s+could\s+you\s+hear\s+me\b/.test(
            normalized
          )
        );
      },
      [normalizeAvaMicPhrase]
    );

  const isAvaMicOffPhrase =
    useCallback(
      (value: string): boolean => {
        const normalized =
          normalizeAvaMicPhrase(value);

        return (
          /\bava\s+(turn|switch)\s+off\s+(the\s+)?(mic|microphone)\b/.test(
            normalized
          ) ||
          /\bava\s+stop\s+listening\b/.test(
            normalized
          )
        );
      },
      [normalizeAvaMicPhrase]
    );

  const isAvaSleepPhrase =
    useCallback(
      (value: string): boolean => {
        const normalized =
          normalizeAvaMicPhrase(value);

        return (
          /\bava\s+go\s+to\s+sleep\b/.test(
            normalized
          ) ||
          /\bava\s+stand\s+by\b/.test(
            normalized
          )
        );
      },
      [normalizeAvaMicPhrase]
    );

  const setWakeWordPreference =
    useCallback(
      (enabled: boolean) => {
        wakeWordEnabledRef.current =
          enabled;

        try {
          window.localStorage.setItem(
            "avaaio_wake_word_enabled",
            enabled ? "1" : "0"
          );
        } catch {}
      },
      []
    );

  const resolveWakeUserName =
    useCallback((): string => {
      const blocked = new Set([
        "",
        "admin",
        "administrator",
        "app",
        "apple user",
        "avaaio",
        "avaaio app",
        "avaaio.app",
        "guest",
        "member",
        "support",
        "system",
        "there",
        "unknown",
        "user",
      ]);

      const cleanName = (
        value: unknown
      ): string => {
        const name = String(
          value || ""
        ).trim();

        const lowered =
          name.toLowerCase().replace(/[.]+$/g, "");

        if (!name || blocked.has(lowered)) {
          return "";
        }

        if (
          name.includes("@") ||
          name.includes("://")
        ) {
          return "";
        }

        if (
          /\.(app|com|net|org|io|ai|co)$/i.test(
            name
          )
        ) {
          return "";
        }

        return name.slice(0, 60);
      };

      const inspectObject = (
        value: unknown,
        depth = 0
      ): string => {
        if (
          !value ||
          typeof value !== "object" ||
          depth > 3
        ) {
          return "";
        }

        const item =
          value as Record<string, unknown>;

        const preferredKeys = [
          "display_name",
          "displayName",
          "preferred_name",
          "preferredName",
          "first_name",
          "firstName",
          "name",
        ];

        for (const key of preferredKeys) {
          const candidate =
            cleanName(item[key]);

          if (candidate) {
            return candidate;
          }
        }

        const nestedKeys = [
          "profile",
          "user_profile",
          "userProfile",
          "auth_user",
          "authUser",
          "user",
          "account",
          "session",
        ];

        for (const key of nestedKeys) {
          const nested =
            inspectObject(
              item[key],
              depth + 1
            );

          if (nested) {
            return nested;
          }
        }

        return "";
      };

      try {
        const stores: Storage[] = [
          window.localStorage,
          window.sessionStorage,
        ];

        for (const store of stores) {
          for (
            let index = 0;
            index < store.length;
            index += 1
          ) {
            const key =
              store.key(index) || "";

            if (
              !/(user|profile|auth|account|session)/i.test(
                key
              )
            ) {
              continue;
            }

            const raw =
              store.getItem(key);

            if (!raw) {
              continue;
            }

            try {
              const parsed =
                JSON.parse(raw);

              const name =
                inspectObject(parsed);

              if (name) {
                return name;
              }
            } catch {}
          }
        }
      } catch {}

      return "";
    }, []);

  // === AVAAIO UNIFIED CONVERSATION LANGUAGE ===
  const conversationLanguageRef = useRef<string>(
    String(
      localStorage.getItem("ava_conversation_language") ||
      localStorage.getItem("ava_mic_lang") ||
      "en"
    )
      .toLowerCase()
      .split("-", 1)[0]
  );

  const pendingLanguageConfirmationRef = useRef<any>(null);
  const declinedLanguageRef = useRef<Set<string>>(
    new Set<string>()
  );

  // __AVA_IOS_AUDIO_SESSION_PHASE15_V4__
  // One authorized microphone stream is retained while Mic is
  // enabled. Recorder rollover is blocked while Ava is speaking.
  const offlinePauseForAvaRef = useRef(false);
  const offlineStartInFlightRef = useRef(false);

  const micIdleAtRef = useRef(0);
  const finalTranscriptBufferRef = useRef("");
  const lastVoiceSubmissionRef = useRef<{
    text: string;
    ts: number;
  }>({
    text: "",
    ts: 0,
  });
  const finalSendTimerRef = useRef<number | null>(null);
  const restartingWebSpeechRef = useRef(false);
  const webspeechNetworkFailCountRef = useRef(0);
  const webspeechLockedOfflineRef = useRef(false);

  const chatApiUrl = useCallback(() => joinUrl(pickApiBase(), "/api/v1/chat"), []);
  const ttsApiUrl = useCallback(() => joinUrl(pickApiBase(), "/api/v1/voice/azure/tts"), []);
  const sttApiUrl = useCallback(() => joinUrl(pickApiBase(), "/api/v1/voice/stt"), []);

  const markInteraction = useCallback(() => {
    lastInteractionRef.current = Date.now();
    setDockVisible(true);
  }, []);

  const markInteractionAndUnlock = useCallback(() => {
    markInteraction();
    markUnlocked();
  }, [markInteraction]);

  const markMicActivity = useCallback(() => {
    micIdleAtRef.current = Date.now();
  }, []);

  const pushMessage = useCallback((msg: Message) => {

    console.log(
      "[PUSH MESSAGE]",
      {
        sender: msg.sender,
        source: (msg as any).source,
        text: String((msg as any).text || ""),
        len: String((msg as any).text || "").length,
      }
    );

    resetChatScrollbarFade(msg.sender === "ava" ? 12000 : 15000);
    setMessages((prev) => {
      const next = [...prev, msg];
      return next.length > MAX_MESSAGES
        ? next.slice(next.length - MAX_MESSAGES)
        : next;
    });
  }, []);

  const pushConsoleLine = useCallback((level: "LOG" | "WARN" | "ERROR", args: any[]) => {
    try {
      const ts = new Date().toISOString().replace("T", " ").slice(0, 19);
      const msg = args
        .map((value) => {
          if (typeof value === "string") return value;
          try {
            return JSON.stringify(value);
          } catch {
            return String(value);
          }
        })
        .join(" ");
      const line = `[${ts}] ${level}: ${msg}`;
      const arr = consoleLinesRef.current;
      arr.push(line);
      if (arr.length > CONSOLE_MAX) arr.splice(0, arr.length - CONSOLE_MAX);
    } catch {}
  }, []);

  const stopBrowserSpeech = useCallback(() => {
    try {
      window.speechSynthesis?.cancel?.();
    } catch {}
  }, []);

  const stopOfflineWave = useCallback(() => { }, []);

  const startOfflineWave = useCallback(() => { }, []);

  const getListeningPlaceholder = useCallback(() => {
    return interfaceCopyRef.current.chat.inputPlaceholder;
  }, []);

  const clearAudioResources = useCallback(() => {
    try {
      // Invalidate callbacks belonging to previous playback.
      ttsPlaybackEpochRef.current += 1;

      const audio =
        audioRef.current ?? ttsPlayerRef.current;

      if (audio) {
        avaSpeechActiveRef.current = false;
        isAvaSpeakingRef.current = false;

        audio.onended = null;
        audio.onerror = null;
        audio.onplaying = null;
        audio.onpause = null;
        audio.onwaiting = null;
        audio.onstalled = null;
        audio.ontimeupdate = null;

        try {
          audio.pause();
        } catch {}

        if (pauseStartedAt === null) {
          setPauseStartedAt(Date.now());
        }

        try {
          audio.currentTime = 0;
        } catch {}

        try {
          audio.removeAttribute("src");
          audio.load();
        } catch {}

        // audioRef represents active playback only.
        audioRef.current = null;

        // ttsPlayerRef retains the reusable Safari player.
        ttsPlayerRef.current = audio;
      }

      const oldUrl = audioUrlRef.current;
      audioUrlRef.current = null;

      if (oldUrl) {
        try {
          URL.revokeObjectURL(oldUrl);
        } catch {}
      }
    } catch (error) {
      console.warn("[Audio] resource cleanup failed", error);
    }
  }, []);

  const resetChatRuntimeState = useCallback(() => {
      // === FORCED UI RECOVERY PATCH (SAFE ADDITION) ===
      sendingLockRef.current = false;
      activeReqIdRef.current = "";
      setIsSending(false);

      // AVAAIO:
      // Keep thinking active until speech actually begins.
      // TTS lifecycle now owns clearing isTyping.

      console.log("[RESET CHAT STATE]", {
        sendingLock: sendingLockRef.current,
        activeReqId: activeReqIdRef.current,
        isTypingWillBe: false,
        isSendingWillBe: false,
        ts: Date.now()
      });

      try {
        clearTypingWatchdog?.();
      } catch {}

      try {
        console.log("[AVA RESET] enhanced recovery applied");
      } catch {}
    

    try {

      clearTypingWatchdog();

    } catch (e) {
      console.warn("[runtime reset] watchdog cleanup failed", e);
    }

    try {
      // auto-unlocked via resetChatRuntimeState()
    } catch {}

    try {
      // auto-unlocked via resetChatRuntimeState()
    } catch {}

    try {
      // auto-unlocked via resetChatRuntimeState()
    } catch {}

  }, []);


  const clearFinalSendTimer = useCallback(() => {
    try {
      if (finalSendTimerRef.current) {
        window.clearTimeout(finalSendTimerRef.current);
        finalSendTimerRef.current = null;
      }
    } catch {}
  }, []);

  const setAvaAudioSessionType = (
    type: "playback" | "play-and-record"
  ) => {
    try {
      const audioSession =
        (navigator as any)?.audioSession;

      if (
        audioSession &&
        typeof audioSession === "object"
      ) {
        audioSession.type = type;

        console.log(
          `[AudioSession] type=${type}`
        );
      }
    } catch (error) {
      console.warn(
        "[AudioSession] type change unavailable",
        error
      );
    }
  };

  const resetOfflineRecorderCycle = useCallback(() => {
    try {
      if (offlineAutoStopTimerRef.current) {
        window.clearTimeout(
          offlineAutoStopTimerRef.current
        );

        offlineAutoStopTimerRef.current = null;
      }

      if (sttStopTimerRef.current) {
        window.clearTimeout(
          sttStopTimerRef.current
        );

        sttStopTimerRef.current = null;
      }

      if (
        sttRecorderRef.current &&
        sttRecorderRef.current.state === "inactive"
      ) {
        sttRecorderRef.current = null;
      }
    } catch (error) {
      console.warn(
        "[OfflineSTT] recorder-cycle reset failed",
        error
      );
    }
  }, []);


  const cleanupOfflineStt = useCallback(() => {
    try {
      sttAbortRef.current?.abort();
      sttAbortRef.current = null;

      if (offlineAutoStopTimerRef.current) {
        window.clearTimeout(offlineAutoStopTimerRef.current);
        offlineAutoStopTimerRef.current = null;
      }
    } catch {}

    try {
      if (sttStopTimerRef.current) {
        window.clearTimeout(sttStopTimerRef.current);
        sttStopTimerRef.current = null;
      }
    } catch {}

    try {
      if (sttRecorderRef.current) {
        if (sttRecorderRef.current.state !== "inactive") sttRecorderRef.current.stop();
        sttRecorderRef.current = null;
      }
    } catch {}

    try {
      if (sttStreamRef.current) {
        sttStreamRef.current.getTracks().forEach((track) => track.stop());
        sttStreamRef.current = null;
      }
    } catch {}

    sttChunksRef.current = [];
  }, []);

  const addUserMessage = useCallback(
    (text: string) => {
      pushMessage({ id: uid(), sender: "user", text, ts: Date.now() });
      markInteractionAndUnlock();
    },
    [markInteractionAndUnlock, pushMessage]
  );

  const stopVoiceNow = useCallback(() => {
    try {
      ttsAbortRef.current?.abort();
      ttsAbortRef.current = null;
      clearAudioResources();
      stopBrowserSpeech();
    } catch {
    } finally {
      resetChatRuntimeState();
      console.log("[TYPING CLEAR]", new Error().stack);
      setIsTyping(false);

      setIsSpeaking(false);
window.dispatchEvent(new CustomEvent("ava-speaking", { detail: { speaking: false } }));
    }
  }, [clearAudioResources, stopBrowserSpeech]);

  const disableMic = useCallback(
    (reason?: string) => {
      try {
        offlinePauseForAvaRef.current = false;
        offlineStartInFlightRef.current = false;
        wakeStandbyRef.current = false;
        conversationActiveRef.current = false;
        wakeActivationInFlightRef.current = false;
        postSpeechCooldownUntilRef.current = 0;

        micArmedRef.current = false;
        setMicConversationMode("off");
        setIsMicOn(false);
        setIsListening(false);
        voiceDraftRef.current = "";
        finalTranscriptBufferRef.current = "";
        restartingWebSpeechRef.current = false;
        clearFinalSendTimer();

        if (reason) console.log(`[Mic] disabled: ${reason}`);

        if ((sttModeRef.current as any) === "offline") {
          offlineUserStopRef.current = true;

          // Gracefully stop recorder so final audio chunk/transcript can process.
          try {
            if (
              sttRecorderRef.current &&
              sttRecorderRef.current.state !== "inactive"
            ) {
              sttRecorderRef.current.stop();
            } else {
              cleanupOfflineStt();
            }
          } catch (e) {
            console.warn("[OfflineSTT] graceful stop failed", e);
            cleanupOfflineStt();
          }

      if (speechEndTimerRef.current) {
        window.clearTimeout(speechEndTimerRef.current);
        speechEndTimerRef.current = null;
      }
          return;
        }

        webspeechUserStopRef.current = true;
        recognitionRef.current?.stop?.();

      if (speechEndTimerRef.current) {
        window.clearTimeout(speechEndTimerRef.current);
        speechEndTimerRef.current = null;
      }
      } catch {}
    },
    [clearFinalSendTimer, cleanupOfflineStt, stopOfflineWave]
  );

  const stopListening = useCallback(() => {
    disableMic("manual toggle off");
  }, [disableMic]);

  const stopAvaNow = useCallback(() => {
    try {
      chatAbortRef.current?.abort();
      chatAbortRef.current = null;
      // NON-BLOCKING: removed pre-speech stopVoiceNow

      if (isListening) {
        try {
          stopListening();
        } catch {}
        setIsListening(false);
      }

      cleanupOfflineStt();
      clearFinalSendTimer();
      // auto-unlocked via resetChatRuntimeState()
      // === guaranteed cleanup: iPhone / Safari send unlock ===

      forceUnlockSend("send cleanup");
      // auto-unlocked via resetChatRuntimeState()


      requestAnimationFrame(() => {

        forceUnlockSend("send cleanup");
      // auto-unlocked via resetChatRuntimeState()

      });
    } catch {}
  }, [cleanupOfflineStt, clearFinalSendTimer, isListening, stopListening, stopOfflineWave, stopVoiceNow]);

  const buildTtsBody = useCallback(
    (text: string) => {
      const lower = (text || "").toLowerCase();

      const isSpanish =
        /[áéíóúñü¿¡]/i.test(text || "") ||
        [
          "hola",
          "gracias",
          "cómo",
          "estás",
          "buenos",
          "buenas",
          "adiós",
          "por favor",
          "español",
          "quiero",
          "necesito",
          "puedes",
          "dime",
        ].some((word) => lower.includes(word));

      const defaultVoice = isSpanish
        ? "es-MX-RenataNeural"
        : "en-US-JennyNeural";

      const cleanedText = (text || "")
        .replace(/[_]+/g, " ")
        .replace(/[`]+/g, "")
        .replace(/[=]{2,}/g, " ")
        .replace(/[~]{2,}/g, " ")
        .replace(/[•◆◇★☆]/g, "")
        .replace(/https?:\/\/\S+/g, "")
        
        .replace(/\s{2,}/g, " ")
        .trim();

      const body: Record<string, string> = {
        text: cleanedText,
        lang: isSpanish ? "es" : "en",
        voice: voiceChoice.trim() || defaultVoice,
        region: (regionChoice || "uksouth").trim(),

        // === HUMANIZED VOICE SETTINGS ===
        style: isSpanish ? "friendly" : "chat",
        rate: "1.08",
        pitch: "+2%",
        emphasis: "moderate",
      };

      if (formatChoice.trim()) body.format = formatChoice.trim();

      return body;
    },
    [formatChoice, regionChoice, voiceChoice]
  );

  const ensureMicPermission = useCallback(
    async (): Promise<boolean> => {
      try {
        const existingStream =
          sttStreamRef.current;

        const liveTracks =
          existingStream
            ?.getAudioTracks()
            .filter(
              (track) =>
                track.readyState === "live"
            ) ?? [];

        if (
          existingStream &&
          liveTracks.length > 0
        ) {
          micPermGrantedRef.current = true;

          setAvaAudioSessionType(
            "play-and-record"
          );

          return true;
        }

        if (
          !navigator?.mediaDevices?.getUserMedia
        ) {
          console.warn(
            "[Mic] mediaDevices.getUserMedia not available"
          );

          return false;
        }

        micPermAskedRef.current = true;

        setAvaAudioSessionType(
          "play-and-record"
        );

        const stream =
          await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          });

        sttStreamRef.current = stream;
        micPermGrantedRef.current = true;

        console.log(
          "[Mic] one authorized stream acquired"
        );

        return true;
      } catch (error: any) {
        console.warn(
          "[Mic] Permission denied or unavailable:",
          error?.name || error,
          error?.message || ""
        );

        micPermGrantedRef.current = false;
        sttStreamRef.current = null;

        return false;
      }
    },
    []
  );


  const primeAudioOnce = useCallback(async () => {
    if (audioPrimedRef.current) return;

    try {
      await ensureUserInteracted();
      const audio = new Audio();
      // @ts-ignore
      audio.playsInline = true;
      audio.muted = true;
      audio.volume = 0;
      await audio.play().catch(() => {});
      audio.pause();
      audioPrimedRef.current = true;
    } catch {}
  }, []);

  const blobHeadHex = useCallback(async (blob: Blob, max = 32): Promise<string> => {
    try {
      const buf = await blob.slice(0, max).arrayBuffer();
      const u8 = new Uint8Array(buf);
      return Array.from(u8)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join(" ");
    } catch {
      return "";
    }
  }, []);

  const sendOfflineSttBlob = useCallback(
    async (blob: Blob): Promise<{
      text: string;
      detectedLanguage: string;
    }> => {
      // AVAAIO_CONNECTION_MODE_STT_UPLOAD_GUARD_START
      if (isConnectionVoiceInputBlocked()) {
        throw new Error(
          connectionModeCopyRef.current
            .localVoiceInputUnavailable
        );
      }
      // AVAAIO_CONNECTION_MODE_STT_UPLOAD_GUARD_END

      const controller = new AbortController();
      sttAbortRef.current = controller;

      const form = new FormData();
      form.append("file", blob, `mic_${Date.now()}.webm`);

      const response = await fetch(sttApiUrl(), {         method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: {
          ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        },
        body: form,
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(`STT failed (${response.status}) ${text.slice(0, 200)}`);
      }

      const data = await (async () => {
  const raw = await response.text();
  try {
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    console.error("⚠️ NON-JSON RESPONSE:", raw.slice(0, 300));
    return {};
  }
})();

        if (data?.plan) setChatPlan(data.plan);
        if (typeof data?.daily_limit !== "undefined") setDailyLimit(data.daily_limit);
        if (typeof data?.words_used !== "undefined") setWordsUsed(data.words_used || 0);
        if (data?.lock) setChatLock(data.lock);
      const transcript =
        (typeof data?.text === "string" && data.text.trim()) ||
        (typeof data?.transcript === "string" &&
          data.transcript.trim()) ||
        (typeof data?.result === "string" &&
          data.result.trim()) ||
        "";

      return {
        text: transcript,
        detectedLanguage: String(
          data?.detected_language ||
          data?.language ||
          ""
        ).trim().toLowerCase(),
      };
    },
    [sttApiUrl]
  );


  // === AVA MIC SELF-HEARING GUARD START ===
  // Keep the authorized stream, but stop recording and disable
  // its tracks so Ava cannot transcribe her own speaker output.
    const pauseMicCaptureForAvaSpeech = () => {
      try {
        if (!micArmedRef.current) return;

        postSpeechCooldownUntilRef.current =
          Number.MAX_SAFE_INTEGER;

        setMicConversationMode(
          "ava_speaking_lockout"
        );

        clearFinalSendTimer();
        finalTranscriptBufferRef.current = "";
        voiceDraftRef.current = "";
        setInput("");

      offlinePauseForAvaRef.current = true;
      offlineUserStopRef.current = true;
      webspeechUserStopRef.current = true;

      setAvaAudioSessionType(
        "play-and-record"
      );

      resetOfflineRecorderCycle();

      try {
        const recorder =
          sttRecorderRef.current;

        if (
          recorder &&
          recorder.state !== "inactive"
        ) {
          recorder.stop();
        } else {
          sttRecorderRef.current = null;
        }
      } catch (error) {
        console.warn(
          "[OfflineSTT] recorder pause failed",
          error
        );
      }

      try {
        sttStreamRef.current
          ?.getAudioTracks()
          .forEach((track) => {
            track.enabled = false;
          });
      } catch (error) {
        console.warn(
          "[Mic] track disable failed",
          error
        );
      }

      try {
        recognitionRef.current?.stop?.();
      } catch {}

      setIsListening(false);
      voiceDraftRef.current = "";

      console.log(
        "[Mic] capture paused for Ava playback"
      );
    } catch (error) {
      console.warn(
        "[Mic] pause during Ava speech failed",
        error
      );
    }
  };

    const resumeMicCaptureAfterAvaSpeech = (cooldownOverrideMs?: number) => {
      try {
        if (!micArmedRef.current) return;

        const cooldownMs =
          typeof cooldownOverrideMs === "number"
            ? Math.max(0, cooldownOverrideMs)
            : avaSelfHearingCooldownMsRef.current;

        postSpeechCooldownUntilRef.current =
          Date.now() + cooldownMs;

        setMicConversationMode(
          "post_speech_cooldown"
        );

        clearFinalSendTimer();
        finalTranscriptBufferRef.current = "";
        voiceDraftRef.current = "";
        setInput("");

        window.setTimeout(() => {
          if (!micArmedRef.current) {
            return;
          }

          if (isAvaSpeakingRef.current) {
            return;
          }

          if (
            Date.now() <
            postSpeechCooldownUntilRef.current
          ) {
            return;
          }

          try {
            postSpeechCooldownUntilRef.current = 0;

            offlinePauseForAvaRef.current = false;
            offlineUserStopRef.current = false;
            webspeechUserStopRef.current = false;

            setAvaAudioSessionType(
              "play-and-record"
            );

            sttStreamRef.current
              ?.getAudioTracks()
              .forEach((track) => {
                if (
                  track.readyState === "live"
                ) {
                  track.enabled = true;
                }
              });

            if (
              (sttModeRef.current as any) ===
                "offline" ||
              webspeechLockedOfflineRef.current
            ) {
              sttModeRef.current = "offline";
              setSttModeLabel("offline");

              void startOfflineListeningRef.current();
            } else if (
              recognitionRef.current
            ) {
              restartingWebSpeechRef.current =
                false;

              recognitionRef.current.start();
            }

            setMicConversationMode(
              conversationActiveRef.current
                ? "conversation_active"
                : wakeStandbyRef.current
                  ? "wake_standby"
                  : "off"
            );

            console.log(
              "[Mic] capture resumed after Ava self-hearing cooldown"
            );
          } catch (error) {
            console.warn(
              "[Mic] resume after Ava speech failed",
              error
            );
          }
        }, cooldownMs);
      } catch (error) {
        console.warn(
          "[Mic] resume scheduler failed",
          error
        );
      }
    };

  // === AVA MIC SELF-HEARING GUARD END ===


  // __AVA_IOS_AUDIO_SESSION_RECOVERY_PHASE1__
  useEffect(() => {
    const resumeContext = (ctx: AudioContext | null) => {
      if (!ctx) return;

      if (
        ctx.state === "suspended" ||
        String(ctx.state) === "interrupted"
      ) {
        void ctx.resume().catch((error) => {
          console.warn(
            "[Audio] lifecycle resume failed",
            error
          );
        });
      }
    };

    const recoverAudioSession = () => {
      if (document.hidden) return;

      resumeContext(audioCtxRef.current);
      resumeContext(micChimeCtxRef.current);

      const audio =
        audioRef.current ?? ttsPlayerRef.current;

      if (
        isAvaSpeakingRef.current &&
        audio &&
        audio.src &&
        audio.paused &&
        !audio.ended
      ) {
        void audio.play().catch((error) => {
          console.warn(
            "[TTS] foreground playback recovery deferred",
            error
          );
        });
      }
    };

    document.addEventListener(
      "visibilitychange",
      recoverAudioSession
    );

    window.addEventListener(
      "pageshow",
      recoverAudioSession
    );

    window.addEventListener(
      "focus",
      recoverAudioSession
    );

    return () => {
      document.removeEventListener(
        "visibilitychange",
        recoverAudioSession
      );

      window.removeEventListener(
        "pageshow",
        recoverAudioSession
      );

      window.removeEventListener(
        "focus",
        recoverAudioSession
      );

      const audio = ttsPlayerRef.current;
      ttsPlayerRef.current = null;
      audioRef.current = null;

      if (audio) {
        audio.onended = null;
        audio.onerror = null;

        try {
          audio.pause();
          audio.removeAttribute("src");
          audio.load();
        } catch {}
      }

      const context = micChimeCtxRef.current;
      micChimeCtxRef.current = null;

      if (context && context.state !== "closed") {
        void context.close().catch(() => {});
      }
    };
  }, []);

  // AVAAIO_CONNECTION_MODE_TTS_DEVICE_START
  const speakDeviceBrowserOnly =
    useCallback(
      (
        text: string,
        ownedSpeechEpoch: number
      ): boolean => {
        try {
          if (
            typeof window === "undefined" ||
            !window.speechSynthesis ||
            typeof SpeechSynthesisUtterance ===
              "undefined"
          ) {
            return false;
          }

          const language =
            String(
              preferencesLanguageRef.current ||
              "en"
            )
              .toLowerCase()
              .split("-", 1)[0];

          const languageTags:
            Record<string, string> = {
              en: "en-US",
              es: "es-ES",
              fr: "fr-FR",
              de: "de-DE",
              it: "it-IT",
              pt: "pt-BR",
            };

          const utterance =
            new SpeechSynthesisUtterance(
              text
            );

          utterance.lang =
            languageTags[language] ||
            "en-US";

          utterance.rate = 1;
          utterance.pitch = 1;
          utterance.volume = 1;

          const finish = () => {
            if (
              ownedSpeechEpoch !==
              ttsRequestEpochRef.current
            ) {
              return;
            }

            isAvaSpeakingRef.current =
              false;

            avaSpeechActiveRef.current =
              false;

            lastAvaSpeechEndRef.current =
              Date.now();

            setIsAudioPlaying(false);
            setIsTyping(false);
            setIsSpeaking(false);

            window.dispatchEvent(
              new CustomEvent(
                "ava-speaking",
                {
                  detail: {
                    speaking: false,
                  },
                }
              )
            );

            resumeMicCaptureAfterAvaSpeech();
          };

          utterance.onstart = () => {
            if (
              ownedSpeechEpoch !==
              ttsRequestEpochRef.current
            ) {
              window.speechSynthesis.cancel();
              return;
            }

            pauseMicCaptureForAvaSpeech();

            isAvaSpeakingRef.current =
              true;

            avaSpeechActiveRef.current =
              true;

            setIsTyping(false);
            setIsSpeaking(true);
            setIsAudioPlaying(true);

            window.dispatchEvent(
              new CustomEvent(
                "ava-speaking",
                {
                  detail: {
                    speaking: true,
                  },
                }
              )
            );
          };

          utterance.onend = finish;
          utterance.onerror = finish;

          window.speechSynthesis.cancel();
          window.speechSynthesis.speak(
            utterance
          );

          return true;
        } catch (error) {
          console.warn(
            "[ConnectionMode] device browser voice unavailable",
            error
          );

          return false;
        }
      },
      []
    );
  // AVAAIO_CONNECTION_MODE_TTS_DEVICE_END

  const speakAva = useCallback(
    async (
        avaText: string,
        reqId?: string,
        requestedSpeechEpoch?: number
      ) => {
      if (isMutedRef.current) return;


        const ownedSpeechEpoch =
          requestedSpeechEpoch ??
          ++ttsRequestEpochRef.current;

        if (
          ownedSpeechEpoch !==
          ttsRequestEpochRef.current
        ) {
          return;
        }
        if (
          micArmedRef.current ||
          isMicOn
        ) {
          console.log(
            "[Mic] remains armed until real TTS playback"
          );
        }

        const text =
 avaTtsSafeText(avaText).trim();

const lower = text.toLowerCase();

let expression = "NEUTRAL";

if (
    lower.includes("smile") ||
    lower.includes("happy")
){
    expression = "HAPPY";
}
else if(
    lower.includes("sad") ||
    lower.includes("sorry")
){
    expression = "SAD";
}
else if(
    lower.includes("wow") ||
    lower.includes("surprised") ||
    lower.includes("amazing")
){
    expression = "SURPRISED";
}

if (lastExpressionSentRef.current !== expression) {

    lastExpressionSentRef.current = expression;

    window.dispatchEvent(
        new CustomEvent(
            "ava-expression",
            {
                detail:{
                    expression
                }
            }
        )
    );

}
        if (!text) return;

        if (
          ownedSpeechEpoch !==
            ttsRequestEpochRef.current
        ) {
          return;
        }

        // AVAAIO_CONNECTION_MODE_TTS_CLOUD_GUARD_START
        const activeConnection =
          connectionModeRef.current;

        if (!activeConnection.confirmed) {
          setIsTyping(false);
          setIsSpeaking(false);
          setIsAudioPlaying(false);

          window.dispatchEvent(
            new CustomEvent(
              "ava-speaking",
              {
                detail: {
                  speaking: false,
                },
              }
            )
          );

          return;
        }

        if (
          activeConnection
            .cloudServicesBlocked ||
          activeConnection
            .requestedMode === "local" ||
          activeConnection
            .effectiveMode === "local" ||
          activeConnection
            .effectiveMode ===
              "disconnected" ||
          activeConnection
            .voiceOutputMode ===
              "device_browser_only"
        ) {
          const deviceSpeechStarted =
            speakDeviceBrowserOnly(
              text,
              ownedSpeechEpoch
            );

          if (!deviceSpeechStarted) {
            setIsTyping(false);
            setIsSpeaking(false);
            setIsAudioPlaying(false);

            window.dispatchEvent(
              new CustomEvent(
                "ava-speaking",
                {
                  detail: {
                    speaking: false,
                  },
                }
              )
            );
          }

          return;
        }
        // AVAAIO_CONNECTION_MODE_TTS_CLOUD_GUARD_END

        const previousTtsController =
          ttsAbortRef.current;

        ttsAbortRef.current = null;

        try {
          previousTtsController?.abort();
        } catch {}

        clearAudioResources();

        try {

        // NON-BLOCKING: removed pre-speech stopVoiceNow

        if (isMutedRef.current) {
          console.log("[TYPING CLEAR]", new Error().stack);
          setIsTyping(false);

          setIsSpeaking(false);
window.dispatchEvent(new CustomEvent("ava-speaking", { detail: { speaking: false } }));
          return;
        }

        await ensureUserInteracted();
        void primeAudioOnce();
        stopBrowserSpeech();

        const controller = new AbortController();
        ttsAbortRef.current = controller;

        const timeoutMs = 20_000; // AVAAIO: bounded latest-response timeout
        const timer = window.setTimeout(() => controller.abort(), timeoutMs);

        let response: Response;
        try {
          if (ownedSpeechEpoch !== ttsRequestEpochRef.current || isMutedRef.current) {
            if (ttsAbortRef.current === controller) ttsAbortRef.current = null;
            console.log("[TYPING CLEAR]", new Error().stack);
            setIsTyping(false);

            setIsSpeaking(false);
window.dispatchEvent(new CustomEvent("ava-speaking", { detail: { speaking: false } }));
            return;
          }

          response = await fetch(ttsApiUrl(), {             method: "POST",
            credentials: "include",
            signal: controller.signal,
            headers: {
              "Content-Type": "application/json",
              ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
            },
            body: JSON.stringify({
              ...buildTtsBody(text),
              timed: true,
            }),
          });
        } finally {
            // AVAAIO: never reset chat runtime during TTS cleanup.
          window.clearTimeout(timer);
        }

        if (ownedSpeechEpoch !== ttsRequestEpochRef.current || isMutedRef.current) {
          if (ttsAbortRef.current === controller) ttsAbortRef.current = null;
          // NON-BLOCKING: removed pre-speech stopVoiceNow
          return;
        }

        if (!response.ok) {
          try {
            const errText = await response.text();
            console.warn("Azure TTS non-OK:", response.status, errText.slice(0, 200));
          } catch {}
          if (ttsAbortRef.current === controller) ttsAbortRef.current = null;
          console.log("[TYPING CLEAR]", new Error().stack);
          setIsTyping(false);

          setIsSpeaking(false);
window.dispatchEvent(new CustomEvent("ava-speaking", { detail: { speaking: false } }));
          return;
        }

        const responseContentType =
          response.headers.get("content-type") || "";

        let blob: Blob;

        if (
          responseContentType.includes(
            "application/json"
          )
        ) {
          const timedPayload =
            await response.json();

          const encodedAudio =
            String(
              timedPayload?.audio_base64 || ""
            );

          if (!encodedAudio) {
            throw new Error(
              "Timed TTS audio missing."
            );
          }

          const binary =
            window.atob(encodedAudio);

          const bytes =
            new Uint8Array(binary.length);

          for (
            let i = 0;
            i < binary.length;
            i++
          ) {
            bytes[i] =
              binary.charCodeAt(i);
          }

          blob = new Blob(
            [bytes],
            {
              type:
                timedPayload?.mime_type ||
                "audio/mpeg",
            }
          );

          readAlongBoundariesRef.current =
            Array.isArray(
              timedPayload?.word_boundaries
            )
              ? timedPayload.word_boundaries
              : [];

          setReadAlongWordIndex(-1);
        } else {
          blob =
            await response.blob();

          readAlongBoundariesRef.current = [];
          setReadAlongWordIndex(-1);
        }

        if (ttsAbortRef.current === controller) ttsAbortRef.current = null;

        if (ownedSpeechEpoch !== ttsRequestEpochRef.current || isMutedRef.current) {
          console.log("[TYPING CLEAR]", new Error().stack);
          setIsTyping(false);

          setIsSpeaking(false);
window.dispatchEvent(new CustomEvent("ava-speaking", { detail: { speaking: false } }));
          return;
        }

        if (!blob || blob.size < 200) {
          console.log("[TYPING CLEAR]", new Error().stack);
          setIsTyping(false);

          setIsSpeaking(false);
window.dispatchEvent(new CustomEvent("ava-speaking", { detail: { speaking: false } }));
          return;
        }

        const audio =
          ttsPlayerRef.current ?? new Audio();

        ttsPlayerRef.current = audio;
        audioRef.current = audio;

        audio.onended = null;
        audio.onerror = null;
        audio.onplaying = null;
        audio.onpause = null;
        audio.onwaiting = null;
        audio.onstalled = null;
        audio.ontimeupdate = null;

        if (readAlongFrameRef.current !== null) {
          window.cancelAnimationFrame(
            readAlongFrameRef.current
          );

          readAlongFrameRef.current = null;
        }

        const updateReadAlongFromAudioClock = () => {
          const boundaries =
            readAlongBoundariesRef.current;

          if (
            !audioRef.current ||
            audioRef.current !== audio
          ) {
            readAlongFrameRef.current = null;
            return;
          }

          if (boundaries.length > 0) {
            // AVAAIO READ-ALONG V1.7
            // Azure word boundaries are now filtered to actual
            // Word events, so use the real audio clock directly.
            const currentMs =
              Number(
                audio.currentTime || 0
              ) * 1000;

            let activeIndex = -1;

            // Find the latest Azure boundary whose
            // audio time has actually been reached.
            for (
              let i =
                boundaries.length - 1;
              i >= 0;
              i--
            ) {
              const boundaryMs =
                Number(
                  boundaries[i]
                    ?.audio_offset_ms ||
                  0
                );

              if (
                currentMs >=
                boundaryMs
              ) {
                activeIndex = i;
                break;
              }
            }

            if (activeIndex >= 0) {
              setReadAlongWordIndex(
                (previous) =>
                  previous === activeIndex
                    ? previous
                    : activeIndex
              );
            }
          }

          if (
            !audio.ended &&
            audioRef.current === audio
          ) {
            readAlongFrameRef.current =
              window.requestAnimationFrame(
                updateReadAlongFromAudioClock
              );
          } else {
            readAlongFrameRef.current = null;
          }
        };

        readAlongFrameRef.current =
          window.requestAnimationFrame(
            updateReadAlongFromAudioClock
          );

        try {
          audio.pause();
        } catch {}

        try {
          audio.currentTime = 0;
        } catch {}

        try {
          audio.removeAttribute("src");
          audio.load();
        } catch {}

        const previousUrl = audioUrlRef.current;
        audioUrlRef.current = null;

        if (previousUrl) {
          try {
            URL.revokeObjectURL(previousUrl);
          } catch {}
        }

        const url = URL.createObjectURL(blob);
        audioUrlRef.current = url;

        const playbackEpoch =
          ++ttsPlaybackEpochRef.current;

        try {
          window.speechSynthesis?.cancel?.();
        } catch {}

        audio.src = url;
        audio.preload = "auto";
        audio.muted = false;
        audio.volume = 1;
        audio.playsInline = true;
        audio.setAttribute("playsinline", "true");

        try {
          (audio as any).disableRemotePlayback = true;
        } catch {}

        try {
          audio.load();
        } catch {}

        // startMouthTracking(audio);

        
        audio.onended = () => {
          if (
            ownedSpeechEpoch !==
                ttsRequestEpochRef.current ||
              playbackEpoch !==
                ttsPlaybackEpochRef.current
          ) {
            return;
          }

          isAvaSpeakingRef.current = false;
          avaSpeechActiveRef.current = false;
          lastAvaSpeechEndRef.current = Date.now();

          setIsAudioPlaying(false);
          setIsTyping(false);
          setIsSpeaking(false);

          window.dispatchEvent(
            new CustomEvent("ava-speaking", {
              detail: { speaking: false },
            })
          );

          try {
            sourceRef.current?.disconnect();
          } catch {}

          sourceRef.current = null;

          clearAudioResources();
          resumeMicCaptureAfterAvaSpeech();
        };

        audio.onerror = () => {
          if (
            ownedSpeechEpoch !==
                ttsRequestEpochRef.current ||
              playbackEpoch !==
                ttsPlaybackEpochRef.current
          ) {
            return;
          }

          console.warn("[TTS] audio playback failed");

          isAvaSpeakingRef.current = false;
          avaSpeechActiveRef.current = false;
          lastAvaSpeechEndRef.current = Date.now();

          setIsAudioPlaying(false);
          setIsTyping(false);
          setIsSpeaking(false);

          window.dispatchEvent(
            new CustomEvent("ava-speaking", {
              detail: { speaking: false },
            })
          );

          try {
            sourceRef.current?.disconnect();
          } catch {}

          sourceRef.current = null;

          clearAudioResources();
          resumeMicCaptureAfterAvaSpeech();
        };

        if (ownedSpeechEpoch !== ttsRequestEpochRef.current || isMutedRef.current) {
          console.log("[TYPING CLEAR]", new Error().stack);
          setIsTyping(false);

          setIsSpeaking(false);
window.dispatchEvent(new CustomEvent("ava-speaking", { detail: { speaking: false } }));
          return;
        }

        try {

          // ==========================================
          // AVAAIO THINKING -> SPEAKING TRANSITION
          // ==========================================
          console.log(
            "[TYPING CLEAR]",
            new Error().stack
          );

          setIsTyping(false);

          if (
            ownedSpeechEpoch !==
              ttsRequestEpochRef.current ||
            isMutedRef.current
          ) {
            return;
          }

          // Keep microphone capture available while Azure generates
          // the voice. Pause it only when real playback begins.
          pauseMicCaptureForAvaSpeech();

          setAvaAudioSessionType(
            "playback"
          );

          isAvaSpeakingRef.current =
            true;

          avaSpeechActiveRef.current =
            true;

          setIsSpeaking(true);
          setIsAudioPlaying(true);

          window.dispatchEvent(
            new CustomEvent(
              "ava-speaking",
              {
                detail: {
                  speaking: true,
                },
              }
            )
          );

          await audio.play();
        } catch (err) {
          console.warn("[TTS] play blocked, retrying:", err);
          try {
            // __AVA_IPHONE_TTS_RETRY_PHASE1_V3__
            // Re-establish the same audio element and iOS playback
            // session before the single guarded retry.
            audioPrimedRef.current = false;

            setAvaAudioSessionType(
              "playback"
            );

            try {
              audio.pause();
              audio.currentTime = 0;
              audio.src = url;
              audio.preload = "auto";
              audio.muted = false;
              audio.volume = 1;
              audio.playsInline = true;
              audio.setAttribute(
                "playsinline",
                "true"
              );
              audio.setAttribute(
                "webkit-playsinline",
                "true"
              );
              audio.load();
            } catch (recoveryError) {
              console.warn(
                "[TTS] iPhone audio element reset failed",
                recoveryError
              );
            }

            await ensureUserInteracted();

            await new Promise<void>((resolve) => {
              window.setTimeout(
                resolve,
                80
              );
            });

            await primeAudioOnce();

            if (
              ownedSpeechEpoch !==
                ttsRequestEpochRef.current ||
              isMutedRef.current
            ) {
              return;
            }

            await audio.play();
          } catch (e2) {
            console.warn("[TTS] retry failed:", e2);

          if (
            ownedSpeechEpoch ===
                ttsRequestEpochRef.current &&
              playbackEpoch ===
                ttsPlaybackEpochRef.current
          ) {
            audio.dispatchEvent(
              new Event("error")
            );
          }
          }
        }
      } catch (error: any) {
        if (
          ownedSpeechEpoch !==
            ttsRequestEpochRef.current
        ) {
          return;
        }

console.error("[TRACE CATCH ERROR]", error);
console.error("[TRACE CATCH STACK]", error?.stack);
        if (error?.name === "AbortError") {
          console.log("[TYPING CLEAR]", new Error().stack);
          setIsTyping(false);

          setIsSpeaking(false);
window.dispatchEvent(new CustomEvent("ava-speaking", { detail: { speaking: false } }));
          return;
        }
        console.error("Azure TTS error:", error);
        console.log("[TYPING CLEAR]", new Error().stack);
        setIsTyping(false);

        setIsSpeaking(false);
window.dispatchEvent(new CustomEvent("ava-speaking", { detail: { speaking: false } }));
      }
    },
    [buildTtsBody, clearAudioResources, disableMic, isMicOn, primeAudioOnce, stopBrowserSpeech, stopVoiceNow, ttsApiUrl]
  );

  const addAvaMessage = useCallback(
    async (
      payload: string | { text: string; thinking?: string | null; source?: "brain" | "openai" | "error"; reqId?: string; noTts?: boolean },
      sourceArg: "brain" | "openai" | "error" = "brain",
      reqIdArg?: string
    ) => {
      const avaText = typeof payload === "object" ? payload.text : payload;
      const thinking = typeof payload === "object" ? payload.thinking || null : null;
      const source = typeof payload === "object" ? payload.source || sourceArg : sourceArg;
      const reqId = typeof payload === "object" ? payload.reqId || reqIdArg : reqIdArg;
      const noTts = typeof payload === "object" ? payload.noTts === true : false;

      console.log(
        "[ADD AVA MESSAGE]",
        {
          avaText,
          thinking,
          source,
          reqId,
          noTts,
        }
      );

        const nextMessage = {
          id: uid(),
          sender: "ava",
          text: avaText,

          ...(thinking
            ? { thinking }
            : {}),

          thinking_text:
            thinking || undefined,

          request_id:
            reqId || undefined,

          is_thinking:
            false,

          source,
          ts: Date.now(),
        } as Message;

        if (reqId) {
          settleThinkingMessage(
            reqId,
            nextMessage
          );
        } else {
          pushMessage(
            nextMessage
          );
        }

        // The real answer is now visible.
        // TTS may begin independently.
        setIsTyping(false);

      markInteractionAndUnlock();

      // UX FIX:
      // TTS must NEVER block conversational UI/runtime lifecycle.

      if (source !== "error" && !noTts) {
          const speechEpoch =
            ++ttsRequestEpochRef.current;

          Promise.resolve()
            .then(
              () =>
                speakAva(
                  avaText,
                  reqId,
                  speechEpoch
                )
            )

          .catch((e) => {
            console.warn("[async speakAva]", e);
              setIsTyping(false);
              setIsSpeaking(false);
          });

      } else {
        // No speech transition will occur.
        setIsTyping(false);
      }
    },
    [
        markInteractionAndUnlock,
        pushMessage,
        settleThinkingMessage,
        speakAva,
      ]
  );

  // AVAAIO_CONNECTION_MODE_ANNOUNCEMENT_START
  useEffect(() => {
    const handleConfirmedConnectionMode =
      (event: Event) => {
        const detail =
          (
            event as CustomEvent<any>
          )?.detail || {};

        if (
          detail?.announce !== true ||
          detail?.confirmed !== true
        ) {
          return;
        }

        connectionModeRef.current = {
          ...connectionModeRef.current,
          ...detail,
        };

        const copy =
          connectionModeCopyRef.current;

        const requestedLocal =
          detail.requestedMode ===
          "local";

        const disconnected =
          detail.effectiveMode ===
            "disconnected" ||
          detail.localReady === false;

        const limited =
          String(
            detail.reason || ""
          ).includes("limited") ||
          String(
            detail.status || ""
          ).includes("limited");

        let message =
          copy.unavailable;

        if (requestedLocal) {
          message =
            disconnected
              ? copy.localUnavailable
              : limited
                ? copy.localLimitedConfirmed
                : copy.localConfirmed;
        } else if (
          detail.effectiveMode ===
          "online"
        ) {
          message =
            copy.onlineConfirmed;
        }

        try {
          ttsAbortRef.current?.abort();
          ttsAbortRef.current = null;
        } catch {}

        stopBrowserSpeech();
        clearAudioResources();

        if (
          requestedLocal ||
          detail.cloudServicesBlocked ===
            true
        ) {
          disableMic(
            "backend-confirmed local mode"
          );
        }

        void addAvaMessage(
          message,
          "brain"
        );
      };

    window.addEventListener(
      "avaaio-connection-mode-confirmed",
      handleConfirmedConnectionMode
    );

    return () => {
      window.removeEventListener(
        "avaaio-connection-mode-confirmed",
        handleConfirmedConnectionMode
      );
    };
  }, [
    addAvaMessage,
    clearAudioResources,
    disableMic,
    stopBrowserSpeech,
  ]);
  // AVAAIO_CONNECTION_MODE_ANNOUNCEMENT_END

  const fetchJsonWithTimeout = useCallback(async (url: string, timeoutMs = 2500) => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {         method: "GET",
        signal: controller.signal,
        credentials: "include",
      });
      const text = await response.text().catch(() => "");
      return { ok: response.ok, status: response.status, text };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        text: error?.name === "AbortError" ? "timeout" : "network_error",
      };
    } finally {
      window.clearTimeout(timer);
    }
  }, []);

  const getCapturedLogs = useCallback((maxLines = 40) => {
    const arr = consoleLinesRef.current;
    return arr.slice(Math.max(0, arr.length - maxLines)).join("\n");
  }, []);

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(textarea);
        return ok;
      } catch {
        return false;
      }
    }
  }, []);

  const toggleAvasEye = useCallback(() => {

    if (chatPlan === "free" && eyeLocked) {
      pushMessage({
        id: uid(),
        sender: "ava",
        text: "Ava’s Eye is locked for free users until your daily Ava limit resets or you upgrade.",
        ts: Date.now(),
      });
      return;
    }

      markInteractionAndUnlock();
    window.dispatchEvent(new Event("ava_interaction"));

    setAvaEyeOpen((prev) => {
      const next = !prev;

      window.dispatchEvent(
        new CustomEvent("ava-eye-toggle", {
          detail: { open: next, source: "chatbox-logic", ts: Date.now() },
        })
      );

      window.dispatchEvent(new Event("ava_face_cam_tracking_changed"));

      return next;
    });
  }, [markInteractionAndUnlock]);

  const toggleVoiceControl = useCallback(async () => {

    if (chatPlan === "free" && muteLocked) {
      pushMessage({
        id: uid(),
        sender: "ava",
        text: "Voice controls are locked for free users until your daily Ava limit resets or you upgrade.",
        ts: Date.now(),
      });
      return;
    }

      markInteractionAndUnlock();

    const nextMuted = !isMutedRef.current;
    isMutedRef.current = nextMuted;
    setIsMuted(nextMuted);

    if (nextMuted) {
      // NON-BLOCKING: removed pre-speech stopVoiceNow
      pushMessage({
        id: uid(),
        sender: "ava",
        text: "Voice output muted.",
        source: "brain",
        ts: Date.now(),
      });
    } else {
      pushMessage({
        id: uid(),
        sender: "ava",
        text: "Voice output restored.",
        source: "brain",
        ts: Date.now(),
      });
    }
  }, [markInteractionAndUnlock, pushMessage, stopVoiceNow]);

  
const lastAvaSpeechEndRef = useRef<number>(0);
const isAvaSpeakingRef = useRef(false);
const lastExpressionSentRef = useRef<string>("");

const avaSpeechActiveRef = useRef(false);

const sendMessage = useCallback(

    async (text: string = input, opts?: {
  alreadyAddedUser?: boolean;
  inputSource?: "voice" | "text";
  detectedLanguage?: string;
  skipLanguageConfirmation?: boolean;
  conversationLanguage?: string;
}) => {
  


  


  

      if (!isAdminUser && chatPlan === "free" && chatLock?.features_locked?.chat) {
        pushMessage({
          id: uid(),
          sender: "ava",
          text: "You’ve reached today’s free Ava limit. Some features are locked until your limit resets. Please come back tomorrow or upgrade to unlock more.",
          ts: Date.now(),
        });
        return;
      }

        markInteractionAndUnlock();
        void primeAudioOnce();

        let clean = text.trim();
        if (!clean) return;

        let messageInputSource: "voice" | "text" =
          opts?.inputSource === "voice" ? "voice" : "text";

        let messageDetectedLanguage = String(
          opts?.detectedLanguage || ""
        ).trim().toLowerCase();

        let messageConversationLanguage = String(
          opts?.conversationLanguage ||
          conversationLanguageRef.current ||
          "en"
        ).trim().toLowerCase().split("-", 1)[0];

        let skipLanguageConfirmation =
          Boolean(opts?.skipLanguageConfirmation);

        // === LANGUAGE-SWITCH CONFIRMATION RESPONSE ===
        const pendingLanguage =
          pendingLanguageConfirmationRef.current;

        if (pendingLanguage) {
          const confirmationReply = clean
            .normalize("NFKC")
            .toLocaleLowerCase()
            .replace(/[.!?¿¡،。]/g, "")
            .replace(/\s+/g, " ")
            .trim();

          const yesReplies = new Set([
            "yes", "yeah", "yep", "sure", "ok", "okay",
            "si", "sí", "claro",
            "oui", "d'accord",
            "ja",
            "sì",
            "sim",
            "да",
            "evet",
            "نعم",
            "हाँ", "हां",
            "はい",
            "是", "好的",
            "네",
          ]);

          const noReplies = new Set([
            "no", "nope",
            "non",
            "nein",
            "não",
            "нет",
            "hayır",
            "لا",
            "नहीं",
            "いいえ",
            "不是", "不",
            "아니요",
          ]);

          const acceptedLanguage =
            yesReplies.has(confirmationReply);

          const declinedLanguage =
            noReplies.has(confirmationReply);

          if (acceptedLanguage || declinedLanguage) {
            if (!opts?.alreadyAddedUser) {
              addUserMessage(clean);
            }

            setInput("");

            const targetLanguage = String(
              pendingLanguage.detectedLanguage || ""
            ).trim().toLowerCase().split("-", 1)[0];

            const currentLanguage = String(
              pendingLanguage.currentLanguage ||
              conversationLanguageRef.current ||
              "en"
            ).trim().toLowerCase().split("-", 1)[0];

            if (acceptedLanguage && targetLanguage) {
              conversationLanguageRef.current = targetLanguage;

              localStorage.setItem(
                "ava_conversation_language",
                targetLanguage
              );

              localStorage.setItem(
                "ava_mic_lang",
                targetLanguage
              );

              declinedLanguageRef.current.delete(
                targetLanguage
              );

              const speechLocales: Record<string, string> = {
                en: "en-US",
                es: "es-US",
                fr: "fr-FR",
                de: "de-DE",
                it: "it-IT",
                pt: "pt-BR",
                ja: "ja-JP",
                zh: "zh-CN",
                ko: "ko-KR",
                ar: "ar-SA",
                he: "he-IL",
                ru: "ru-RU",
                hi: "hi-IN",
                nl: "nl-NL",
                pl: "pl-PL",
                tr: "tr-TR",
                el: "el-GR",
              };

              try {
                if (recognitionRef.current) {
                  recognitionRef.current.lang =
                    speechLocales[targetLanguage] ||
                    targetLanguage;
                }
              } catch {}
            }

            if (declinedLanguage && targetLanguage) {
              declinedLanguageRef.current.add(
                targetLanguage
              );
            }

            pendingLanguageConfirmationRef.current = null;

            clean = String(
              pendingLanguage.originalMessage || ""
            ).trim();

            if (!clean) return;

            messageInputSource =
              pendingLanguage.inputSource === "voice"
                ? "voice"
                : "text";

            messageDetectedLanguage = String(
              pendingLanguage.detectedLanguage || ""
            ).trim().toLowerCase();

            messageConversationLanguage =
              acceptedLanguage
                ? targetLanguage
                : currentLanguage;

            skipLanguageConfirmation = true;

            opts = {
              ...opts,
              alreadyAddedUser: true,
            };
          } else {
            // A different message cancels the unanswered prompt.
            pendingLanguageConfirmationRef.current = null;
          }
        }

        // === HUMAN-FRIENDLY COMMAND ALIAS ROUTER ===
        let routedCommand = clean;

        const lc = clean.toLowerCase();
        const userCommand = lc.replace(/^ava\s+/, "").trim();

        if (lc === "diagnostics") {
          routedCommand = "ava diagnostics";
        } else if (lc === "repair dashboard") {
          routedCommand = "ava repair dashboard";
        } else if (lc === "pending fixes") {
          routedCommand = "ava pending fixes";
        } else if (lc.startsWith("review fix ")) {
          routedCommand = "ava " + clean;
        } else if (lc.startsWith("approve fix ")) {
          routedCommand = "ava " + clean;
        } else if (lc.startsWith("execute fix ")) {
          routedCommand = "ava " + clean;
        }

        // clear input immediately after capture so all return paths stay clean
        setInput("");

        // === FRIENDLY USER NAVIGATION COMMANDS ===
        if (
          handleFriendlyNavigationCommand(clean)
        ) {
          safeMaybeAddUser(maybeAddUser);

          let destination = "destination";

          if (lc.includes("profile")) destination = "Profile";
          else if (lc.includes("account")) destination = "Account Center";
          else if (lc.includes("games")) destination = "Games Hub";
          else if (lc.includes("education")) destination = "Education Center";
          else if (lc.includes("support")) destination = "Support Center";
          else if (lc.includes("settings")) destination = "Settings";

          await addAvaMessage(
            `Opening ${destination}...`,
            "brain"
          );

          return;
        }

        // === FRIENDLY DIAGNOSTICS ===
        if (
          ["diagnostics", "check account", "account diagnostics", "system check"].includes(userCommand)
        ) {
          safeMaybeAddUser(maybeAddUser);

          const planLabel = isAdminUser
            ? "Admin"
            : chatPlan === "pro"
            ? "Pro"
            : "Free";

          const memory =
            localStorage.getItem("ava_memory_learning_enabled") === "false"
              ? "Disabled"
              : "Available";

          await addAvaMessage(
            [
              "🧠 Ava Diagnostics",
              "",
              `Plan: ${planLabel}`,
              "System: Online",
              "Chat: Ready",
              `Memory: ${memory}`,
              "Learning Tools: Ready",
              "Account: Connected",
              "",
              "Your Ava systems appear operational."
            ].join("\n"),
            "brain"
          );

          return;
        }

        // === FREE/USER-SAFE THEME COMMAND GUARD ===
        if (avaIsThemeOrbColorCommand(clean) && !(isAdminUser || chatPlan === "pro")) {
          safeMaybeAddUser(maybeAddUser);
          await addAvaMessage(
            [
              "Theme customization is available with Pro.",
              "",
              "You can still use Ava normally, open Education, Games, Support, Profile, or Account.",
              "",
              "Try:",
              "• open account",
              "• open education",
              "• open games"
            ].join("\n"),
            "brain"
          );
          return;
        }

        // === LOCAL PLAN-AWARE COMMAND MENU START ===
        // === LOCAL STATUS COMMAND START ===
        if (["status", "/status", "check status", "ava status"].includes(lc) || userCommand === "status") {
          const planLabel = isAdminUser
            ? "Admin"
            : chatPlan === "pro"
            ? "Pro"
            : "Free";

          const memoryEnabled =
            localStorage.getItem("ava_memory_learning_enabled") !== "false";

          const subscriptionStatus =
            String(
              localStorage.getItem("subscription_status") || ""
            ).toLowerCase() === "active"
              ? "Active"
              : isAdminUser
              ? "Active"
              : "Inactive";

          const voiceStatus = isMutedRef.current
            ? "Muted"
            : "Enabled";

          const eyeStatus =
            isAdminUser || chatPlan === "pro"
              ? avaEyeOpen
                ? "Enabled"
                : "Available"
              : "Locked";

          const diagnosticsStatus =
            isAdminUser ? "Available" : null;

          await addAvaMessage({
            text: [
              "🧠 Ava Status",
              "",
              `Plan: ${planLabel}`,
              `Subscription: ${subscriptionStatus}`,
              "System: Online",
              "Chat: Ready",
              `Memory: ${memoryEnabled ? "Active" : "Disabled"}`,
              `Voice: ${voiceStatus}`,
              `Ava Eye: ${eyeStatus}`,
              "Learning Tools: Ready",
              diagnosticsStatus
                ? `Diagnostics: ${diagnosticsStatus}`
                : null,
              "Account: Connected",
            ]
              .filter(Boolean)
              .join("\n"),

            source: "brain",
          });

          return;
        }
        // === LOCAL STATUS COMMAND END ===

        // === HELP / ONBOARDING COMMAND ===
        if (["/help", "help"].includes(clean.toLowerCase())) {

          const lines = [
            "🧠 Welcome to AVAAIO",
            "",
            "I’m Ava — your bilingual AI assistant and interactive platform guide.",
            "",
            "You can talk naturally with Ava by typing or speaking conversationally.",
            "",
            "AVAAIO includes:",
            "• Interactive hologram chat",
            "• Draggable chat bubbles",
            "• Voice conversations",
            "• Memory and personalization",
            "• Education and games",
            "• Account and customization systems",
            "",
            "You can also ask Ava to guide you around the platform.",
            "",
            "Try saying:",
            "• show me around",
            "• open dashboard",
            "• open profile",
            "• what can Ava do?",
            "",
            "Tip: You do not need to memorize commands. Natural conversation works too.",
          ];

          await addAvaMessage({
            text: lines.join("\n"),
            source: "brain",
          });

          return;
        }

        // === COMMAND REFERENCE MENU ===
        if (["/commands", "/command", "commands", "command"].includes(clean.toLowerCase())) {
          const planLabel = isAdminUser
            ? "Admin"
            : chatPlan === "pro"
            ? "Pro"
            : "Free";

          const gettingStartedCommands = [
            "• help — learn what Ava can do",
            "• status — check Ava system status",
            "• commands — open the Ava help center",
          ];

          const navigationCommands = [
            "• open profile",
            "• open account",
            "• open games",
            "• open education",
            "• open support",
            "• open settings",
            "• open dashboard",
            "• logout",
          ];

          const featureCommands = [
            "• learning tools",
            "• check account",
          ];

          const proCommands = [
            "• change theme <color>",
            "• change orb <color>",
            "• Ava Vision controls",
            "• weather assistance",
            "• multilingual conversations",
          ];

            const lines = [
              `🧠 Ava Help Center (${planLabel} plan)`,
              "",
              "Ava can help you with:",
              "• Conversations and questions",
              "• Learning and education tools",
              "• Games and interactive features",
              "• Voice interaction",
                "• Interactive draggable chat bubbles",
                "• Hologram-style movable chat windows",
              "• Memory and personalization",
              "• Account and profile access",
              "• Theme customization",
              "• Weather assistance (Pro)",
              "• Multi-language support (Pro)",
                "• Bilingual conversations",
              "",
              "Quick Start:",
              ...gettingStartedCommands,
              "",
              "Navigation:",
              ...navigationCommands,
              "",
              "Features:",
              ...featureCommands,
              ...(chatPlan === "pro" || isAdminUser
                ? ["", "Pro Features:", ...proCommands]
                : []),
              "",
              "Tip: You can talk naturally to Ava instead of memorizing commands.",
                "Try saying:",
                "• show me around",
                "• explain AVAAIO",
                "• take me to settings",
                "• what can Ava do?",
            ];

          await addAvaMessage({
            text: lines.join("\n"),
            
            source: "brain",
          });
          return;
        }
        // === LOCAL PLAN-AWARE COMMAND MENU END ===

        // === LOCAL GUIDED TOUR START ===
        if (
          [
            "show me around",
            "tour",
            "platform tour",
            "guide me",
            "walkthrough",
            "give me a tour",
            "show platform",
            "show me the platform",
            "what pages are there",
            "explain avaaio",
          ].includes(clean.toLowerCase())
        ) {
          safeMaybeAddUser(maybeAddUser);

          const lines = [
            "🧠 AVAAIO Platform Guide",
            "",
            "I’m Ava — your bilingual AI assistant and interactive platform companion.",
            "",
            "You can speak naturally with Ava, type messages, or use the movable hologram-style chat bubbles around the screen.",
            "",
            "Platform Areas:",
            "",
            "🏠 Dashboard",
            "• Main home area",
            "• Quick access to Ava systems and features",
            "",
            "👤 Profile",
            "• Your personal information",
            "• Ava memory and personalization settings",
            "",
            "⚙️ Settings",
            "• Themes and appearance",
            "• Voice and system customization",
            "• Ava behavior controls",
            "",
            "🎮 Games",
            "• Interactive games and experiences",
            "• Chess, checkers, and future activities",
            "",
            "📘 Education",
            "• Learning tools and educational support",
            "• Interactive study assistance",
            "",
            "🛟 Support",
            "• Help center and platform guidance",
            "• Account and troubleshooting support",
            "",
            "🌍 Ava Features:",
            "• Bilingual conversations",
            "• Voice interaction",
            "• Memory and personalization",
            "• Interactive draggable chat bubbles",
            "• Hologram-style movable interface",
            "",
            "Try saying:",
            "• open dashboard",
            "• open settings",
            "• open games",
            "• help",
            "• status",
            "",
            "You can talk to Ava conversationally instead of memorizing commands.",
          ];

          await addAvaMessage({
            text: lines.join("\\n"),
            source: "brain",
          });

          return;
        }
        // === LOCAL GUIDED TOUR END ===

        // === LOCAL TYPED DIAGNOSTICS DIRECT START ===
        if (["diagnostics", "/diagnostics", "ava diagnostics", "ava diagnose"].includes(clean.toLowerCase())) {
          if (!opts?.alreadyAddedUser) addUserMessage(clean);
          const diagnosticText = await runAvaSelfDiagnostics();
          await addAvaMessage(diagnosticText, "brain");
          return;
        }
        // === LOCAL TYPED DIAGNOSTICS DIRECT END ===

        // === AVA_SELF_REPAIR_SAFE_HOOK ===
        if (isSelfRepairCommand(routedCommand)) {
          console.log("🔥 SELF REPAIR TRIGGERED", routedCommand);
          if (!opts?.alreadyAddedUser) addUserMessage(clean);
          const diagnosticText = await runAvaSelfDiagnostics();
          
await addAvaMessage(diagnosticText, "brain");
// AVA SPEECH END NOW CONTROLLED BY REAL AUDIO END EVENTS

          return;
        }
        // === END AVA_SELF_REPAIR_SAFE_HOOK ===

        const maybeAddUser = () => {
        if (!opts?.alreadyAddedUser) addUserMessage(clean);
      };

      
        // === LOCAL DATE FIX ===
        if (/what day is today|what'?s the date|current date/i.test(clean)) {
          const now = new Date();
          const formatted = now.toLocaleDateString(undefined, {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric"
          });

          safeMaybeAddUser(maybeAddUser);
          await addAvaMessage(`Today is ${formatted}.`, "brain");
          return;
        }

        // === AVA REAL SYSTEM AWARENESS HOOK ===
        if (isSystemAwarenessQuery(clean)) {
          safeMaybeAddUser(maybeAddUser);
          const reply = await buildSystemAwarenessReply(clean);
          await addAvaMessage(reply, "brain");
          return;
        }
        // === END AVA REAL SYSTEM AWARENESS HOOK ===

        // === AVA SANDBOX DIAGNOSTICS + REPAIR HOOK ===
        if (isSandboxRepairCommand(clean)) {
          if (!opts?.alreadyAddedUser) addUserMessage(clean);
          const reply = await buildSandboxRepairReply(clean);
          await addAvaMessage(reply, "brain");
          return;
        }
        // === END AVA SANDBOX DIAGNOSTICS + REPAIR HOOK ===


        

          // === ADMIN PIN BACKEND GATE ===

          const submittedPin = (
            clean.match(/^ava\s+pin\s+(.+)$/i)?.[1] || ""
          ).trim();

          if (submittedPin) {
            safeMaybeAddUser(maybeAddUser);

            try {
              const res = await fetch(
                "/api/v1/chat/verify-admin-pin",
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  credentials: "include",
                  body: JSON.stringify({
                    pin: submittedPin,
                  }),
                }
              );

              const data = await res.json().catch(
                () => ({})
              );

              sessionStorage.removeItem(
                "ava_admin_pin_verified"
              );

              if (res.ok && data?.ok) {
                await addAvaMessage(
                  "✅ Super Admin controls unlocked for ten minutes.",
                  "brain"
                );
              } else {
                await addAvaMessage(
                  data?.detail ||
                    data?.message ||
                    "❌ Administrator verification failed.",
                  "brain"
                );
              }
            } catch (err) {
              console.error(err);
              await addAvaMessage(
                "⚠️ Administrator verification request failed.",
                "brain"
              );
            }

            return;
          }

          if (clean === "ava lock admin") {
            safeMaybeAddUser(maybeAddUser);

            try {
              const res = await fetch(
                "/api/v1/chat/admin-elevation/logout",
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  credentials: "include",
                  body: JSON.stringify({}),
                }
              );

              const data = await res.json().catch(
                () => ({})
              );

              sessionStorage.removeItem(
                "ava_admin_pin_verified"
              );

              await addAvaMessage(
                res.ok
                  ? "🔒 Administrator controls locked."
                  : data?.detail ||
                      data?.message ||
                      "Unable to lock administrator controls.",
                "brain"
              );
            } catch (err) {
              console.error(err);
              await addAvaMessage(
                "⚠️ Administrator lock request failed.",
                "brain"
              );
            }

            return;
          }

          if (
            clean.startsWith("ava propose ") ||
            false || // backend-first: do not locally intercept ava repair
            clean.startsWith("ava apply ") ||
            clean === "ava fix yourself" ||
            clean.startsWith("ava open eye") ||
            clean.startsWith("ava close eye") ||
            clean.startsWith("ava open mouth") ||
            clean.startsWith("ava close mouth")
          ) {
            safeMaybeAddUser(maybeAddUser);

            let elevated = false;

            try {
              const res = await fetch(
                "/api/v1/chat/admin-elevation/status",
                {
                  method: "GET",
                  credentials: "include",
                }
              );

              const data = await res.json().catch(
                () => ({})
              );

              elevated = Boolean(
                res.ok &&
                data?.elevated
              );
            } catch {
              elevated = false;
            }

            if (!elevated) {
              await addAvaMessage(
                "🔐 Open the Admin Console and choose Unlock Admin.",
                "brain"
              );
              return;
            }
          }

          // === END ADMIN PIN BACKEND GATE ===


// === AVA FEATURE SCAN ===
        if (clean === "ava scan self") {
          safeMaybeAddUser(maybeAddUser);

          const logs = (window as any).__AVA_MESH_LOGS || [];

          if (!logs.length) {
            await addAvaMessage(
              "No mesh logs detected. Make sure AvaFace mesh logging is active.",
              "brain"
            );
            return;
          }

          const scan = logs.map((m: any) => ({
            feature: detectFeatureFromNames(m.name, m.material),
            meshName: m.name,
            materialName: m.material,
          }));

          const summary = buildFeatureSummary(scan);

          await addAvaMessage(summary, "brain");
          return;
        }
        // === END AVA FEATURE SCAN ===


        if (clean.startsWith("ava diagnose ")) {
          safeMaybeAddUser(maybeAddUser);

          const feature = normalizeDiagnoseFeature(clean);
          const logs = (window as any).__AVA_MESH_LOGS || [];

          const scan = logs.map((m: any) => ({
            feature: detectFeatureFromNames(m.name, m.material),
            meshName: m.name,
            materialName: m.material,
          }));

          let count = 0;
          if (feature === "horn") {
            count = scan.filter((x: any) => x.feature === "horn_left" || x.feature === "horn_right").length;
          } else if (feature === "helmet") {
            count = scan.filter(
              (x: any) =>
                x.feature === "helmet" ||
                x.feature === "side_panel" ||
                x.feature === "top_strip"
            ).length;
          } else {
            count = scan.filter((x: any) => x.feature === feature).length;
          }

          const reply = buildFeatureDiagnosis(feature, count);
          await addAvaMessage(reply, "brain");
          return;
        }


        if (clean.startsWith("ava propose ")) {
          safeMaybeAddUser(maybeAddUser);

          const feature = normalizeProposeFeature(clean);
          const logs = (window as any).__AVA_MESH_LOGS || [];

          const scan = logs.map((m: any) => ({
            feature: detectFeatureFromNames(m.name, m.material)
          }));

          let count = 0;

          if (feature === "horn") {
            count = scan.filter((x: any) => x.feature === "horn_left" || x.feature === "horn_right").length;
          } else if (feature === "helmet") {
            count = scan.filter(
              (x: any) =>
                x.feature === "helmet" ||
                x.feature === "side_panel" ||
                x.feature === "top_strip"
            ).length;
          } else {
            count = scan.filter((x: any) => x.feature === feature).length;
          }

          const reply = buildRepairProposal(feature, count);
          await addAvaMessage(reply, "brain");
          return;
        }

const cmd = normalizeCmd(clean);

      if (isStopCmd(cmd)) {
        safeMaybeAddUser(maybeAddUser);
        stopAvaNow();
        



await addAvaMessage("Okay - stopped.", "brain");
// AVA SPEECH END NOW CONTROLLED BY REAL AUDIO END EVENTS

        return;
      }

      // AVAAIO_CONNECTION_MODE_COMMAND_START
      const requestedConnectionMode =
        detectConnectionModeCommand(clean);

      if (requestedConnectionMode) {
        safeMaybeAddUser(maybeAddUser);

        try {
          // Confirmation is announced only by the shared provider event
          // after the backend PATCH response has been accepted.
          await setConnectionMode(
            requestedConnectionMode
          );
        } catch (error) {
          console.error(
            "[ConnectionMode] voice/text command failed",
            error
          );

          await addAvaMessage(
            connectionModeCopyRef.current
              .unavailable,
            "error"
          );
        }

        return;
      }
      // AVAAIO_CONNECTION_MODE_COMMAND_END

      const math = tryLocalMathAnswer(clean);
      if (math.ok && math.answer) {
        safeMaybeAddUser(maybeAddUser);
        
await addAvaMessage(math.answer, "brain");
// AVA SPEECH END NOW CONTROLLED BY REAL AUDIO END EVENTS

        return;
      }

      const isStatusCmd = false; // backend-first: do not hijack ava status/status
      const isPortsCmd = false; // backend-first
      const isHealthCmd = false; // backend-first
      const isHelpCmd = cmd === "ava help" || cmd === "help";
      const isClearCmd = cmd === "ava clear" || cmd === "clear";
      const isLogsCmd = false; // backend-first
      const isCopyLogsCmd =
        cmd === "ava copy logs" || cmd === "copy logs" || cmd === "copylogs" || cmd === "ava copylogs";

      if (isHelpCmd) {
        safeMaybeAddUser(maybeAddUser);
        await addAvaMessage(getLocalHelpText(), "brain");
        return;
      }

      if (isClearCmd) {
        safeMaybeAddUser(maybeAddUser);
        setMessages([]);
        
await addAvaMessage("Chat cleared.", "brain");
// AVA SPEECH END NOW CONTROLLED BY REAL AUDIO END EVENTS

        return;
      }

      if (isPortsCmd) {
        safeMaybeAddUser(maybeAddUser);
        
await addAvaMessage("Ports: Frontend 5173 • Dashboard API 8000 • Brain 8001", "brain");
// AVA SPEECH END NOW CONTROLLED BY REAL AUDIO END EVENTS

        return;
      }

      if (isStatusCmd) {
        safeMaybeAddUser(maybeAddUser);
        await addAvaMessage(
          buildStatusText({
            mode: getMode(),
            api: pickApiBase(),
            tokenPresent: !!getToken(),
            voiceChoice,
            formatChoice,
            regionChoice,
            isMuted: isMutedRef.current,
            micPermGranted: micPermGrantedRef.current,
            sttModeLabel,
            isMicOn,
            webspeechLockedOffline: webspeechLockedOfflineRef.current,
          }),
          "brain"
        );
        return;
      }

      if (isHealthCmd) {
        safeMaybeAddUser(maybeAddUser);

        const dashUrl = joinUrl(pickApiBase(), "/health");
        const brainUrl = joinUrl(BRAIN_BASE, "/api/brain/health");

        const [dash, brain] = await Promise.all([
          fetchJsonWithTimeout(dashUrl, 2500),
          fetchJsonWithTimeout(brainUrl, 2500),
        ]);

        const dashLine = `Dashboard /health: ${dash.ok ? "OK" : "FAIL"}${dash.status ? ` (${dash.status})` : ""}`;
        const brainLine = `Brain /api/brain/health: ${brain.ok ? "OK" : "FAIL"}${brain.status ? ` (${brain.status})` : ""}`;

        
await addAvaMessage(`${dashLine}\n${brainLine}`, "brain");
// AVA SPEECH END NOW CONTROLLED BY REAL AUDIO END EVENTS

        return;
      }

      if (isLogsCmd) {
        safeMaybeAddUser(maybeAddUser);
        const logs = getCapturedLogs(40);
        
await addAvaMessage(logs || "No logs captured yet.", "brain");
// AVA SPEECH END NOW CONTROLLED BY REAL AUDIO END EVENTS

        return;
      }

      if (isCopyLogsCmd) {
        safeMaybeAddUser(maybeAddUser);
        const logs = getCapturedLogs(80);
        const ok = logs ? await copyToClipboard(logs) : false;
        await addAvaMessage(ok ? "Copied logs to clipboard." : "Could not copy logs (browser blocked).", "brain");
        return;
      }

      // UX FIX:
      // Do NOT globally freeze chat interaction while Ava responds.
      // Request ownership + AbortController already protect runtime safety.

      sendingLockRef.current = true;

      // NON-BLOCKING: removed pre-speech stopVoiceNow

      const reqId =
        // @ts-ignore
        crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(16).slice(2)}`;

      activeReqIdRef.current = reqId;


      // A new question permanently supersedes any older voice fetch
      // or reusable-player audio.
      ttsRequestEpochRef.current += 1;

      const supersededTtsController =
        ttsAbortRef.current;

      ttsAbortRef.current = null;

      try {
        supersededTtsController?.abort();
      } catch {}

      clearAudioResources();

const controller = new AbortController();
      chatAbortRef.current = controller;

      safeMaybeAddUser(maybeAddUser);
      setInput("");

      if (speechEndTimerRef.current) {
        window.clearTimeout(speechEndTimerRef.current);
        speechEndTimerRef.current = null;
      }
      setIsSending(true);
      
console.log("[SET TYPING TRUE]", {
  reqId,
  ts: Date.now()
});
setIsTyping(true);

// The newest request explicitly supersedes any unresolved request.
removeThinkingMessage();
beginThinkingMessage(reqId);

// Keep the thinking indicator active while the request is running.
// This watchdog is only a final recovery mechanism.
clearTypingWatchdog();

typingWatchdogRef.current = window.setTimeout(() => {
  if (activeReqIdRef.current !== reqId) return;

  console.warn("[TYPING WATCHDOG] request exceeded 120 seconds", {
    reqId,
    ts: Date.now(),
  });

  sendingLockRef.current = false;
  activeReqIdRef.current = "";
  setIsSending(false);
  setIsTyping(false);
  typingWatchdogRef.current = null;
}, 120_000);



      // ⚡ Instant visual response state
      try {
        console.log("[thinking]", "Ava is thinking...");
      } catch (e) {
        console.log("[instant response ui]", e);
      }

      try {
                // === AVA SELF-REPAIR TRIGGER ===
        const repairText = (text || input || '').toString().trim();
        const userTextLower = repairText.toLowerCase();

        // === DIRECT THEME / ORB COLOR COMMAND ===
        if (avaIsThemeOrbColorCommand(repairText)) {

          
const resolveThemeAdminRole = async (): Promise<string> => {
            console.log("[resolveThemeAdminRole] called", {
              time: Date.now(),
              repairText,
              stack: new Error().stack,
            });

            const directRole = (
              (typeof user !== "undefined" && (user as any)?.role) ||
              localStorage.getItem("ava_user_role") ||
              localStorage.getItem("role") ||
              ""
            ).toString().toLowerCase();

            if (["admin", "owner", "superadmin"].includes(directRole)) {
              return directRole;
            }

            const token =
              localStorage.getItem("token") ||
              localStorage.getItem("access_token") ||
              localStorage.getItem("authToken") ||
              localStorage.getItem("jwt") ||
              sessionStorage.getItem("token") ||
              sessionStorage.getItem("access_token") ||
              sessionStorage.getItem("authToken") ||
              sessionStorage.getItem("jwt") ||
              "";

            try {
              const authRes = await fetch("https://api.avaaio.com/api/v1/auth/me", {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              });

              if (authRes.ok) {
                const authJson = await authRes.json().catch(() => null);
                const authUser = (authJson as any)?.user || authJson;
                const apiRole = String((authUser as any)?.role || "").toLowerCase();

                
const apiSub = String(
                    (authUser as any)?.subscription_status ||
                    (authUser as any)?.subscription ||
                    ""
                  ).toLowerCase();

                  const normalizedRole =
                    ["admin", "owner", "superadmin"].includes(apiRole)
                      ? "admin"
                      : apiRole;

                  
if (normalizedRole) {
                    localStorage.setItem("ava_user_role", normalizedRole);
                    localStorage.setItem("role", normalizedRole);
                  }

                  if (apiSub) {
                    localStorage.setItem("subscription_status", apiSub);
                  }

                  const derivedPlan =
                    normalizedRole === "admin"
                      ? "admin"
                      : (
                          normalizedRole === "vip" ||
                          apiSub === "active"
                        )
                          ? "pro"
                          : "free";

                  localStorage.setItem("ava_chat_plan", derivedPlan);

                  console.log("[DashboardAuth hydrated]", {
                    role: normalizedRole,
                    subscription_status: apiSub,
                    derivedPlan,
                  });

                  return normalizedRole || directRole;

              }
            } catch (e) {
              console.warn("[Ava theme admin check] auth/me failed", e);
            }

            return directRole;
          };

          const role = await resolveThemeAdminRole();
          const isAdminUser = ["admin", "owner", "superadmin"].includes(role);

          if (!isAdminUser) {
            await addAvaMessage({
              text: `Theme customization is available for admins only. Debug role: ${role || "empty"}`,
              
              source: "brain",
              reqId,
            });
            return;
          }

          const match = repairText
            .toLowerCase()
            .match(
              /\b(red|crimson|cyan|blue|midnight|purple|green|emerald|orange|yellow|gold|amber|pink|aurora|lava|white|black)\b/,
            );

          const aliases: Record<string, string> = {
            red: "lava",
            crimson: "lava",
            blue: "midnight",
            green: "emerald",
            orange: "amber",
            yellow: "amber",
            gold: "amber",
            pink: "aurora",
            white: "cyan",
          };

          const requested = match?.[1] || "cyan";
          const color =
            aliases[requested] || requested;

          try {
            setTheme(color as any);
          } catch (e) {
            console.warn("Theme command local update failed", e);
          }

          

          await addAvaMessage({
            text: `Done — I changed your orb color to ${color}.`,
            
            source: "brain",
            reqId,
          });

          return;
        }

        if (
            !avaIsThemeOrbColorCommand(repairText) &&
          userTextLower.includes("fix") ||
          userTextLower.includes("repair") ||
          userTextLower.includes("dashboard")
        ) {
          try {
            localStorage.setItem("ava_last_repair_request", repairText);

            const res = await fetch(`${API_BASE}/api/v1/chat/repair/analyze`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ text: localStorage.getItem("ava_last_repair_request") || repairText }),
            });

            const txt = await res.text();

              let cleanRepairText = txt;
              try {
                const parsed = txt ? JSON.parse(txt) : {};
                if (parsed?.blocked || String(parsed?.error || "").toLowerCase().includes("admin only")) {
                  cleanRepairText = "That command is restricted to administrators.";
                }
              } catch {}

              await addAvaMessage({
                text: cleanRepairText,
                
                source: "brain",
                reqId,
              });

            return;
          } catch (e) {
            console.error("Repair route failed", e);
          }
        }



        // === AVA APPLY REPAIR ===
        if (repairText === "ava apply repair") {
          try {
            const res = await fetch(`${API_BASE}/api/v1/chat/repair/apply`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ text: localStorage.getItem("ava_last_repair_request") || repairText }),
            });

            const data = await res.json();
            if (data?.blocked || String(data?.error || "").toLowerCase().includes("admin only")) {
              await addAvaMessage({
                text: "That command is restricted to administrators.",
                
                source: "brain",
                reqId,
              });
              return;
            }


            await addAvaMessage({
              text: "✅ Repair applied successfully.",
              
              source: "brain",
              reqId,
            });

            return;
          } catch (e) {
            console.error("Apply repair failed", e);
          }
        }

console.log("[TRACE A] passed repair/theme section");

const behavior = JSON.parse(localStorage.getItem("ava_behavior") || "{}");
        
// === PRO WEATHER + MULTILINGUAL GATE START ===
const normalizedMessage = String(routedCommand || "").toLowerCase();

const isWeatherCommand =
  /\b(weather|forecast|temperature|temp|rain|snow|humidity)\b/i.test(normalizedMessage);

const isMultilingualCommand =
  /\b(translate|translation|speak spanish|speak french|speak german|spanish|french|german|italian|portuguese|japanese|chinese|korean|arabic|russian)\b/i.test(normalizedMessage);

console.log("[TRACE B] before pro gate", {
  chatPlan,
  subscription_status: localStorage.getItem("subscription_status"),
  isAdminUser:
    typeof isAdminUser !== "undefined"
      ? isAdminUser
      : "UNDEFINED"
});

const hasProAccess =
  isAdminUser ||
  chatPlan === "pro" ||
  String(localStorage.getItem("subscription_status") || "").toLowerCase() === "active";

if ((isWeatherCommand || isMultilingualCommand) && !hasProAccess) {
  await addAvaMessage(
    [
      "This feature is available with Pro.",
      "",
      isWeatherCommand
        ? "• Live weather assistance requires Pro access."
        : "• Multi-language conversations require Pro access.",
      "",
      "You can still use Ava normally with chat, games, education, support, and profile tools."
    ].join("\n"),
    "brain",
    reqId
  );

  return;
}
// === PRO WEATHER + MULTILINGUAL GATE END ===

console.log("[TRACE C] about to fetch chat API");

const response = await fetch(chatApiUrl(), {
          method: "POST",
          credentials: "include",
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            "X-User-Timezone":
              Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
            ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
          },
          body: JSON.stringify({
              behavior,
              message: routedCommand,
              input_source: messageInputSource,
              detected_language: messageDetectedLanguage,
              conversation_language: messageConversationLanguage,
              language_confirmation_enabled: hasProAccess,
              skip_language_confirmation: skipLanguageConfirmation,
              declined_languages: Array.from(
                declinedLanguageRef.current
              ),
              avoid_repetition: true,

              recent_messages: messages.slice(-12).map((m) => ({
                sender: m.sender,
                text: m.text,
                ts: m.ts,
              })),
              client_time: new Date().toISOString(),
                client_local_time: new Date().toLocaleString("en-US", {
                  hour12: true,
                }),
                user_timezone:
                  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
                timezone_offset_minutes: new Date().getTimezoneOffset(),
              user_id: USER_ID,
              // AVAAIO_CONNECTION_MODE_REQUEST_START
              connection_mode:
                connectionModeRef.current
                  .requestedMode,
              mode:
                connectionModeRef.current
                  .requestedMode,
              // AVAAIO_CONNECTION_MODE_REQUEST_END
              vision: getLatestVision(),
            }),
        });

        console.log("[TRACE D] fetch returned");
console.log("CHAT RESPONSE STATUS", response.status, response.ok);
        if (activeReqIdRef.current !== reqId) {

          console.warn(
            "[REQ ABORT] stale request cleanup"
          );

          // Superseded request: preserve the newer request state.

          return;
        }

        if (!response.ok) {
          const errText = await response.text().catch(() => "");
          throw new Error(`Chat API failed (${response.status}) ${errText}`);
        }

        const data = await (async () => {
  const raw = await response.text();
  try {
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    console.error("⚠️ NON-JSON RESPONSE:", raw.slice(0, 300));
    return {};
  }
})();

console.log("[TRACE E1] data parsed", data);

// AVAAIO_CONNECTION_MODE_RESPONSE_START
reconcileFromChatPayload(data);
// AVAAIO_CONNECTION_MODE_RESPONSE_END

// Preserve the original question while awaiting language consent.
if (data?.language_confirmation?.required) {
  pendingLanguageConfirmationRef.current = {
    originalMessage:
      data.language_confirmation.original_message ||
      routedCommand,
    inputSource:
      data.language_confirmation.input_source ||
      messageInputSource,
    detectedLanguage:
      data.language_confirmation.detected_language || "",
    languageName:
      data.language_confirmation.language_name || "",
    currentLanguage:
      data.language_confirmation.current_language ||
      messageConversationLanguage ||
      "en",
  };
}

        // === AVA FREE PLAN BACKEND BRIDGE START ===
        if (data?.error === "free_plan_locked") {
          console.warn("[AVA LOCK] Free plan limit reached", data);

          setChatLock({
            limit_reached: true,
            locked_until: Date.now() + 24 * 60 * 60 * 1000,
            features_locked: {
              chat: true,
              mic: true,
              mute: true,
              eye: true,
            },
          });

          setChatPlan("free");

          if (typeof data?.daily_limit !== "undefined") {
            setDailyLimit(data.daily_limit);
          }

          if (typeof data?.words_used !== "undefined") {
            setWordsUsed(data.words_used || 0);
          }

          await addAvaMessage(
            data?.message ||
              "You’ve reached today’s free Ava limit. Some features are locked until your limit resets. Please come back tomorrow or upgrade to unlock more.",
            "brain",
            reqId
          );

          return;
        }
        // === AVA FREE PLAN BACKEND BRIDGE END ===

        if (activeReqIdRef.current !== reqId) {

          console.warn(
            "[REQ ABORT] stale request cleanup"
          );

          // Superseded request: preserve the newer request state.

          return;
        }

        
console.log(
  "[CHAT RESPONSE RAW DATA]",
  data
);

console.log("[TRACE E2] before parseReply");

const rawReply = parseReply(data);

console.log(
  "[CHAT PARSED REPLY]",
  rawReply
);

console.log("[TRACE E3] after parseReply", rawReply);

console.log("[TRACE E4] before expression handling");

// ==========================================================
// AVA BACKEND → FACE EXPRESSION BRIDGE
// ==========================================================

try {

  const rawExpression =
    data?.expression ??
    data?.emotion ??
    data?.face ??
    data?.avatar_expression ??
    "NEUTRAL";

  const expression =
    typeof rawExpression === "string"
      ? rawExpression
      : (
          rawExpression?.face ??
          rawExpression?.expression ??
          rawExpression?.emotion ??
          "NEUTRAL"
        );

  console.log(
    "[AVA EXPRESSION RAW]",
    rawExpression
  );

  console.log(
    "[AVA EXPRESSION FACE]",
    expression
  );

  console.log("[AVA API RESPONSE]", data);
  console.log("[AVA BACKEND EXPRESSION]", expression);

  if (typeof (window as any).avaExpression === "function") {

    (window as any).avaExpression(
      String(expression).toUpperCase()
    );

    console.log(
      "[AVA FACE EXPRESSION]",
      expression
    );

  }

} catch (err) {

  console.warn(
    "[AVA FACE EXPRESSION FAILED]",
    err
  );

}

const finalReply = rawReply;

console.log("[TRACE E5] adaptive backend reply preserved");

console.log("[TRACE F] FINAL REPLY", finalReply);
console.log("[TRACE F TYPE]", typeof finalReply);

console.log(
  "[CHAT FINAL REPLY]",

  finalReply
);

console.log("[TRACE] BEFORE addAvaMessage");

await addAvaMessage({
          text: finalReply,
          source: didUseFallback(data) ? "openai" : "brain",
          reqId,
          // Final assistant replies should speak.
          // Tiny/login/help/system fastpaths can still be silenced elsewhere.
          noTts: false,
        });

console.log("[TRACE] AFTER addAvaMessage");

        avaSpeechActiveRef.current = true;
      } catch (error: any) {
        if (error?.name === "AbortError") {
  console.warn("[CHAT ABORTED SAFE CLEANUP]");
}
        
console.error("[CHAT SEND ERROR]", error);
console.error("[CHAT SEND ERROR STACK]", error?.stack);


        if (activeReqIdRef.current !== reqId) {

          console.warn(
            "[REQ ABORT] stale request cleanup"
          );

          // Superseded request: preserve the newer request state.

          return;
        }

        
const isFreeLimitError =
  error?.status === 429 ||
  error?.response?.status === 429 ||
  String(error?.message || "").includes("429");

await addAvaMessage(
  isFreeLimitError
    ? "You’ve reached today’s free Ava limit. Some features are locked until your limit resets. Please come back tomorrow or upgrade to unlock more."
    : "I’m having trouble reaching assist services right now. If you want, say: 'Ava go offline' to stay local.",
  isFreeLimitError ? "brain" : "error",
  reqId
);
// AVA SPEECH END NOW CONTROLLED BY REAL AUDIO END EVENTS

      } finally {
        clearTypingWatchdog();

        if (activeReqIdRef.current === reqId) {
          // Release the HTTP/send lock.
          // TTS owns the thinking-to-speaking transition.
          sendingLockRef.current = false;
          activeReqIdRef.current = "";
          setIsSending(false);

          markInteractionAndUnlock();

          console.log("[CHAT REQUEST COMPLETE]", {
            reqId,
            typingOwnedByTts: true,
            ts: Date.now(),
          });
        } else {
          console.log("[CHAT FINALLY] superseded request ignored", {
            reqId,
            activeReqId: activeReqIdRef.current,
            ts: Date.now(),
          });
        }
      }
    },
    [
      addAvaMessage,
        addUserMessage,
        beginThinkingMessage,
        chatApiUrl,
        clearAudioResources,
      copyToClipboard,
      fetchJsonWithTimeout,
      formatChoice,
      getCapturedLogs,
      input,
      isMicOn,
      isSending,
      markInteractionAndUnlock,
      primeAudioOnce,
        regionChoice,
        removeThinkingMessage,
      sttModeLabel,
      stopAvaNow,
      stopVoiceNow,
      voiceChoice,
    ]
  );

  const flushFinalTranscript = useCallback(async () => {
    clearFinalSendTimer();
    const finalText = finalTranscriptBufferRef.current.trim();


      
    if (!finalText) return;


      const normalizedFinalText =
        finalText
          .toLocaleLowerCase()
          .replace(/\s+/g, " ")
          .trim();

      const previousSubmission =
        lastVoiceSubmissionRef.current;

      if (
        normalizedFinalText &&
        previousSubmission.text ===
          normalizedFinalText &&
        Date.now() -
          previousSubmission.ts <
          5000
      ) {
        console.warn(
          "[Mic] duplicate voice submission blocked:",
          finalText
        );

        finalTranscriptBufferRef.current =
          "";

        voiceDraftRef.current = "";
        setInput("");

        return;
      }

      lastVoiceSubmissionRef.current = {
        text: normalizedFinalText,
        ts: Date.now(),
      };

finalTranscriptBufferRef.current = "";
    voiceDraftRef.current = "";
    setInput("");

      if (speechEndTimerRef.current) {
        window.clearTimeout(speechEndTimerRef.current);
        speechEndTimerRef.current = null;
      }

    addUserMessage(finalText);
    
// === STOP AUDIO BEFORE NEW MESSAGE ===
try {
  window.speechSynthesis?.cancel?.();
} catch {}
try {
  if (audioRef.current) {
    audioRef.current.pause();
      if (pauseStartedAt === null) {
        setPauseStartedAt(Date.now());
      }
    audioRef.current.currentTime = 0;
  }
} catch {}

await sendMessage(finalText, {
  alreadyAddedUser: true,
  inputSource: "voice",
});
  }, [addUserMessage, clearFinalSendTimer, sendMessage]);

  const scheduleFinalTranscriptSend = useCallback(() => {
    clearFinalSendTimer();
    finalSendTimerRef.current = window.setTimeout(() => {
      void flushFinalTranscript();
    }, MIC_FINAL_SEND_DEBOUNCE_MS);
  }, [clearFinalSendTimer, flushFinalTranscript]);

    const handleLocalAvaMicControl =
      useCallback(
        (rawText: string): boolean => {
          const normalized =
            normalizeAvaMicPhrase(rawText);

          if (!normalized) {
            return false;
          }

          const wakePhrase =
            isAvaWakePhrase(normalized);

          if (
            wakePhrase &&
            Date.now() -
              lastWakePhraseAtRef.current <
              2500
          ) {
            return true;
          }

          if (
            wakeStandbyRef.current &&
            !conversationActiveRef.current
          ) {
            if (!wakePhrase) {
              return false;
            }

            if (
              wakeActivationInFlightRef.current
            ) {
              return true;
            }

            lastWakePhraseAtRef.current =
              Date.now();

            wakeActivationInFlightRef.current =
              true;

            wakeStandbyRef.current = false;
            conversationActiveRef.current = true;

            setMicConversationMode(
              "conversation_active"
            );

            clearFinalSendTimer();
            finalTranscriptBufferRef.current = "";
            voiceDraftRef.current = "";
            setInput("");

            const trustedName =
              resolveWakeUserName();

            const acknowledgement =
              trustedName
                ? `Yes, ${trustedName}?`
                : "Yes, I’m listening.";

            pushMessage({
              id: uid(),
              sender: "ava",
              text: acknowledgement,
              ts: Date.now(),
            });

            window.setTimeout(() => {
              void speakAva(
                acknowledgement
              );
            }, 0);

            window.setTimeout(() => {
              wakeActivationInFlightRef.current =
                false;
            }, 5000);

            console.log(
              "[WakeWord] conversation activated"
            );

            return true;
          }

          if (
            conversationActiveRef.current
          ) {
            if (
              isAvaMicOffPhrase(normalized)
            ) {
              setWakeWordPreference(false);

              pushMessage({
                id: uid(),
                sender: "ava",
                text: "Microphone off.",
                ts: Date.now(),
              });

              disableMic(
                "Ava voice command"
              );

              console.log(
                "[WakeWord] microphone disabled by voice command"
              );

              return true;
            }

            if (
              isAvaSleepPhrase(normalized)
            ) {
              conversationActiveRef.current =
                false;

              wakeStandbyRef.current =
                true;

              wakeActivationInFlightRef.current =
                false;

              clearFinalSendTimer();
              finalTranscriptBufferRef.current = "";
              voiceDraftRef.current = "";
              setInput("");

              setMicConversationMode(
                "wake_standby"
              );

              pushMessage({
                id: uid(),
                sender: "ava",
                text: "Standing by.",
                ts: Date.now(),
              });

              console.log(
                "[WakeWord] returned to wake standby"
              );

              return true;
            }

            if (wakePhrase) {
              lastWakePhraseAtRef.current =
                Date.now();

              return true;
            }
          }

          return false;
        },
        [
          clearFinalSendTimer,
          disableMic,
          isAvaMicOffPhrase,
          isAvaSleepPhrase,
          isAvaWakePhrase,
          normalizeAvaMicPhrase,
          pushMessage,
          resolveWakeUserName,
          setWakeWordPreference,
          speakAva,
        ]
      );

    const startOfflineListening = useCallback(async () => {
    // AVAAIO_CONNECTION_MODE_OFFLINE_STT_GUARD_START
    if (isConnectionVoiceInputBlocked()) {
      offlineStartInFlightRef.current = false;
      disableMic(
        "connection mode blocks cloud STT"
      );
      return;
    }
    // AVAAIO_CONNECTION_MODE_OFFLINE_STT_GUARD_END

    if (offlineStartInFlightRef.current) {
      return;
    }

    if (
      offlinePauseForAvaRef.current ||
      isAvaSpeakingRef.current
    ) {
      return;
    }

    const existingRecorder =
      sttRecorderRef.current;

    if (
      existingRecorder &&
      existingRecorder.state !== "inactive"
    ) {
      return;
    }

    offlineStartInFlightRef.current = true;
      markInteractionAndUnlock();
    markMicActivity();
    sttModeRef.current = "offline";
    setSttModeLabel("offline");
    offlineUserStopRef.current = false;
    webspeechUserStopRef.current = false;
    micArmedRef.current = true;
    setIsMicOn(true);

    micStartedAtRef.current = Date.now();

    const permissionOk = await ensureMicPermission();

    if (!permissionOk) {
      offlineStartInFlightRef.current = false;
      pushMessage({
        id: uid(),
        sender: "ava",
        text: "I can’t access your microphone. Allow Mic permission in the browser, then press Mic again.",
        source: "error",
        ts: Date.now(),
      });
      disableMic("permission failed");
      return;
    }

    try {
      resetOfflineRecorderCycle();

      if (!navigator?.mediaDevices?.getUserMedia) {
        pushMessage({
          id: uid(),
          sender: "ava",
          text: "Offline Mic needs mediaDevices.getUserMedia, but it’s not available in this browser.",
          source: "error",
          ts: Date.now(),
        });
        disableMic("mediaDevices unavailable");
        return;
      }

      let stream =
        sttStreamRef.current;

      let liveTracks =
        stream
          ?.getAudioTracks()
          .filter(
            (track) =>
              track.readyState === "live"
          ) ?? [];

      if (
        !stream ||
        liveTracks.length === 0
      ) {
        setAvaAudioSessionType(
          "play-and-record"
        );

        stream =
          await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          });

        sttStreamRef.current = stream;
        micPermGrantedRef.current = true;

        liveTracks =
          stream
            .getAudioTracks()
            .filter(
              (track) =>
                track.readyState === "live"
            );

        console.log(
          "[OfflineSTT] replacement stream acquired"
        );
      } else {
        console.log(
          "[OfflineSTT] retained stream reused"
        );
      }

      liveTracks.forEach((track) => {
        track.enabled = true;
      });

        let mime = "";

        if (
          typeof MediaRecorder !==
          "undefined"
        ) {
          const supports =
            (MediaRecorder as any)
              .isTypeSupported;

          if (
            supports?.(
              "audio/webm;codecs=opus"
            )
          ) {
            mime =
              "audio/webm;codecs=opus";
          } else if (
            supports?.(
              "audio/webm"
            )
          ) {
            mime = "audio/webm";
          } else if (
            supports?.(
              "audio/mp4;codecs=mp4a.40.2"
            )
          ) {
            mime =
              "audio/mp4;codecs=mp4a.40.2";
          } else if (
            supports?.(
              "audio/mp4"
            )
          ) {
            mime = "audio/mp4";
          } else if (
            supports?.(
              "audio/ogg;codecs=opus"
            )
          ) {
            mime =
              "audio/ogg;codecs=opus";
          }
        }

      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      sttRecorderRef.current = recorder;
      sttChunksRef.current = [];
      sttRetryOnceRef.current = false;

      recorder.onstart = () => {
        offlineStartInFlightRef.current = false;
        offlineUserStopRef.current = false;
        sttStartedAtRef.current = Date.now();
        setIsListening(true);
        voiceDraftRef.current = "";
        startOfflineWave();
        setInput("");

      if (speechEndTimerRef.current) {
        window.clearTimeout(speechEndTimerRef.current);
        speechEndTimerRef.current = null;
      }
        markMicActivity();
      markInteractionAndUnlock();
      };

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event?.data && event.data.size > 0) sttChunksRef.current.push(event.data);
      };

      recorder.onerror = () => {
        offlineStartInFlightRef.current = false;
        setIsListening(false);
        voiceDraftRef.current = "";

        cleanupOfflineStt();

        if (!micArmedRef.current) return;

        pushMessage({
          id: uid(),
          sender: "ava",
                      text:
              interfaceCopyRef.current.chat.offlineMicError,
            source: "error",
          ts: Date.now(),
        });
      };

      recorder.onstop = async () => {
        offlineStartInFlightRef.current = false;
        sttRecorderRef.current = null;

        setIsListening(false);
        voiceDraftRef.current = "";

        const pausedForAva =
          offlinePauseForAvaRef.current ||
          isAvaSpeakingRef.current;

        if (pausedForAva) {
          sttChunksRef.current = [];
          resetOfflineRecorderCycle();
          setInput("");

          console.log(
            "[OfflineSTT] rollover blocked during Ava playback"
          );

          return;
        }

        const stoppedByUser =
          offlineUserStopRef.current;

        offlineUserStopRef.current = false;

        const finishOfflineCycle = () => {
          if (stoppedByUser) {
            cleanupOfflineStt();
          } else {
            resetOfflineRecorderCycle();
          }
        };

        try {
          const parts = sttChunksRef.current;
          sttChunksRef.current = [];

          if (!parts.length) {
            finishOfflineCycle();
            setInput("");

      if (speechEndTimerRef.current) {
        window.clearTimeout(speechEndTimerRef.current);
        speechEndTimerRef.current = null;
      }
            if (micArmedRef.current && !stoppedByUser) {
              window.setTimeout(() => {
                if (
                !micArmedRef.current ||
                offlinePauseForAvaRef.current ||
                isAvaSpeakingRef.current
              ) return;
              }, 250);
            }
            return;
          }

          const blob = new Blob(parts, {
              type:
                recorder.mimeType ||
                mime ||
                "audio/webm",
            });
          const elapsed = Date.now() - (sttStartedAtRef.current || Date.now());
          const head = await blobHeadHex(blob, 24);

          console.log(
            `[OfflineSTT] blob size=${blob.size} type=${blob.type || "(none)"} elapsed=${elapsed}ms head=${head || "(n/a)"} userStop=${stoppedByUser ? "yes" : "no"}`
          );

          if (elapsed < OFFLINE_MIN_RECORD_MS || blob.size < OFFLINE_MIN_BYTES) {
            finishOfflineCycle();

            if (!stoppedByUser && !sttRetryOnceRef.current) {
              sttRetryOnceRef.current = true;
              setInput("");

      if (speechEndTimerRef.current) {
        window.clearTimeout(speechEndTimerRef.current);
        speechEndTimerRef.current = null;
      }
              window.setTimeout(() => {
                if (
                !micArmedRef.current ||
                offlinePauseForAvaRef.current ||
                isAvaSpeakingRef.current
              ) return;
              }, 180);
              return;
            }

            if (!stoppedByUser) {
              setInput("");

      if (speechEndTimerRef.current) {
        window.clearTimeout(speechEndTimerRef.current);
        speechEndTimerRef.current = null;
      }
              if (micArmedRef.current) {
                window.setTimeout(() => {
                  if (
                !micArmedRef.current ||
                offlinePauseForAvaRef.current ||
                isAvaSpeakingRef.current
              ) return;

                  try {
                    if ((sttModeRef.current as any) === "offline") {
                      void startOfflineListening();
                    } else if (recognitionRef.current) {
                      if (webspeechLockedOfflineRef.current || (sttModeRef.current as any) === "offline") {
            console.log("[Mic] skip WebSpeech start — offline STT locked");
            return;
          }
          recognitionRef.current.start();
                    }
                  } catch (e) {
                    console.warn("[Mic] restart recovery failed", e);
                  }
                }, 250);
              }
            }

            return;
          }

          finishOfflineCycle();
          setInput("…");

          if (isAvaSpeakingRef.current || Date.now() - lastAvaSpeechEndRef.current < 3200) {
            console.log("[OfflineSTT] discarded speaker/chirp echo during Ava speech cooldown");
            finishOfflineCycle();
            setInput("");

            if (micArmedRef.current && !stoppedByUser) {
              window.setTimeout(() => {
                if (
                !micArmedRef.current ||
                offlinePauseForAvaRef.current ||
                isAvaSpeakingRef.current
              ) return;
                try {
                  void startOfflineListening();
                } catch (e) {
                  console.warn("[OfflineSTT] restart after echo discard failed", e);
                }
              }, 450);
            }
            return;
          }

            const sttResult = await sendOfflineSttBlob(blob);
            const finalText = sttResult.text.trim();

            if (
              finalText &&
              handleLocalAvaMicControl(
                finalText
              )
            ) {
              finishOfflineCycle();
              setInput("");
              return;
            }

            if (
              wakeStandbyRef.current &&
              !conversationActiveRef.current
            ) {
              finishOfflineCycle();
              setInput("");

              if (
                micArmedRef.current &&
                !stoppedByUser
              ) {
                window.setTimeout(() => {
                  if (
                    !micArmedRef.current ||
                    offlinePauseForAvaRef.current ||
                    isAvaSpeakingRef.current
                  ) {
                    return;
                  }

                  void startOfflineListening();
                }, 350);
              }

              return;
            }

            if (finalText) {
            markMicActivity();
            addUserMessage(finalText);
            setInput("");

      if (speechEndTimerRef.current) {
        window.clearTimeout(speechEndTimerRef.current);
        speechEndTimerRef.current = null;
      }
            voiceDraftRef.current = "";
            
// === STOP AUDIO BEFORE NEW MESSAGE ===
try {
  window.speechSynthesis?.cancel?.();
} catch {}
try {
  if (audioRef.current) {
    audioRef.current.pause();
      if (pauseStartedAt === null) {
        setPauseStartedAt(Date.now());
      }
    audioRef.current.currentTime = 0;
  }
} catch {}

await sendMessage(finalText, {
  alreadyAddedUser: true,
  inputSource: "voice",
  detectedLanguage: sttResult.detectedLanguage,
});
          } else {
            setInput("");

      if (speechEndTimerRef.current) {
        window.clearTimeout(speechEndTimerRef.current);
        speechEndTimerRef.current = null;
      }
          }

          if (micArmedRef.current && !stoppedByUser) {
            window.setTimeout(() => {
              if (
                !micArmedRef.current ||
                offlinePauseForAvaRef.current ||
                isAvaSpeakingRef.current
              ) return;

              try {
                void startOfflineListening();
              } catch (e) {
                console.warn("[OfflineSTT] restart failed", e);
              }
            }, 250);
          }
        } catch (error: any) {
          if (
            error?.name === "AbortError" ||
            String(error).includes("signal is aborted")
          ) {
            console.log("[OfflineSTT] graceful abort during speech transition");
            return;
          }

          console.error("[OfflineSTT] upload/transcribe error:", error);
          setInput("");

      if (speechEndTimerRef.current) {
        window.clearTimeout(speechEndTimerRef.current);
        speechEndTimerRef.current = null;
      }

          if (micArmedRef.current && !stoppedByUser) {
            window.setTimeout(() => {
              if (
                !micArmedRef.current ||
                offlinePauseForAvaRef.current ||
                isAvaSpeakingRef.current
              ) return;

              try {
                void startOfflineListening();
              } catch (e) {
                console.warn("[OfflineSTT] retry restart failed", e);
              }
            }, 350);
          }
        }
      };

      recorder.start(250);
      offlineStartInFlightRef.current = false;

      // Offline STT sends audio only after MediaRecorder stops.
      // Auto-stop chunks so Ava can respond while mic remains on.
      if (offlineAutoStopTimerRef.current) {
        window.clearTimeout(offlineAutoStopTimerRef.current);
      }
      offlineAutoStopTimerRef.current = window.setTimeout(() => {
        offlineAutoStopTimerRef.current = null;
        try {
          if (
            micArmedRef.current &&
            (sttModeRef.current as any) === "offline" &&
            sttRecorderRef.current &&
            sttRecorderRef.current.state !== "inactive"
          ) {
            sttRecorderRef.current.stop();
          }
        } catch (e) {
          console.warn("[OfflineSTT] auto chunk stop failed", e);
        }
      }, 2600);
    } catch (error: any) {
      offlineStartInFlightRef.current = false;

      console.warn("[OfflineSTT] start failed:", error?.name || error, error?.message || "");
      setIsListening(false);
      voiceDraftRef.current = "";

      cleanupOfflineStt();

      pushMessage({
        id: uid(),
        sender: "ava",
        text: "Offline Mic couldn't start. Try again.",
        source: "error",
        ts: Date.now(),
      });

      disableMic("offline start failed");
    }
  }, [
    addUserMessage,
    blobHeadHex,
    cleanupOfflineStt,
    resetOfflineRecorderCycle,
    disableMic,
    ensureMicPermission,
    markInteractionAndUnlock,
    markMicActivity,
    pushMessage,
    sendOfflineSttBlob,
    sendMessage,
    startOfflineWave,
    stopOfflineWave,
  ]);

  const startOfflineListeningRef =
    useRef(startOfflineListening);

  startOfflineListeningRef.current =
    startOfflineListening;

  const interruptAvaPlaybackForMic = () => {
    console.log("[Mic] user interrupted Ava playback");

    try {
      ttsAbortRef.current?.abort();
      ttsAbortRef.current = null;
    } catch {}

    clearAudioResources();
    stopBrowserSpeech();

    avaSpeechActiveRef.current = false;
    isAvaSpeakingRef.current = false;
    setIsSpeaking(false);

    window.dispatchEvent(
      new CustomEvent("ava-speaking", {
        detail: { speaking: false },
      })
    );

    if (micArmedRef.current) {
      resumeMicCaptureAfterAvaSpeech(0);
    }
  };

  const playMicClick = useCallback((mode: "on" | "off") => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;

      let ctx = micChimeCtxRef.current;

      if (!ctx || ctx.state === "closed") {
        ctx = new AudioCtx();
        micChimeCtxRef.current = ctx;
      }

      if (
        ctx.state === "suspended" ||
        String(ctx.state) === "interrupted"
      ) {
        void ctx.resume().catch((error) => {
          console.warn(
            "[Mic Chime] context resume failed",
            error
          );
        });
      }

      const now = ctx.currentTime;

      const makeTone = (
        freqStart: number,
        freqEnd: number,
        start: number,
        length: number,
        peak: number
      ) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = "triangle";
        osc.frequency.setValueAtTime(freqStart, now + start);
        osc.frequency.exponentialRampToValueAtTime(freqEnd, now + start + length);

        gain.gain.setValueAtTime(0.0001, now + start);
        gain.gain.exponentialRampToValueAtTime(peak, now + start + 0.012);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + start + length);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now + start);
        osc.stop(now + start + length + 0.01);
      };

      if (mode === "on") {
        // Bright two-step sci-fi activation chirp.
        makeTone(720, 1040, 0, 0.075, 0.055);
        makeTone(1180, 1540, 0.045, 0.085, 0.032);
      } else {
        // Softer descending shutdown click.
        makeTone(640, 360, 0, 0.095, 0.045);
        makeTone(420, 240, 0.05, 0.08, 0.024);
      }

      window.setTimeout(() => {
        try {
          ctx.close();
        } catch {}
      }, 220);
    } catch {}
  }, []);

  // AVAAIO_CONNECTION_MODE_RUNTIME_ENFORCEMENT_START
  useEffect(() => {
    connectionModeRef.current =
      connectionMode;

    const mustBlockCloudAudio =
      !connectionMode.confirmed ||
      connectionMode
        .cloudServicesBlocked ||
      connectionMode.requestedMode ===
        "local" ||
      connectionMode.effectiveMode ===
        "local" ||
      connectionMode.effectiveMode ===
        "disconnected";

    if (!mustBlockCloudAudio) {
      return;
    }

    try {
      ttsAbortRef.current?.abort();
      ttsAbortRef.current = null;
    } catch {}

    clearAudioResources();

    disableMic(
      "connection-mode runtime enforcement"
    );
  }, [
    connectionMode,
    clearAudioResources,
    disableMic,
  ]);
  // AVAAIO_CONNECTION_MODE_RUNTIME_ENFORCEMENT_END

  const handleMicToggle = useCallback(async () => {
    if (micToggleLockRef.current) return;
    micToggleLockRef.current = true;

    try {
      const hadActiveMicSession =
        micArmedRef.current || isMicOn;

      if (
        isAvaSpeakingRef.current ||
        avaSpeechActiveRef.current
      ) {
        interruptAvaPlaybackForMic();

        if (hadActiveMicSession) {
          return;
        }
      }

      if (chatPlan === "free" && micLocked) {
        pushMessage({
          id: uid(),
          sender: "ava",
          text: "Mic is locked for free users until your daily Ava limit resets or you upgrade.",
          ts: Date.now(),
        });
        return;
      }

      markInteractionAndUnlock();
      markMicActivity();
      void primeAudioOnce();

      if (micArmedRef.current || isMicOn) {
        const sinceStart = Date.now() - micStartedAtRef.current;
        if (sinceStart < 450) {
          console.log(`[Mic] ignore rapid stop toggle (${sinceStart}ms since start)`);
          return;
        }
          setWakeWordPreference(false);
          playMicClick("off");
          stopListening();
        return;
      }

      // AVAAIO_CONNECTION_MODE_MIC_GUARD_START
      if (isConnectionVoiceInputBlocked()) {
        pushMessage({
          id: uid(),
          sender: "ava",
          text:
            connectionModeCopyRef.current
              .localVoiceInputUnavailable,
          source: "error",
          ts: Date.now(),
        });

        disableMic(
          "connection mode blocks voice input"
        );

        return;
      }
      // AVAAIO_CONNECTION_MODE_MIC_GUARD_END

      offlineUserStopRef.current = false;
      webspeechUserStopRef.current = false;
      micArmedRef.current = true;

      setWakeWordPreference(true);

      wakeStandbyRef.current = false;
      conversationActiveRef.current = true;
      wakeActivationInFlightRef.current = false;
      postSpeechCooldownUntilRef.current = 0;

      setMicConversationMode(
        "conversation_active"
      );

      setIsMicOn(true);
      playMicClick("on");
      finalTranscriptBufferRef.current = "";
      voiceDraftRef.current = "";
        // Start every manual microphone session in live recognition.
        // Offline Whisper remains the fallback when WebSpeech is
        // unavailable or reports a real network failure.
        webspeechLockedOfflineRef.current = false;
        webspeechNetworkFailCountRef.current = 0;

        if (recognitionRef.current) {
          cleanupOfflineStt();

          sttModeRef.current =
            "webspeech";

          setSttModeLabel(
            "webspeech"
          );

          micStartedAtRef.current =
            Date.now();

          try {
            recognitionRef.current.start();

            console.log(
              "[Mic] Starting WebSpeech as primary"
            );

            return;
          } catch (error: any) {
            if (
              error?.name ===
              "InvalidStateError"
            ) {
              setIsListening(true);

              console.log(
                "[Mic] WebSpeech already active"
              );

              return;
            }

            console.warn(
              "[Mic] WebSpeech start failed; switching to offline fallback:",
              error?.name || error,
              error?.message || ""
            );
          }
        } else {
          console.warn(
            "[Mic] WebSpeech unavailable; switching to offline fallback"
          );
        }

        const permissionOk =
          await ensureMicPermission();

        if (!permissionOk) {
          pushMessage({
            id: uid(),
            sender: "ava",
            text: "I can’t access your microphone. Allow Mic permission in the browser, then press Mic again.",
            source: "error",
            ts: Date.now(),
          });

          disableMic(
            "permission blocked"
          );

          return;
        }

        webspeechLockedOfflineRef.current =
          true;

        sttModeRef.current =
          "offline";

        setSttModeLabel(
          "offline"
        );

        console.log(
          "[Mic] Starting offline STT fallback"
        );

        await startOfflineListening();
    } finally {
      resetChatRuntimeState();
      window.setTimeout(() => {
        micToggleLockRef.current = false;
      }, 650);
    }
  }, [
    chatPlan,
      cleanupOfflineStt,
      interruptAvaPlaybackForMic,
    micLocked,
    isAdminUser,
    limitReached,
    chatLock,
    markInteractionAndUnlock,
    markMicActivity,
    primeAudioOnce,
    playMicClick,
    isMicOn,
    ensureMicPermission,
    disableMic,
    pushMessage,
    stopListening,
    startOfflineListening,
  ]);

  const handleLogout = useCallback(async () => {
      markInteractionAndUnlock();
    stopAvaNow();

    try {
      await fetch(joinUrl(pickApiBase(), "/api/v1/logout"), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        },
      });
    } catch {
    } finally {
      resetChatRuntimeState();
      setShowSettings(false);
      setIsLoggingOutVisual(true);
      stopAvaNow();

      // Prevent dashboard reflow/jitter during logout.
      window.setTimeout(() => {
        hardLogout();
      }, 180);
    }
  }, [markInteractionAndUnlock, stopAvaNow]);

  const go = useCallback(
    (path: string) => {
      markInteractionAndUnlock();
      setShowSettings(false);
      navigate(path);
    },
    [markInteractionAndUnlock, navigate]
  );


  useEffect(() => {
    if (!isAudioPlaying) {
      clearReadAlong();
      return;
    }

    const latestAva =
      [...messages]
        .reverse()
        .find(
          (message) =>
            message.sender === "ava" &&
            !message.is_thinking
        );

    if (latestAva?.id) {
      setReadAlongMessageId(
        latestAva.id
      );
    }
  }, [
    isAudioPlaying,
    messages,
    clearReadAlong,
  ]);

  // =====================================================
  // AVAAIO READ-ALONG V1.4
  // Dual-layer spoken-word auto-follow.
  //
  // Keep Ava's active spoken word in the lower-middle
  // reading band so new text progresses upward naturally.
  // Never use scrollIntoView because iOS may scroll the page.
  // =====================================================
  useEffect(() => {
    if (
      readAlongWordIndex < 0 ||
      !readAlongMessageId
    ) {
      return;
    }

    const frame =
      window.requestAnimationFrame(() => {
        const feed =
          document.querySelector(
            ".chat-feed"
          ) as HTMLElement | null;

        if (!feed) {
          return;
        }

        const messageLayer =
          feed.querySelector(
            ".ava-chat-scroll-fade"
          ) as HTMLElement | null;

        const activeWord =
          document.querySelector(
            '[data-ava-read-active="true"]'
          ) as HTMLElement | null;

        if (!activeWord) {
          return;
        }

        const moveLayer = (
          layer: HTMLElement | null,
          desiredRatio: number
        ) => {
          if (!layer) {
            return;
          }

          const layerRect =
            layer.getBoundingClientRect();

          const wordRect =
            activeWord.getBoundingClientRect();

          const currentWordCenter =
            wordRect.top +
            wordRect.height / 2;

          const desiredWordCenter =
            layerRect.top +
            layerRect.height *
              desiredRatio;

          const delta =
            currentWordCenter -
            desiredWordCenter;

          // Do not twitch the chat for tiny word-to-word
          // movement. Start moving once the active word
          // leaves the comfortable lower reading band.
          const upperBand =
            layerRect.top +
            layerRect.height * 0.52;

          const lowerBand =
            layerRect.top +
            layerRect.height * 0.78;

          const outsideReadingBand =
            currentWordCenter <
              upperBand ||
            currentWordCenter >
              lowerBand;

          if (!outsideReadingBand) {
            return;
          }

          const maxScroll =
            Math.max(
              0,
              layer.scrollHeight -
              layer.clientHeight
            );

          if (maxScroll <= 1) {
            return;
          }

          const nextTop =
            Math.max(
              0,
              Math.min(
                maxScroll,
                layer.scrollTop +
                  delta
              )
            );

          layer.scrollTo({
            top: nextTop,
            behavior:
              isPhone ||
              isPhoneLandscape ||
              keyboardOpen
                ? "auto"
                : "smooth",
          });
        };

        // AVAAIO already uses both scrolling surfaces.
        // Synchronize both exactly like the existing
        // latest-message pinning system does.
        moveLayer(
          messageLayer,
          0.68
        );

        moveLayer(
          feed,
          0.68
        );
      });

    return () => {
      window.cancelAnimationFrame(
        frame
      );
    };
  }, [
    readAlongWordIndex,
    readAlongMessageId,
    isPhone,
    isPhoneLandscape,
    keyboardOpen,
  ]);

  const toggleReadingPause = useCallback(() => {
    const now = Date.now();

    setReadingPausedAt((prev) => {
      if (prev === null) {
        try {
          audioRef.current?.pause?.();
        } catch {}
        return now;
      }

      setReadingPauseOffsetMs((ms) => ms + Math.max(0, now - prev));
      try {
        void audioRef.current?.play?.();
      } catch {}
      return null;
    });
  }, []);

  // ===== AVAAIO TRUE SCROLLBAR IDLE FADE =====
  useEffect(() => {
    resetChatScrollbarFade(isAudioPlaying ? 12000 : 5500);
  }, [messages.length, isAudioPlaying, resetChatScrollbarFade]);

  const fadeClass = useCallback(
    (ts: number, speechEndedAt?: number | null) => {
      // While reading is paused, keep Ava responses fully visible.
      // When unpaused, fade continues from where it left off.
      if (readingPausedAt !== null) return "";

      const effectiveNow = nowTick - readingPauseOffsetMs;

      // Start fading AFTER Ava finishes speaking.
      const fadeStart = speechEndedAt && speechEndedAt > ts
        ? speechEndedAt
        : ts;

      const age = effectiveNow - fadeStart;

      
  // =========================================
  // AVAAIO REAL SPEECH-SYNC FADE SYSTEM
  // =========================================

  // Never fade while audio is active
  if (isAudioPlaying) {
    return "";
  }

  
      // =========================================
      // AVAAIO REAL SPEECH-SYNC FADE SYSTEM
      // =========================================

      // Never fade while Ava is speaking
      if (isAudioPlaying) {
        return "";
      }

      // Give users reading time AFTER
      // speech has finished
      const SAFE_READ_DELAY = CHAT_SAFE_READ_DELAY_MS; // AVAAIO: readable pause after speech

      if (age < SAFE_READ_DELAY) {
        return "";
      }

      const adjustedAge =
        age - SAFE_READ_DELAY;

if (adjustedAge > FADE_4)
    return "msg-fade-4";

  if (adjustedAge > FADE_3)
    return "msg-fade-3";

  if (adjustedAge > FADE_2)
    return "msg-fade-2";

  if (adjustedAge > FADE_1)
    return "msg-fade-1";

  return "";

    },
    [nowTick, readingPausedAt, readingPauseOffsetMs]
  );

  // ===== AVAAIO FEED SCROLLBAR FOLLOWS CHAT FADE =====
  const chatFeedFadeClass = (() => {
    try {
      if (readingPausedAt !== null || isAudioPlaying || isTyping || isSending || isAvaSpeakingRef.current) {
        return "avaaio-feed-active";
      }

      const latestAvaMessage = [...messages]
        .reverse()
        .find((m) => m.sender !== "user");

      if (!latestAvaMessage) return "avaaio-feed-active";

      const speechEndedAt = lastAvaSpeechEndRef.current || 0;
      const fadeStart =
        speechEndedAt && speechEndedAt > latestAvaMessage.ts
          ? speechEndedAt
          : latestAvaMessage.ts;

      const age = Date.now() - fadeStart - readingPauseOffsetMs;

      if (age > FADE_4) return "avaaio-feed-fade-4";
      if (age > FADE_3) return "avaaio-feed-fade-3";
      if (age > FADE_2) return "avaaio-feed-fade-2";
      if (age > FADE_1) return "avaaio-feed-fade-1";

      return "avaaio-feed-active";
    } catch {
      return "avaaio-feed-active";
    }
  })();

  /*
   * AVAAIO_CHAT_SPACE_COORDINATION_V1
   *
   * Portrait: Ava lifts while a bubble remains visible.
   * Landscape: Ava moves opposite the bubble.
   * Final fade: Ava smoothly returns to center.
   */
  const latestChatMessage =
    messages.length > 0
      ? messages[messages.length - 1]
      : null;

  const latestChatMessageTs =
    latestChatMessage?.ts || 0;

  const chatVisualActive = (() => {
    try {
      if (!latestChatMessage) return false;

      if (
        readingPausedAt !== null ||
        isAudioPlaying ||
        isTyping ||
        isSending ||
        isAvaSpeakingRef.current
      ) {
        return true;
      }

      const speechEndedAt =
        lastAvaSpeechEndRef.current || 0;

      const fadeStart =
        latestChatMessage.sender !== "user" &&
        speechEndedAt > latestChatMessage.ts
          ? speechEndedAt
          : latestChatMessage.ts;

      const effectiveAge =
        nowTick -
        readingPauseOffsetMs -
        fadeStart;

      return (
        effectiveAge <=
        CHAT_SAFE_READ_DELAY_MS + FADE_4
      );
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    let animationFrame = 0;

    const syncAvaChatSpace = () => {
      try {
        const body = document.body;

        if (!chatVisualActive) {
          body.dataset.avaaioChatSpace = "idle";
          return;
        }

        const visualViewport =
          window.visualViewport;

        const viewportLeft =
          visualViewport?.offsetLeft || 0;

        const viewportWidth =
          visualViewport?.width ||
          window.innerWidth;

        const viewportHeight =
          visualViewport?.height ||
          window.innerHeight;

        if (viewportWidth <= viewportHeight) {
          body.dataset.avaaioChatSpace =
            "portrait-up";
          return;
        }

        const bubbles =
          Array.from(
            document.querySelectorAll<HTMLElement>(
              ".chat-bubble"
            )
          );

        const activeBubble =
          [...bubbles]
            .reverse()
            .find((element) => {
              const bounds =
                element.getBoundingClientRect();

              const style =
                window.getComputedStyle(element);

              return (
                bounds.width > 0 &&
                bounds.height > 0 &&
                style.display !== "none" &&
                style.visibility !== "hidden" &&
                Number(style.opacity || "1") > 0.01
              );
            });

        const fallback =
          document.querySelector<HTMLElement>(
            ".chatbox-container"
          );

        const bounds =
          (activeBubble || fallback)
            ?.getBoundingClientRect();

        const viewportCenter =
          viewportLeft + viewportWidth / 2;

        const bubbleCenter =
          bounds
            ? bounds.left + bounds.width / 2
            : viewportLeft;

        body.dataset.avaaioChatSpace =
          bubbleCenter <= viewportCenter
            ? "landscape-right"
            : "landscape-left";
      } catch (error) {
        console.warn(
          "[AVAAIO CHAT SPACE] synchronization failed",
          error
        );
      }
    };

    const scheduleSync = () => {
      if (animationFrame) {
        window.cancelAnimationFrame(
          animationFrame
        );
      }

      animationFrame =
        window.requestAnimationFrame(
          syncAvaChatSpace
        );
    };

    scheduleSync();

    window.addEventListener(
      "resize",
      scheduleSync
    );

    window.addEventListener(
      "orientationchange",
      scheduleSync
    );

    window.addEventListener(
      "pointermove",
      scheduleSync,
      { passive: true }
    );

    window.visualViewport?.addEventListener(
      "resize",
      scheduleSync
    );

    window.visualViewport?.addEventListener(
      "scroll",
      scheduleSync
    );

    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(
          animationFrame
        );
      }

      window.removeEventListener(
        "resize",
        scheduleSync
      );

      window.removeEventListener(
        "orientationchange",
        scheduleSync
      );

      window.removeEventListener(
        "pointermove",
        scheduleSync
      );

      window.visualViewport?.removeEventListener(
        "resize",
        scheduleSync
      );

      window.visualViewport?.removeEventListener(
        "scroll",
        scheduleSync
      );
    };
  }, [
    chatVisualActive,
    latestChatMessageTs,
  ]);

  useEffect(() => {
    return () => {
      try {
        delete document.body.dataset
          .avaaioChatSpace;
      } catch {
        // Nothing else is required.
      }
    };
  }, []);


  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    try {
      setVoiceChoice((localStorage.getItem(LS_VOICE) || "").trim());
      setFormatChoice((localStorage.getItem(LS_FORMAT) || "").trim());
      setRegionChoice((localStorage.getItem(LS_REGION) || "").trim() || "uksouth");
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LS_VOICE, voiceChoice || "");
      localStorage.setItem(LS_FORMAT, formatChoice || "");
      localStorage.setItem(LS_REGION, regionChoice || "");
    } catch {}
  }, [voiceChoice, formatChoice, regionChoice]);

  useEffect(() => {
    let interval: number | null = null;

    const update = () => {
      const now = new Date();

      setClock(
        now.toLocaleTimeString(interfaceCopy.chat.locale, {
          hour: "2-digit",
          minute: "2-digit",
        })
      );

      setDateStr(
        now.toLocaleDateString(interfaceCopy.chat.locale, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      );
    };

    update();

    // Sync to the exact next minute boundary.
    const syncDelay =
      60000 -
      (
        (new Date().getSeconds() * 1000) +
        new Date().getMilliseconds()
      );

    const timeout = window.setTimeout(() => {
      update();

      interval = window.setInterval(update, 60_000);
    }, syncDelay);

    return () => {
      window.clearTimeout(timeout);

      if (interval) {
        window.clearInterval(interval);
      }
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  // AVAAIO MOBILE UX PHASE 2:
  // Keep the latest user question and Ava response visible inside the
  // real feed. Avoid scrollIntoView because iOS can scroll the page.
  useEffect(() => {
    if (!messages?.length) return;

    let firstRaf = 0;
    let secondRaf = 0;
    const settleTimers: number[] = [];

    const pinConversationToLatest = () => {
      const feed =
        document.querySelector(
          ".chat-feed"
        ) as HTMLElement | null;

      if (!feed) return;

      // AVAAIO MOBILE UX PHASE 2D:
      // Phase 2B intentionally uses different active scrolling layers:
      // portrait/desktop -> .ava-chat-scroll-fade
      // iPhone landscape -> .chat-feed
      //
      // Synchronize both layers so the newest messages remain visible
      // when Safari changes orientation or keyboard viewport geometry.
      const messageLayer =
        feed.querySelector(
          ".ava-chat-scroll-fade"
        ) as HTMLElement | null;

      const behavior: ScrollBehavior =
        isPhone ||
        isPhoneLandscape ||
        keyboardOpen
          ? "auto"
          : "smooth";

      if (messageLayer) {
        messageLayer.scrollTo({
          top: messageLayer.scrollHeight,
          behavior,
        });
      }

      feed.scrollTo({
        top: feed.scrollHeight,
        behavior,
      });
    };

    firstRaf = requestAnimationFrame(() => {
      pinConversationToLatest();

      secondRaf = requestAnimationFrame(
        pinConversationToLatest
      );
    });

    if (keyboardOpen || isPhone || isPhoneLandscape) {
      [80, 180, 320, 520, 800].forEach(delay => {
        settleTimers.push(
          window.setTimeout(
            pinConversationToLatest,
            delay
          )
        );
      });
    }

    return () => {
      cancelAnimationFrame(firstRaf);
      cancelAnimationFrame(secondRaf);

      settleTimers.forEach(timer => {
        window.clearTimeout(timer);
      });
    };
  }, [
    messages.length,
    keyboardOpen,
    visualViewportHeight,
    viewportWidth,
    viewportHeight,
    isPhone,
    isPhoneLandscape,
  ]);

  useEffect(() => {
    if (consoleWrappedRef.current) return;
    consoleWrappedRef.current = true;

    const origLog = console.log.bind(console);
    const origWarn = console.warn.bind(console);
    const origError = console.error.bind(console);

    console.log = (...args: any[]) => {
      pushConsoleLine("LOG", args);
      origLog(...args);
    };

    console.warn = (...args: any[]) => {
      pushConsoleLine("WARN", args);
      origWarn(...args);
    };

    console.error = (...args: any[]) => {
      pushConsoleLine("ERROR", args);
      origError(...args);
    };

    pushConsoleLine("LOG", ["[ChatBox] console capture active"]);
  }, [pushConsoleLine]);

  useEffect(() => {
    if (!showSettings) return;

    const onDown = (event: MouseEvent) => {
      const element = settingsRef.current;
      if (!element) return;
      if (element.contains(event.target as Node)) return;
      setShowSettings(false);
      markInteractionAndUnlock();
    };

    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [markInteractionAndUnlock, showSettings]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const idle = Date.now() - lastInteractionRef.current;
      const chatBusy =
        isTyping ||
        isSending ||
        isListening ||
        isSpeaking ||
        isAudioPlaying ||
        sendingLockRef.current ||
        Boolean(activeReqIdRef.current);

      if (!showSettings && !chatBusy && idle > DOCK_IDLE_MS) {
        setDockVisible(false);
      }
    }, 250);

    return () => window.clearInterval(timer);
  }, [
    isAudioPlaying,
    isListening,
    isSending,
    isSpeaking,
    isTyping,
    showSettings,
  ]);

  useEffect(() => {
    const micIdleTimer = window.setInterval(() => {
      if (!micArmedRef.current) return;
      if (!micIdleAtRef.current) return;

      const idleFor = Date.now() - micIdleAtRef.current;
      if (idleFor >= MIC_IDLE_AUTO_OFF_MS) {
          // AVAAIO: quietly stop idle mic to avoid repeated notification spam.
          if (
            wakeStandbyRef.current ||
            conversationActiveRef.current
          ) {
            markMicActivity();

            console.log(
              "[WakeWord] idle shutdown skipped while persistent mode is active"
            );

            return;
          }

          disableMic("idle timeout");
        }
    }, 2000);

    return () => window.clearInterval(micIdleTimer);
  }, [disableMic, pushMessage]);

  useEffect(() => {
    const wakeMouseMove = () => markInteraction();
    const wakeAndUnlock = () => markInteractionAndUnlock();
      markInteractionAndUnlock();

    window.addEventListener("mousemove", wakeMouseMove);
    window.addEventListener("mousedown", wakeAndUnlock);
    window.addEventListener("keydown", wakeAndUnlock);
    window.addEventListener("touchstart", wakeAndUnlock, { passive: true });

    return () => {
      window.removeEventListener("mousemove", wakeMouseMove);
      window.removeEventListener("mousedown", wakeAndUnlock);
      window.removeEventListener("keydown", wakeAndUnlock);
      window.removeEventListener("touchstart", wakeAndUnlock);
    };
  }, [markInteraction, markInteractionAndUnlock]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      console.warn("[Mic] SpeechRecognition not supported in this browser.");
      return;
    }

    const rec = new SR();
    recognitionRef.current = rec;

    rec.continuous = true;

    // === MULTILINGUAL MIC LOCALE SUPPORT ===
    // Web Speech listens in the current accepted conversation language.
    // Whisper remains the automatic spoken-language detection path.
    const browserLang = String(
      localStorage.getItem("ava_conversation_language") ||
      localStorage.getItem("ava_mic_lang") ||
      navigator.language ||
      "en-US"
    ).toLowerCase();

    const baseLanguage = browserLang.split("-", 1)[0];

    const speechLocales: Record<string, string> = {
      en: "en-US",
      es: "es-US",
      fr: "fr-FR",
      de: "de-DE",
      it: "it-IT",
      pt: "pt-BR",
      ja: "ja-JP",
      zh: "zh-CN",
      ko: "ko-KR",
      ar: "ar-SA",
      he: "he-IL",
      ru: "ru-RU",
      hi: "hi-IN",
      nl: "nl-NL",
      pl: "pl-PL",
      tr: "tr-TR",
      el: "el-GR",
    };

    const micLang =
      speechLocales[baseLanguage] ||
      (browserLang.includes("-")
        ? browserLang
        : "en-US");

    rec.lang = micLang;

    console.log("[Mic] SpeechRecognition language:", micLang);

    rec.interimResults = true;

    rec.onstart = () => {
      webspeechUserStopRef.current = false;
      restartingWebSpeechRef.current = false;
      sttModeRef.current = "webspeech";
      setSttModeLabel("webspeech");
      setIsListening(true);
      setIsMicOn(true);

      setMicConversationMode(
        conversationActiveRef.current
          ? "conversation_active"
          : wakeStandbyRef.current
            ? "wake_standby"
            : "conversation_active"
      );

      markMicActivity();

      markInteractionAndUnlock();
    };

    rec.onresult = (event: any) => {
      let interim = "";
      let finalText = "";

      try {
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const result = event.results[i];
          const text = (result?.[0]?.transcript || "").toString();
          if (!text) continue;
          if (result.isFinal) finalText += text;
          else interim += text;
        }
      } catch {}

        const finalClean = finalText.trim();
        const interimClean = interim.trim();

        const blockedByAvaSpeech =
          isAvaSpeakingRef.current ||
          avaSpeechActiveRef.current ||
          offlinePauseForAvaRef.current ||
          Date.now() <
            postSpeechCooldownUntilRef.current ||
          Date.now() -
            lastAvaSpeechEndRef.current <
            avaSelfHearingCooldownMsRef.current;

        if (blockedByAvaSpeech) {
          clearFinalSendTimer();
          finalTranscriptBufferRef.current = "";
          voiceDraftRef.current = "";
          setInput("");

          console.log(
            "[WakeWord] discarded recognition during Ava speech/cooldown"
          );

          return;
        }

        const localControlText = [
          finalClean,
          interimClean,
        ]
          .filter(Boolean)
          .join(" ")
          .trim();

        if (
          localControlText &&
          handleLocalAvaMicControl(
            localControlText
          )
        ) {
          clearFinalSendTimer();
          finalTranscriptBufferRef.current = "";
          voiceDraftRef.current = "";
          setInput("");
          return;
        }

        if (
          wakeStandbyRef.current &&
          !conversationActiveRef.current
        ) {
          clearFinalSendTimer();
          finalTranscriptBufferRef.current = "";
          voiceDraftRef.current = "";
          setInput("");
          return;
        }

        console.log("[MicDebug] result", {
        finalClean,
        interimClean,
        resultIndex: event?.resultIndex,
        resultsLength: event?.results?.length,
      });
      if (
        finalClean ||
        interimClean
      ) {
        markMicActivity();

        // Continuing speech cancels any earlier send decision.
        clearFinalSendTimer();
      }

      if (finalClean) {
        finalTranscriptBufferRef.current =
          mergeVoiceTranscript(
            finalTranscriptBufferRef.current,
            finalClean
          );
      }

      const live =
        mergeVoiceTranscript(
          finalTranscriptBufferRef.current,
          interimClean
        );


      if (live) {
        voiceDraftRef.current = live;
        setInput(live);

      // reset silence timer
      if (speechEndTimerRef.current) {
        window.clearTimeout(speechEndTimerRef.current);
      }

      speechEndTimerRef.current = window.setTimeout(() => {
        if (recognitionRef.current && micArmedRef.current) {
          const buffered = (finalTranscriptBufferRef.current || "").trim();
          const draft = (voiceDraftRef.current || "").trim();
          // Include the final interim continuation even when earlier
          // words were already marked final.
          const completed =
            mergeVoiceTranscript(
              buffered,
              draft
            );

          if (completed) {
            console.log(
              "[Mic] finalizing complete draft on silence:",
              completed
            );

            finalTranscriptBufferRef.current =
              completed;
          }

          console.log(
            "[Mic] auto send/pause after silence"
          );


// Keep the microphone armed while Ava processes and speaks.
void flushFinalTranscript();
pauseMicCaptureForAvaSpeech();
        }
      }, MIC_SILENCE_SEND_MS);
      markInteractionAndUnlock();
      }
    };

    rec.onerror = (event: any) => {
      const err = (event?.error || "").toString();
      console.warn("[MicDebug] error", err, event);

      if (webspeechUserStopRef.current && (err === "aborted" || err === "no-speech" || !err)) {
        setIsListening(false);
        voiceDraftRef.current = "";

      if (speechEndTimerRef.current) {
        window.clearTimeout(speechEndTimerRef.current);
        speechEndTimerRef.current = null;
      }
        return;
      }

      if (!micArmedRef.current) {
        setIsListening(false);
        return;
      }

      // Normal silence is not a microphone failure.
      if (err === "no-speech") {
        setIsListening(false);
        voiceDraftRef.current = "";

        if (speechEndTimerRef.current) {
          window.clearTimeout(speechEndTimerRef.current);
          speechEndTimerRef.current = null;
        }

        return;
      }

      // rec.onend performs the single handoff after
      // Web Speech releases microphone ownership.
      if (err === "network") {
        console.warn(
          "[Mic] WebSpeech network error — handing off to offline STT"
        );

        setIsListening(false);
        voiceDraftRef.current = "";
        webspeechLockedOfflineRef.current = true;

        if (speechEndTimerRef.current) {
          window.clearTimeout(speechEndTimerRef.current);
          speechEndTimerRef.current = null;
        }

        return;
      }
      if (err === "not-allowed" || err === "service-not-allowed") {
        pushMessage({
          id: uid(),
          sender: "ava",
          text: "Mic permission is blocked. Click the browser lock icon and allow Microphone, then try again.",
          source: "error",
          ts: Date.now(),
        });
        disableMic("permission blocked");
        return;
      }

      if (err === "audio-capture") {
        pushMessage({
          id: uid(),
          sender: "ava",
          text: "No microphone was detected. Check your device microphone and browser permissions, then try again.",
          source: "error",
          ts: Date.now(),
        });
        disableMic("audio capture failed");
        return;
      }

      const shouldFallbackToOffline =
        !navigator.onLine || err === "bad-grammar";

      setIsListening(false);
      voiceDraftRef.current = "";

      if (shouldFallbackToOffline) {
        if (err === "network") {
          webspeechNetworkFailCountRef.current += 1;
          if (webspeechNetworkFailCountRef.current >= WEBSPEECH_NETWORK_FAIL_LOCK_THRESHOLD) {
            webspeechLockedOfflineRef.current = true;
          }
        }

        sttModeRef.current = "offline";
        setSttModeLabel("offline");
        if (micArmedRef.current) {
          window.setTimeout(() => {
            if (!micArmedRef.current) return;
            try {
              void startOfflineListeningRef.current();
            } catch (e) {
              console.warn("[Mic] offline restart failed", e);
            }
          }, 250);
        }
        return;
      }

    };

    rec.onend = () => {
      if (sttModeRef.current !== "webspeech") {
        restartingWebSpeechRef.current = false;
        return;
      }

      if (
        webspeechUserStopRef.current ||
        !micArmedRef.current
      ) {
        webspeechUserStopRef.current = false;
        restartingWebSpeechRef.current = false;
        setIsListening(false);
        voiceDraftRef.current = "";

        if (speechEndTimerRef.current) {
          window.clearTimeout(speechEndTimerRef.current);
          speechEndTimerRef.current = null;
        }

        return;
      }

      if (webspeechLockedOfflineRef.current) {
        setIsListening(false);
        restartingWebSpeechRef.current = false;

        if (micArmedRef.current) {
          sttModeRef.current = "offline";
          setSttModeLabel("offline");

          window.setTimeout(() => {
            if (
              !micArmedRef.current ||
              offlinePauseForAvaRef.current ||
              isAvaSpeakingRef.current
            ) {
              return;
            }

            try {
              void startOfflineListeningRef.current();
            } catch (error) {
              console.warn(
                "[Mic] offline handoff failed",
                error
              );
            }
          }, 450);
        }

        return;
      }

      setIsListening(false);

      if (restartingWebSpeechRef.current) {
        return;
      }

      restartingWebSpeechRef.current = true;

      window.setTimeout(() => {
        if (
          !micArmedRef.current ||
          !recognitionRef.current ||
          webspeechLockedOfflineRef.current
        ) {
          restartingWebSpeechRef.current = false;
          return;
        }

        try {
          if (
            webspeechLockedOfflineRef.current ||
            (sttModeRef.current as any) === "offline"
          ) {
            restartingWebSpeechRef.current = false;
            console.log(
              "[Mic] skip WebSpeech start — offline STT locked"
            );
            return;
          }

          recognitionRef.current.start();
        } catch (error: any) {
          restartingWebSpeechRef.current = false;
          console.warn(
            "[Mic] restart failed:",
            error?.name || error,
            error?.message || ""
          );
        }
      }, 250);
    };
  }, [
    disableMic,
    markInteractionAndUnlock,
    markMicActivity,
    pushMessage,
    clearFinalSendTimer,
    stopOfflineWave,
  ]);

  // __AVAAIO_FIRST_TIME_WELCOME_MEMORY_V3__
  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;

    if (welcomeShownRef.current) return;
    welcomeShownRef.current = true;

    const parseWelcomeMemory = (
      value: unknown,
    ): Record<string, any> => {
      if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        return value as Record<string, any>;
      }

      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);

          if (
            parsed &&
            typeof parsed === "object" &&
            !Array.isArray(parsed)
          ) {
            return parsed as Record<string, any>;
          }
        } catch {}
      }

      return {};
    };

    const runWelcome = async () => {
      try {
        if (
          messages &&
          messages.length > 0
        ) {
          return;
        }

        const token = getToken();

        const response = await fetch(
          joinUrl(
            pickApiBase(),
            "/api/v1/auth/me",
          ),
          {
            credentials: "include",
            cache: "no-store",
            headers: {
              Accept: "application/json",
              ...(token
                ? {
                    Authorization:
                      `Bearer ${token}`,
                  }
                : {}),
            },
          },
        );

        if (
          !response.ok ||
          cancelled
        ) {
          welcomeShownRef.current = false;
          return;
        }

        const data = await response
          .json()
          .catch(() => null);

        const user = (
          data?.user ||
          data?.profile ||
          data?.me ||
          data
        ) as Record<string, any> | null;

        if (!user || cancelled) {
          welcomeShownRef.current = false;
          return;
        }

        const memory =
          parseWelcomeMemory(
            user.memory,
          );

        const welcomeValue =
          memory.ava_welcome_completed;

        const firstWelcomePending =
          welcomeValue === false ||
          welcomeValue === 0 ||
          String(welcomeValue)
            .trim()
            .toLowerCase() === "false" ||
          String(welcomeValue)
            .trim() === "0";

        let setupName = "";

        try {
          setupName = String(
            sessionStorage.getItem(
              "ava_first_welcome_name",
            ) || "",
          ).trim();
        } catch {}

        const cleanName = String(
          user.display_name ||
          memory.display_name ||
          user.name ||
          setupName ||
          "",
        )
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 60);

        const email = String(
          user.email || "guest",
        )
          .trim()
          .toLowerCase();

        const safeUserKey =
          email.replace(
            /[^a-z0-9._-]/gi,
            "_",
          );

        const loginCountKey =
          `ava_login_count_v2:${safeUserKey}`;

        const relationshipKey =
          `ava_relationship_established_v2:${safeUserKey}`;

        const sessionGreetingKey =
          `ava_session_greeting_seen_v2:${safeUserKey}`;

        if (
          sessionStorage.getItem(
            sessionGreetingKey,
          ) === "1"
        ) {
          return;
        }

        timer = window.setTimeout(
          () => {
            void (async () => {
              if (cancelled) return;

              sessionStorage.setItem(
                sessionGreetingKey,
                "1",
              );

              if (firstWelcomePending) {
                const introduction =
                  cleanName
                    ? `Hi, ${cleanName}. I’m Ava. Welcome to AVAAIO. I’m here to help you learn, communicate, research, translate, explore, and get everyday things done. Take a moment to familiarize yourself with the platform and discover the available tools. I’ll be right here whenever you need me.`
                    : "Hi. I’m Ava. Welcome to AVAAIO. I’m here to help you learn, communicate, research, translate, explore, and get everyday things done. Take a moment to familiarize yourself with the platform and discover the available tools. I’ll be right here whenever you need me.";

                await addAvaMessage(
                  introduction,
                  "brain",
                );

                try {
                  const markResponse =
                    await fetch(
                      joinUrl(
                        pickApiBase(),
                        "/api/v1/auth/profile",
                      ),
                      {
                        method: "PATCH",
                        credentials: "include",
                        headers: {
                          "Content-Type":
                            "application/json",
                          Accept:
                            "application/json",
                          ...(token
                            ? {
                                Authorization:
                                  `Bearer ${token}`,
                              }
                            : {}),
                        },
                        body: JSON.stringify({
                          memory: {
                            ava_welcome_completed:
                              true,
                            ava_welcome_version:
                              3,
                          },
                        }),
                      },
                    );

                  const markData =
                    await markResponse
                      .json()
                      .catch(() => null);

                  if (
                    !markResponse.ok ||
                    markData?.ok !== true
                  ) {
                    console.warn(
                      "[AvaWelcome] completion was not saved",
                      markData,
                    );
                  } else {
                    try {
                      sessionStorage.removeItem(
                        "ava_first_welcome_name",
                      );
                    } catch {}
                  }
                } catch (error) {
                  console.warn(
                    "[AvaWelcome] completion save failed",
                    error,
                  );
                }

                return;
              }

              const loginCount =
                Number(
                  localStorage.getItem(
                    loginCountKey,
                  ) || "0",
                ) + 1;

              localStorage.setItem(
                loginCountKey,
                String(loginCount),
              );

              localStorage.setItem(
                relationshipKey,
                "1",
              );

              const greetings =
                cleanName
                  ? [
                      `Hey ${cleanName}, what’s up?`,
                      `Hi ${cleanName}. What are we working on today?`,
                      `Hello ${cleanName}, I see you’re back. What’s going on?`,
                      `Hey ${cleanName}. Need help with something, or are we just talking?`,
                      `Hi ${cleanName}. Is there anything you’d like help with?`,
                    ]
                  : [
                      "Hey, what’s up?",
                      "Hi. What are we working on today?",
                      "Hello, I see you’re back. What’s going on?",
                      "Hey. Need help with something, or are we just talking?",
                      "Hi. Is there anything you’d like help with?",
                    ];

              const now = new Date();

              const greetingIndex =
                (
                  loginCount +
                  now.getDate() +
                  now.getHours()
                ) % greetings.length;

              await addAvaMessage(
                greetings[greetingIndex],
                "brain",
              );
            })();
          },
          700,
        );
      } catch (error) {
        console.warn(
          "[AvaWelcome] greeting check failed",
          error,
        );

        welcomeShownRef.current = false;
      }
    };

    void runWelcome();

    return () => {
      cancelled = true;

      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, [addAvaMessage]);

  useEffect(() => {
    const startWakeStandbyIfEnabled =
      () => {
        // AVAAIO_CONNECTION_MODE_WAKE_STANDBY_GUARD_START
        if (isConnectionVoiceInputBlocked()) {
          disableMic(
            "connection mode blocks wake standby"
          );
          return;
        }
        // AVAAIO_CONNECTION_MODE_WAKE_STANDBY_GUARD_END
        let enabled = false;

        try {
          enabled =
            window.localStorage.getItem(
              "avaaio_wake_word_enabled"
            ) === "1";
        } catch {}

        wakeWordEnabledRef.current =
          enabled;

        if (
          !enabled ||
          document.hidden ||
          micArmedRef.current ||
          !recognitionRef.current
        ) {
          return;
        }

        wakeStandbyRef.current = true;
        conversationActiveRef.current = false;
        wakeActivationInFlightRef.current = false;
        postSpeechCooldownUntilRef.current = 0;

        offlineUserStopRef.current = false;
        webspeechUserStopRef.current = false;
        webspeechLockedOfflineRef.current = false;

        micArmedRef.current = true;
        sttModeRef.current = "webspeech";

        setSttModeLabel("webspeech");
        setIsMicOn(true);
        setMicConversationMode(
          "wake_standby"
        );

        finalTranscriptBufferRef.current = "";
        voiceDraftRef.current = "";
        setInput("");

        try {
          recognitionRef.current.start();

          console.log(
            "[WakeWord] wake standby restored"
          );
        } catch (error: any) {
          if (
            error?.name ===
            "InvalidStateError"
          ) {
            setIsListening(true);

            console.log(
              "[WakeWord] recognition already active"
            );

            return;
          }

          micArmedRef.current = false;
          wakeStandbyRef.current = false;

          setIsMicOn(false);
          setIsListening(false);
          setMicConversationMode("off");

          console.warn(
            "[WakeWord] automatic standby requires an unlocked browser microphone session",
            error
          );
        }
      };

    const timer =
      window.setTimeout(
        startWakeStandbyIfEnabled,
        1100
      );

    const handleVisibility = () => {
      if (!document.hidden) {
        window.setTimeout(
          startWakeStandbyIfEnabled,
          250
        );
      }
    };

    document.addEventListener(
      "visibilitychange",
      handleVisibility
    );

    window.addEventListener(
      "pageshow",
      startWakeStandbyIfEnabled
    );

    return () => {
      window.clearTimeout(timer);

      document.removeEventListener(
        "visibilitychange",
        handleVisibility
      );

      window.removeEventListener(
        "pageshow",
        startWakeStandbyIfEnabled
      );
    };
  }, []);

  useEffect(() => {
    return () => {
      try {
        disableMic("component unmount");
        stopBrowserSpeech();

        cleanupOfflineStt();
        clearFinalSendTimer();
        chatAbortRef.current?.abort();
        chatAbortRef.current = null;
        ttsAbortRef.current?.abort();
        ttsAbortRef.current = null;
        clearAudioResources();
      } catch {}
    };
  }, [cleanupOfflineStt, clearAudioResources, clearFinalSendTimer, disableMic, stopBrowserSpeech, stopOfflineWave]);

  
// =====================================================
// AVAAIO HUD DRAG HANDLERS
// =====================================================

const beginHudDrag = (e: React.PointerEvent) => {
  if (!isPhoneLandscape) return;
  dragHudRef.current.active = true;

  dragHudRef.current.startX = e.clientX;
  dragHudRef.current.startY = e.clientY;

  dragHudRef.current.originX = hudPosition.x;
  dragHudRef.current.originY = hudPosition.y;
};


const moveHudDrag = (e: React.PointerEvent) => {
  if (!isPhoneLandscape) return;
  if (!dragHudRef.current.active) return;

  const dx =
    e.clientX - dragHudRef.current.startX;

  const dy =
    e.clientY - dragHudRef.current.startY;

  const rawX =
    dragHudRef.current.originX + dx;

  const rawY =
    dragHudRef.current.originY + dy;

  // AVAAIO iPhone landscape Ava drag no-snap zone
  if (isPhoneLandscape) {
    const next = {
      x: Math.max(-520, Math.min(520, rawX)),
      y: Math.max(-220, Math.min(920, rawY)),
    };

    setHudPosition(next);

    try {
      localStorage.setItem(
        "avaaio_landscape_ava_position",
        JSON.stringify(next)
      );
    } catch {}

    return;
  }

  // =========================================
  // REAL DOM VIEWPORT CLAMP SYSTEM
  // =========================================

  const hud =
    document.querySelector(
      ".chat-feed"
    ) as HTMLElement | null;

  if (!hud) {
    setHudPosition({
      x: rawX,
      y: rawY,
    });

    return;
  }

  const rect =
    hud.getBoundingClientRect();

  const viewportWidth =
    window.innerWidth;

  const viewportHeight =
    window.innerHeight;

  const safePadding =
    window.innerWidth < 700
      ? 8
      : 18;

  const nextLeft =
    rect.left + (rawX - hudPosition.x);

  const nextRight =
    rect.right + (rawX - hudPosition.x);

  const nextTop =
    rect.top + (rawY - hudPosition.y);

  const nextBottom =
    rect.bottom + (rawY - hudPosition.y);

  let clampedX = rawX;
  let clampedY = rawY;

  // LEFT EDGE
  if (nextLeft < safePadding) {
    clampedX +=
      safePadding - nextLeft;
  }

  // RIGHT EDGE
  if (
    nextRight >
    viewportWidth - safePadding
  ) {
    clampedX -=
      nextRight -
      (viewportWidth - safePadding);
  }

  // TOP EDGE
  if (nextTop < safePadding) {
    clampedY +=
      safePadding - nextTop;
  }

  // BOTTOM EDGE
  if (
    nextBottom >
    viewportHeight - safePadding
  ) {
    clampedY -=
      nextBottom -
      (viewportHeight - safePadding);
  }

  const next = {
    x: clampedX,
    y: clampedY,
  };

  setHudPosition(next);

  try {
    localStorage.setItem(
      "avaaio_landscape_ava_position",
      JSON.stringify(next)
    );
  } catch {}
};

const endHudDrag = () => {
  dragHudRef.current.active = false;
};


useEffect(() => {
  const handleResize = () => {
    setHudPosition((prev) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const landscapeNow = w > h && h <= 520 && w <= 960;

      if (landscapeNow) {
        return {
          x: Math.max(-520, Math.min(520, prev.x)),
          y: Math.max(-220, Math.min(920, prev.y)),
        };
      }

      const maxX =
        window.innerWidth < 700
          ? window.innerWidth * 0.14
          : window.innerWidth * 0.22;

      const maxY =
        window.innerHeight < 700
          ? window.innerHeight * 0.10
          : window.innerHeight * 0.18;

      return {
        x: Math.min(maxX, Math.max(-maxX, prev.x)),
        y: Math.min(maxY, Math.max(-maxY, prev.y)),
      };
    });
  };

  window.addEventListener("resize", handleResize);

  return () =>
    window.removeEventListener("resize", handleResize);
}, []);

const resetHudPosition = () => {
  const reset = { x: 0, y: 0 };

  setHudPosition(reset);

  try {
    localStorage.setItem(
      "avaaio_landscape_ava_position",
      JSON.stringify(reset)
    );
  } catch {}
};


const consoleShellStyle: React.CSSProperties = {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1200,
      pointerEvents: "auto",

    transform: dockVisible ? "translateY(0)" : `translateY(${bottomDockHeight + 12}px)`,
    opacity: dockVisible ? 1 : 0,
    transition: "transform 240ms ease, opacity 220ms ease",
  };

  const consoleInnerStyle: React.CSSProperties = {
    width: "100%",
    display: "flex",
    flexDirection: "column",

    gap: keyboardOpen ? 1 : 3,

    padding: keyboardOpen
      ? "0px 8px calc(env(safe-area-inset-bottom,0px)+10px)"
      : "4px 12px calc(env(safe-area-inset-bottom,0px)+6px)",

    minHeight: keyboardOpen ? (isPhoneLandscape ? 58 : 70) : 112,
    maxHeight: keyboardOpen ? (isPhoneLandscape ? 58 : 70) : isPhoneLandscape ? 72 : 138,

    borderRadius: 26,

    margin: "0 auto 8px auto",

    maxWidth: isPhoneLandscape
      ? "calc(100vw - 24px)"
      : "min(1600px,98vw)",

    boxSizing: "border-box",

    background:
      "linear-gradient(180deg,rgba(3,10,22,.72) 0%,rgba(2,6,18,.82) 100%)",

    border:
      "1px solid rgba(0,180,255,.22)",

    boxShadow:
      "0 0 28px rgba(0,160,255,.18),0 0 80px rgba(0,80,255,.08)",

    backdropFilter:"blur(28px)",
    WebkitBackdropFilter:"blur(28px)",
  };

  const showHeaderRow = !(keyboardOpen && isPhoneLandscape);
  const showControlButtons = !(keyboardOpen || isPhoneLandscape); // unchanged

  const headerRowStyle: React.CSSProperties = {
    display: showHeaderRow ? "flex" : "none",
    alignItems:
      isPhoneLandscape
        ? "flex-start"
        : "center",
    justifyContent: "space-between",
    width: "100%",
    minHeight: isPhoneLandscape ? 18 : isPhone ? 16 : 18,
    padding: 0,
    margin: 0,
  };

  const controlsRowStyle: React.CSSProperties = {
    display: showControlButtons ? "flex" : "none",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: isPhoneLandscape ? 10 : isPhone ? 6 : 8,
    width: "100%",
    flexWrap: "nowrap",
    overflowX: "auto",
    overflowY: "hidden",

    // iPhone/iPad momentum scrolling
    WebkitOverflowScrolling: "touch",
    overscrollBehaviorX: "contain",
    touchAction: "pan-x",

    // smoother cinematic horizontal scroll
    scrollBehavior: "smooth",
    paddingBottom: isPhoneLandscape ? 2 : 0,
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  };

  const actionButtonStyle: React.CSSProperties = {
    flex: "0 0 auto",
    minWidth: isPhoneLandscape ? 92 : isPhone ? 78 : 86,
    height: isPhoneLandscape ? 38 : isPhone ? 34 : 36,
    borderRadius: isPhoneLandscape ? 12 : 10,
    boxSizing: "border-box",
    fontSize: isPhoneLandscape ? 14 : isPhone ? 13 : 14,
    padding: isPhoneLandscape ? "0 14px" : undefined,
  };

  const inputRowStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    flexWrap: "nowrap",

    // Keep the chat controls from collapsing
    flexShrink: 0,
    flexGrow: 0,
    flexBasis: "auto",
    minHeight: isPhoneLandscape ? 40 : isPhone ? 44 : 46,

    gap: keyboardOpen ? 4 : isPhoneLandscape ? 4 : isPhone ? 8 : 10,
    boxSizing: "border-box",
  };

  const inputWrapStyle: React.CSSProperties = {
    flex: "1 1 auto",
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    flex: "1 1 0%",
    flexBasis: 0,
    minWidth: 0,
    maxWidth: "100%",
    height: keyboardOpen ? 24 : isPhoneLandscape ? 40 : isPhone ? 42 : 44,
    borderRadius: keyboardOpen ? 12 : 12,
    boxSizing: "border-box",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    caretColor:
      isPhoneLandscape
        ? "#ffffff"
        : isMicOn && sttModeLabel === "offline"
          ? "transparent"
          : undefined,
    background: keyboardOpen ? "rgba(0, 0, 0, 0.14)" : "rgba(0, 0, 0, 0.18)",
    border: "1px solid rgba(100,180,255,0.14)",
    fontSize: "16px",  // AVAAIO: always 16px — prevents iOS Safari auto-zoom
    paddingLeft: keyboardOpen ? "10px" : undefined,
    paddingRight: keyboardOpen ? "10px" : undefined,
  };

  const sendButtonStyle: React.CSSProperties = {
    width: keyboardOpen ? 42 : 54,
    minWidth: keyboardOpen ? 42 : 54,
    height: keyboardOpen ? 42 : 54,
    minHeight: keyboardOpen ? 42 : 54,
    maxHeight: keyboardOpen ? 42 : 54,

    flexShrink: 0,
    flexGrow: 0,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "50%",

    border: "1px solid rgba(0,255,255,.45)",

    background:
      "linear-gradient(180deg,#1266b3,#0a2f57)",

    boxShadow:
      "0 0 18px rgba(0,255,255,.45), inset 0 0 10px rgba(255,255,255,.10)",

    color: "#ffffff",

    cursor: "pointer",

    transition:
      "all .18s ease",

    overflow: "visible",
};

  const footerRowStyle: React.CSSProperties = {
    display: (keyboardOpen && isPhoneLandscape) ? "none" : "flex",
    alignItems: "center",
    justifyContent:
      isPhoneLandscape
        ? "flex-start"
        : "flex-end",
    gap: 8,
    width: "100%",
    marginTop: isPhoneLandscape ? 2 : 0,
  };


  // === AVA ORB QUICK COMMAND BRIDGE START ===
  useEffect(() => {
    const onOrbCommand = (event: Event) => {
      const custom = event as CustomEvent<{ command?: string }>;
      const command = String(custom?.detail?.command || "").trim();
      if (!command) return;

      try {
        markInteractionAndUnlock();
      } catch {}

      try {
        setInput("");
      } catch {}

      void sendMessage(command);
    };

    window.addEventListener("ava-orb-command", onOrbCommand as EventListener);
    return () => window.removeEventListener("ava-orb-command", onOrbCommand as EventListener);
  }, [sendMessage, markInteractionAndUnlock]);


  // === AVA ORB HISTORY BRIDGE START ===
  useEffect(() => {

    const openHistory = () => {
      setShowHistoryPanel(true);
    };

    window.addEventListener(
      "ava-open-history",
      openHistory as EventListener
    );

    return () => {

      window.removeEventListener(
        "ava-open-history",
        openHistory as EventListener
      );

    };

  }, []);

  // === AVA ORB HISTORY BRIDGE END ===

  // === AVA ORB QUICK COMMAND BRIDGE END ===


  return (
    <>
      <div
        className="chatbox-container"
        style={{
        pointerEvents: "auto",
        }}
      >

      <style>{`
        .ava-thinking-dot {
          display: inline-block;
          margin: 0 2px;
          opacity: 0.45;
          transform: translateY(0) scale(1);

          animation: avaThinkingPulse 1.05s infinite ease-in-out;

          text-shadow:
            0 0 4px rgba(255,255,255,0.35),
            0 0 10px rgba(0,255,255,0.25);
        }

        .ava-thinking-dot:nth-child(2) {
          animation-delay: 0.18s;
        }

        .ava-thinking-dot:nth-child(3) {
          animation-delay: 0.36s;
        }

        @keyframes avaThinkingPulse {
          0%, 80%, 100% {
            opacity: 0.35;
            transform: translateY(0) scale(1);
          }

          40% {
            opacity: 1;
            transform: translateY(-5px) scale(1.28);
          }
        }


          /* __AVAAIO_THINKING_INDICATOR_PHASE1_V3__
           *
           * Visual-only animated indicator inside the existing
           * request-owned Thinking bubble.
           */
          .avaaio-thinking-indicator {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            min-height: 24px;
            color: #d8f8ff;
            font-weight: 700;
            letter-spacing: 0.045em;
            text-shadow:
              0 0 8px rgba(255,255,255,0.18),
              0 0 16px rgba(0,255,255,0.30);
          }

          .avaaio-thinking-indicator-label {
            white-space: nowrap;
          }

          .avaaio-thinking-indicator-dots {
            display: inline-flex;
            align-items: center;
            justify-content: flex-start;
            gap: 4px;
            min-width: 30px;
          }

          .avaaio-thinking-indicator-dots .ava-thinking-dot {
            width: 7px;
            height: 7px;
            margin: 0;
            border-radius: 999px;
            background: #64f3ff;
            box-shadow:
              0 0 7px rgba(100,243,255,0.92),
              0 0 15px rgba(0,200,255,0.52);
          }

          @media (prefers-reduced-motion: reduce) {
            .avaaio-thinking-indicator-dots .ava-thinking-dot {
              animation: none !important;
              opacity: 0.82;
              transform: none !important;
            }
          }

          /* __AVAAIO_THINKING_LINE_VISIBLE_V1__
           *
           * The animated line lives inside the request-owned body
           * so mobile and desktop header geometry cannot hide it.
           */
          .avaaio-thinking-indicator-stack {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: 9px;
            width: min(190px, 48vw);
            min-width: 125px;
          }

          .avaaio-thinking-line-visible {
            display: block !important;
            width: 100% !important;
            min-width: 125px;
            min-height: 5px;
            opacity: 1 !important;
            visibility: visible !important;
            overflow: visible;
          }

          .avaaio-thinking-line-visible
          .avaaio-thinking-bar-wrap {
            display: block !important;
            width: 100% !important;
            min-width: 125px !important;
            opacity: 1 !important;
            visibility: visible !important;
          }

          .avaaio-thinking-line-visible
          .avaaio-thinking-bar {
            display: block !important;
            width: 100% !important;
            min-width: 125px !important;
            opacity: 1 !important;
            visibility: visible !important;
            overflow: hidden;
          }

          .avaaio-thinking-line-visible
          .avaaio-thinking-pulse {
            display: block !important;
            opacity: 1;
            visibility: visible !important;
          }

/* __AVAAIO_THINKING_TRACK_V2__
 *
 * Independent moving track. This does not depend on the old
 * Thinking bar styles and cannot collapse to zero height.
 */
.avaaio-thinking-track {
  position: relative;
  display: block !important;
  width: 100% !important;
  min-width: 130px;
  height: 6px !important;
  min-height: 6px !important;
  margin-top: 2px;
  overflow: hidden;
  opacity: 1 !important;
  visibility: visible !important;
  border: 1px solid rgba(112, 242, 255, 0.72);
  border-radius: 999px;
  background:
    linear-gradient(
      90deg,
      rgba(43, 165, 190, 0.20),
      rgba(89, 232, 255, 0.38),
      rgba(43, 165, 190, 0.20)
    );
  box-shadow:
    inset 0 0 6px rgba(67, 225, 255, 0.36),
    0 0 8px rgba(67, 225, 255, 0.46);
}

.avaaio-thinking-runner {
  position: absolute;
  display: block !important;
  top: 0;
  bottom: 0;
  left: 0;
  width: 42%;
  min-width: 48px;
  height: 100%;
  opacity: 1 !important;
  visibility: visible !important;
  border-radius: inherit;
  background:
    linear-gradient(
      90deg,
      transparent 0%,
      rgba(112, 244, 255, 0.58) 20%,
      #ffffff 50%,
      rgba(112, 244, 255, 0.92) 72%,
      transparent 100%
    );
  box-shadow:
    0 0 8px rgba(255, 255, 255, 0.94),
    0 0 16px rgba(53, 231, 255, 0.96);
  transform: translateX(-115%);
  animation:
    avaaioThinkingTrackSweep
    1.12s
    cubic-bezier(0.42, 0, 0.58, 1)
    infinite;
  will-change: transform;
}

@keyframes avaaioThinkingTrackSweep {
  0% {
    transform: translateX(-115%);
  }

  100% {
    transform: translateX(290%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .avaaio-thinking-runner {
    animation-duration: 2.4s;
  }
}

          .chat-bubble.avaaio-thinking-persistent,
          .chat-bubble.avaaio-thinking-persistent.msg-fade-1,
          .chat-bubble.avaaio-thinking-persistent.msg-fade-2,
          .chat-bubble.avaaio-thinking-persistent.msg-fade-3,
          .chat-bubble.avaaio-thinking-persistent.msg-fade-4 {
            opacity: 1 !important;
            visibility: visible !important;
            filter: none !important;
            transform: none !important;
            pointer-events: auto !important;
            animation: none !important;
          }

          .chat-bubble.avaaio-thinking-persistent {
            min-width: 150px;
          }

          .chat-bubble.avaaio-thinking-persistent
          .avaaio-thinking-bar-wrap {
            display: inline-flex;
            width: min(180px, 48vw);
            min-width: 105px;
          }

        body.ava-audio-playing .chat-bubble.ava,
        body.ava-audio-playing .chat-bubble.ava.msg-fade-1,
        body.ava-audio-playing .chat-bubble.ava.msg-fade-2,
        body.ava-audio-playing .chat-bubble.ava.msg-fade-3,
        body.ava-audio-playing .chat-bubble.ava.msg-fade-4 {
          opacity: 1 !important;
          filter: none !important;
          transform: none !important;
          transition: none !important;
        }

      `}

</style>
<div
          className={`chat-feed ${readingPausedAt !== null ? "ava-reading-paused" : ""} ${scrollbarVisible || isAudioPlaying ? "avaaio-scrollbar-visible" : "avaaio-scrollbar-hidden"}`}
          onWheel={() => resetChatScrollbarFade(7500)}
          onTouchStart={() => resetChatScrollbarFade(7500)}
          onMouseMove={() => resetChatScrollbarFade(7500)}

          onPointerDown={beginHudDrag}
          onPointerMove={moveHudDrag}
          onPointerUp={endHudDrag}
          onPointerLeave={endHudDrag}

          onDoubleClick={resetHudPosition}

          style={{

          transform:
            isPhoneLandscape
              ? `translate(${hudPosition.x}px, ${hudPosition.y}px)`
              : "translate(0px, 0px)",

          transition:
            dragHudRef.current.active
              ? "none"
              : "transform 160ms ease",

          cursor:
              isPhoneLandscape
                ? dragHudRef.current.active
                  ? "grabbing"
                  : "grab"
                : "default",

          touchAction: isPhoneLandscape ? "none" : "auto",

          zIndex: 4,

          // =========================================
          // KEEP AVA VISUALLY CENTERED
          // =========================================

          maxWidth:
            isPhone
              ? "94vw"
              : isTablet
              ? "600px"
              : "640px",

          display: "flex",
          flexDirection: "column",

          // =========================================
          // AVAAIO SAFE HUD VIEWPORT CONSTRAINTS
          // =========================================

          width:
            isPhone
              ? "100%"
              : "100%",

          overflowX: "hidden",

          // =========================================
          // AVAAIO SPATIAL RESPONSIVE LAYOUT
          // =========================================

          paddingLeft:
            isPhone
              ? "10px"
              : "18px",

          paddingRight:
            isPhone
              ? "92px"
              : "88px",

          alignItems:
            isPhone
              ? "flex-start"
              : isPhoneLandscape
              ? "flex-start"
              : "flex-start",

          justifyContent:
            isPhone
              ? "flex-end"
              : "flex-end",

          paddingTop:
            isPhoneLandscape
              ? "4px"
              : isPhone
              ? "calc(6vh + 46px)"
              : "calc(5vh + 42px)",
            paddingBottom: `${feedBottomPadding}px`,




          position: "relative",
          pointerEvents: "auto",

          overflowY: "auto",

          WebkitOverflowScrolling: "touch",

            // Real vertical mobile scrolling

            // iPhone/iPad momentum scrolling

            // Prevent scroll chaining issues
            overscrollBehaviorY: "contain",

            // Native touch scrolling

            // Better reading space

            // More cinematic viewport height
            minHeight: 0,

            maxHeight:
                isPhoneLandscape
                  ? `${landscapeFeedMaxHeight}px`
                  : keyboardOpen
                  ? `${keyboardFeedMaxHeight}px`
                  : isPhone
                  ? "72vh"
                  : "70vh",

            scrollPaddingBottom: `${feedBottomPadding}px`,

            scrollBehavior:
                keyboardOpen || isPhoneLandscape
                  ? "auto"
                  : "smooth",
          }}>
<div className="ava-chat-scroll-fade">
            {messages.map((m) => (
              <div
                key={m.id}
                role={m.is_thinking ? "status" : undefined}
                aria-live={m.is_thinking ? "polite" : undefined}
                aria-atomic={m.is_thinking ? true : undefined}
                className={`chat-bubble ${m.sender === "user" ? "user" : m.source === "error" ? "error" : "ava"} ${m.is_thinking ? "avaaio-thinking-persistent" : ""} ${
                  m.sender !== "user" && (m.is_thinking || readingPausedAt !== null || isAudioPlaying)
                    ? ""
                    : fadeClass(
                        m.ts,
                        m.sender !== "user" ? lastAvaSpeechEndRef.current : null
                      )
                }`}
                style={
                  m.sender !== "user"
                    ? {
                        background: isPhone || isPhoneLandscape
                          ? "linear-gradient(180deg, rgba(8,14,24,0.42) 0%, rgba(3,8,18,0.56) 100%)"
                          : "rgba(6,10,18,0.72)",

                        border: "1px solid rgba(0,255,255,0.06)",

                        maxWidth:
                          isPhone
                            ? "min(62vw, 320px)"
                            : isPhoneLandscape
                            ? "min(48vw, 420px)"
                            : isTablet
                            ? "min(42vw, 520px)"
                            : "min(34vw, 680px)",


                        wordBreak: "break-word",

                        overflowWrap: "break-word",

                        boxShadow: isPhone || isPhoneLandscape
                          ? "0 0 22px rgba(0,255,255,0.10), 0 0 48px rgba(0,140,255,0.08)"
                          : "0 0 18px rgba(0,255,255,0.08)",

                        backdropFilter:
                          isPhone || isPhoneLandscape
                            ? "blur(18px)"
                            : "blur(24px)",

                        WebkitBackdropFilter:
                          isPhone || isPhoneLandscape
                            ? "blur(18px)"
                            : "blur(24px)",

                        position: "relative",

                        overflowX: "visible",
                          overflowY: "visible",

                        // =====================================
                        // AVAAIO TOP-ORIGIN MESSAGE FLOW
                        // =====================================

                        minHeight: "fit-content",
                          height: "auto",
                          maxHeight: "none",

                        display: "flex",
                        flexDirection: "column",

                        justifyContent: "flex-start",
                        alignItems: "flex-start",

                        scrollBehavior: "smooth",

                        WebkitOverflowScrolling: "touch",

                        transition:
                          "background 220ms ease, box-shadow 220ms ease, border-color 220ms ease",

                        marginLeft:
                          m.sender !== "user"
                            ? (isPhone || isPhoneLandscape ? "0px" : "auto")
                            : "auto",

                        marginRight:
                          m.sender === "user"
                            ? "0px"
                            : "auto",

                        alignSelf:
                          m.sender === "user"
                            ? "flex-end"
                            : "flex-start",

                        ...(readingPausedAt !== null
                          ? {
                        opacity: 1,
                        filter: "none",
                        transform: "none",
                        transition: "none",
                          background: isPhone || isPhoneLandscape ? "rgba(10, 16, 26, 0.58)" : undefined,
                          border: isPhone || isPhoneLandscape ? "1px solid rgba(0,255,255,0.10)" : undefined,
                          boxShadow: isPhone || isPhoneLandscape
                            ? "0 0 18px rgba(0,255,255,0.10), 0 0 40px rgba(0,140,255,0.08)"
                            : undefined,
                          backdropFilter: isPhone || isPhoneLandscape ? "blur(16px)" : undefined,
                          WebkitBackdropFilter: isPhone || isPhoneLandscape ? "blur(16px)" : undefined,
                          zIndex: isPhone || isPhoneLandscape ? 3 : undefined,
                      }
                          : {})
                      }
                    : undefined
                }
              >
                
{showHudBeacon && m.sender !== "user" && (
  <div
    style={{
      position: "absolute",
      top: "10px",
      right: "12px",
      width: "12px",
      height: "12px",
      borderRadius: "999px",
      background: "#00ffff",
      boxShadow:
        "0 0 14px rgba(0,255,255,0.95), 0 0 32px rgba(0,180,255,0.55)",
      animation: "avaPulse 1.8s infinite",
      zIndex: 5,
    }}
  />
)}

<div style={{ fontSize: "13px", opacity: 0.75, marginBottom: isPhone || isPhoneLandscape ? "10px" : "4px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", width: "100%" }}>
                    
{m.sender !== "user" && (
  isAudioPlaying ||
  isSpeaking ||
  readingPausedAt !== null ||
  Date.now() - (lastAvaSpeechEndRef.current || 0) < 30000
) && (
  <button
    type="button"
    aria-label={
      readingPausedAt === null
        ? "Pause Ava response"
        : "Resume Ava response"
    }
    title={
      readingPausedAt === null
        ? "Pause response"
        : "Resume response"
    }
    onClick={toggleReadingPause}
    style={{
      width: 28,
      height: 28,
      minWidth: 28,

      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      borderRadius: "999px",

      background:
        "radial-gradient(circle at center, rgba(255,255,255,0.10) 0%, rgba(0,0,0,0.82) 100%)",

      border: "1px solid rgba(0,255,255,0.75)",

      color: "#ffffff",

      boxShadow:
        "0 0 12px rgba(255,255,255,0.14), 0 0 20px rgba(0,255,255,0.06)",

      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",

      cursor: "pointer",

      transition:
        "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",

      padding: 0,
      flexShrink: 0,
    }}
  >
    {readingPausedAt === null ? (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "3px",
        }}
      >
        <div
          style={{
            width: "3px",
            height: "11px",
            borderRadius: "999px",
            background: "#ffffff",
          }}
        />
        <div
          style={{
            width: "3px",
            height: "11px",
            borderRadius: "999px",
            background: "#ffffff",
          }}
        />
      </div>
    ) : (
      <div
        style={{
          width: 0,
          height: 0,
          borderTop: "6px solid transparent",
          borderBottom: "6px solid transparent",
          borderLeft: "9px solid white",
          marginLeft: "2px",
        }}
      />
    )}
  </button>
)}

                      <span
                        style={{
                          fontWeight: 900,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: m.sender === "user" ? "#ffffff" : "#7ee7ff",
                          textShadow:
                            m.sender === "user"
                              ? "0 0 8px rgba(255,255,255,0.16)"
                              : "0 0 16px rgba(0,255,255,0.55), 0 0 28px rgba(0,140,255,0.35)",
                        }}
                      >
                      {m.sender === "user"
                      ? "YOU"
                      : m.source === "openai"
                      ? "AVA (fallback)"
                      : m.source === "error"
                      ? "AVA (error)"
                      : readingPausedAt === null ? "AVA" : "AVA (paused)"}
                    </span>

                      <span
                        style={{
                          fontSize: "11px",
                          opacity: 0.58,
                          color: "#d8f6ff",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {new Date(m.ts).toLocaleTimeString(interfaceCopy.chat.locale, {
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </span>


                    {m.sender !== "user" && !m.is_thinking && (m as any).thinking && (
                      <span
                        title={(m as any).thinking}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "3px",
                          opacity: 0.85,
                          fontSize: "16px",
                          lineHeight: 1,
                        }}
                      >
                        <div className="avaaio-thinking-bar-wrap">
                          <div className="avaaio-thinking-bar">
                            <div className="avaaio-thinking-pulse"></div>
                          </div>
                        </div>
                      </span>
                    )}
                    </div>
                </div>
                  <div
                    style={{
                      fontSize: isPhoneLandscape ? "14px" : "15px",
                      lineHeight: 1.5,
                      whiteSpace: "pre-wrap",
                      textTransform: "none",
                      letterSpacing: "normal",
                      fontVariantCaps: "normal",
                      background: "transparent",
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    {(() => {
                      
if (m.is_thinking) {
  return (
    <span
      className="avaaio-thinking-indicator-stack"
    >
      <span
        className="avaaio-thinking-indicator"
      >
        <span
          className="avaaio-thinking-indicator-label"
                  >
            {interfaceCopy.chat.thinking}
          </span>

        <span
          className="avaaio-thinking-indicator-dots"
          aria-hidden="true"
        >
          <span className="ava-thinking-dot" />
          <span className="ava-thinking-dot" />
          <span className="ava-thinking-dot" />
        </span>
      </span>

      <span
        className="avaaio-thinking-track"
        aria-hidden="true"
      >
        <span className="avaaio-thinking-runner" />
      </span>
    </span>
  );
}

const raw = String(m.text || "");

const renderReadAlongText = (
  displayText: string
) => {
  if (
    m.sender === "user" ||
    m.id !== readAlongMessageId
  ) {
    return displayText;
  }

  const pieces =
    displayText.split(/(\s+)/);

  let currentWord = -1;

  return pieces.map(
    (piece, pieceIndex) => {
      if (/^\s+$/.test(piece)) {
        return piece;
      }

      currentWord += 1;

      const active =
        currentWord ===
        readAlongWordIndex;

      return (
        <span
          key={`${m.id}-${pieceIndex}`}
          data-ava-read-word={
            currentWord
          }
          data-ava-read-active={
            active
              ? "true"
              : undefined
          }
          style={{
            display: "inline",
            borderRadius: "5px",
            padding:
              active
                ? "1px 3px"
                : "1px 0",
            margin:
              active
                ? "0 -1px"
                : 0,
            color:
              active
                ? "#ffffff"
                : "inherit",
            background:
              active
                ? "rgba(0,255,255,0.22)"
                : "transparent",
            boxShadow:
              active
                ? "0 0 10px rgba(0,255,255,0.42)"
                : "none",
            textShadow:
              active
                ? "0 0 8px rgba(126,231,255,0.60)"
                : "inherit",
            transition:
              "background 80ms linear, box-shadow 80ms linear",
          }}
        >
          {piece}
        </span>
      );
    }
  );
};


                      const letters = raw.replace(/[^A-Za-z]/g, "");
                      const upperRatio =
                        letters.length > 8
                          ? letters
                              .split("")
                              .filter((c) => c === c.toUpperCase()).length /
                            letters.length
                          : 0;

                      if (upperRatio < 0.72) {
                          return renderReadAlongText(raw);
                        }

                      const PRESERVE = new Set([
                        "AVAAIO",
                        "AVA",
                        "AI",
                        "API",
                        "TTS",
                        "STT",
                        "USA",
                        "HTML",
                        "CSS",
                        "JS",
                        "JSON",
                        "GPT",
                      ]);

                      return renderReadAlongText(raw
                        .split(/(\s+)/)
                        .map((word) => {
                          const clean = word.replace(/[^A-Z]/g, "");

                          if (
                            PRESERVE.has(clean) ||
                            /^https?:/i.test(word)
                          ) {
                            return word;
                          }

                          if (/^[A-Z0-9\W]+$/.test(word) && clean.length > 2) {
                            return (
                              word.charAt(0) +
                              word.slice(1).toLowerCase()
                            );
                          }

                          return word;
                        })
                        .join(""));
                    })()}
                  </div>

                {m.sender !== "user" && m.sources && m.sources.length > 0 && (
                  <div style={{
                    marginTop: "8px",
                    padding: "6px 8px",
                    background: "transparent",
                    borderRadius: "0px",
                    fontSize: "12px"
                  }}>
                    <div style={{ opacity: 0.7, marginBottom: isPhone || isPhoneLandscape ? "10px" : "4px" }}>Sources</div>
                    <ul style={{ paddingLeft: "14px", margin: 0 }}>
                      {m.sources.map((s: any, i: number) => (
                        <li key={i}>
                          <a
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: themeColors.accent, textDecoration: "none" }}
                          >
                            {s.title || s.url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}

            

              {false && isTyping && (
                <div className="chat-bubble ava">
                  <div style={{ fontSize: "13px", opacity: 0.75, marginBottom: isPhone || isPhoneLandscape ? "10px" : "4px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", width: "100%" }}>
                      AVA
                    </div>
                  </div>

                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 10px",
                      borderRadius: "12px",
                      background: "transparent",
                      border: "none",
                    }}
                  >
                    <div
                        className="avaaio-thinking-bar-wrap"
                        title={interfaceCopy.chat.thinkingTitle}
                      >
                      <div className="avaaio-thinking-bar">
                        <div className="avaaio-thinking-pulse"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={chatEndRef} />
          </div>
        </div>
      </div>



      <div className="holo-console" style={consoleShellStyle}>
        <div style={consoleInnerStyle}>
          <div
            style={{
              ...headerRowStyle,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              paddingTop: "5px",
              paddingBottom: "3px",
              boxSizing: "border-box",
            }}>
            <div
              className="console-clock"
              style={{
                flex: 1,
                margin: 0,
                paddingLeft: "10px",
                textAlign: "left",
                fontSize: isPhoneLandscape ? "11px" : isPhone ? "11px" : "12px",
                opacity: 0.82,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            >
              {dateStr}
            </div>

            <div
              className="console-date"
              style={{
                flex: 1,
                margin: 0,
                paddingRight: "10px",
                marginLeft: isPhoneLandscape ? "32px" : "0px",
                                fontSize: isPhoneLandscape ? "11px" : isPhone ? "11px" : "12px",
                opacity: 0.82,
                letterSpacing: "0.08em",
                whiteSpace: "nowrap",
                textAlign: "right",
              }}
            >
              {clock}
            </div>
          </div>

          <div
            style={{
              ...controlsRowStyle,
            }}
          >
            {!isCompact && (
              <div
                style={{
                  ...actionButtonStyle,
                  minWidth: isPhoneLandscape ? 104 : 96,
                  visibility: "hidden",
                  pointerEvents: "none",
                  flex: "0 0 auto",
                }}
              />
            )}
          </div>

          <div
            style={{
              ...inputRowStyle,
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden",
              boxSizing: "border-box",
              flexShrink: 1,
            }}>
            <div style={{ ...inputWrapStyle, position: "relative", flex: 1, minWidth: 0, width: "100%", maxWidth: "100%" }}>
              <input
                type="text"
                className="console-input"

                                  placeholder={
                    interfaceCopy.chat.inputPlaceholder
                  }
                  value={input}
                readOnly={false}
                onChange={(e) => {
                  if (isMicOn && sttModeLabel === "offline") return;
                  setInput(e.target.value);
      markInteractionAndUnlock();
                }}
                onKeyDown={(e) => {
                  markUnlocked();
                  // UX FIX:
                  // Allow conversational interruption even while Ava is responding.
                  if (e.key === "Enter") {
                    clearFinalSendTimer();
                    void sendMessage();
                  }
                }}
                onFocus={() => {
      markInteractionAndUnlock();
                }}
                style={{
                  ...inputStyle,
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                  textAlign: "left",
                  textIndent: 0,
                  fontSize: isPhone ? "16px" : "15px",
                  paddingLeft: isPhone ? "14px" : "12px",
                  paddingRight:
                    isPhoneLandscape
                      ? "48px"
                      : isPhone
                      ? "112px"
                      : "56px",
                }}
              />

                <button
                  type="button"
                  onClick={handleMicToggle}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    toggleVoiceControl();
                  }}
                    title={
                      micConversationMode === "ava_speaking_lockout"
                        ? "Tap to interrupt Ava"
                        : micConversationMode === "wake_standby"
                          ? interfaceCopy.chat.wakeStandbyTitle
                          : isMicOn
                            ? interfaceCopy.chat.micOffTitle
                            : interfaceCopy.chat.micOnTitle
                    }
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: isMicOn || isListening
                        ? "translateY(-50%) scale(1.06)"
                        : "translateY(-50%) scale(1)",
                    height: "36px",
                    width: "36px",
                    borderRadius: "50%",
                      border:
                        micConversationMode === "ava_speaking_lockout"
                          ? "1px solid #facc15"
                          : isMicOn
                            ? `1px solid ${themeColors.accent}`
                            : `1px solid ${themeColors.border}`,
                    cursor: "pointer",
                      background:
                        micConversationMode === "ava_speaking_lockout"
                          ? "linear-gradient(135deg, #fde047, #eab308)"
                          : isMicOn
                            ? `linear-gradient(135deg, ${themeColors.accent}, ${themeColors.buttonBg || themeColors.accent})`
                            : (themeColors.panelBg || themeColors.cardBg),
                      color:
                        micConversationMode === "ava_speaking_lockout"
                          ? "#171717"
                          : isMicOn
                            ? themeColors.bg
                            : themeColors.text,
                    fontSize: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                      boxShadow:
                        micConversationMode === "ava_speaking_lockout"
                          ? "0 0 16px rgba(250, 204, 21, 0.95), 0 0 34px rgba(250, 204, 21, 0.65)"
                          : isMicOn || isListening
                            ? `0 0 14px ${themeColors.accentGlow}, 0 0 28px ${themeColors.accentGlow}`
                            : `0 0 6px ${themeColors.accentGlow}`,
                    transition: "all 0.2s ease",
              }}
            >
              {(micConversationMode === "ava_speaking_lockout" || isMicOn || isListening) && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    inset: "-4px",
                    borderRadius: "999px",
                      border:
                        micConversationMode === "ava_speaking_lockout"
                          ? "1px solid rgba(250, 204, 21, 0.95)"
                          : `1px solid ${themeColors.accentGlow}`,
                    opacity: 0.45,
                    animation: "avaMicPulse 1.15s ease-out infinite",
                    pointerEvents: "none",
                  }}
                />
              )}
              
<svg
  className="avaaio-mic-icon"
  width="20"
  height="20"
  viewBox="0 0 24 24"
  fill="none"
  aria-hidden="true"
>
  <path
    d="M12 15.2a4.2 4.2 0 0 0 4.2-4.2V7.2A4.2 4.2 0 0 0 12 3a4.2 4.2 0 0 0-4.2 4.2V11a4.2 4.2 0 0 0 4.2 4.2Z"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
  />
  <path
    d="M5.5 11.2A6.5 6.5 0 0 0 12 17.7a6.5 6.5 0 0 0 6.5-6.5"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
  />
  <path
    d="M12 17.7V21"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
  />
  <path
    d="M8.5 21h7"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
  />
</svg>

            </button>
            </div>

            

            <button
              type="button"
              className="console-btn send"
              onClick={() => {
      markInteractionAndUnlock();
                clearFinalSendTimer();
                void primeAudioOnce();

                if (false && sendLocked) {
                  pushMessage({
                    id: uid(),
                    sender: "ava",
                    text: "You’ve reached today’s free Ava limit. Some features are locked until your limit resets. Please come back tomorrow or upgrade to unlock more.",
                    ts: Date.now(),
                  });
                  return;
                }

                // UX FIX:
                // Never block conversational interruption.
                // Force-unlock stale runtime state if detected,
                // but ALWAYS allow sendMessage to proceed.

                if (isSending || sendingLockRef.current) {
                  forceUnlockSend("send button detected stuck state");
                }

                void sendMessage();
              }}
              
style={{
                  ...sendButtonStyle,

                    background:
                      "radial-gradient(circle at 35% 30%, rgba(155,210,255,0.95) 0%, rgba(37,119,255,0.42) 34%, rgba(2,8,24,0.92) 76%, rgba(0,0,0,0.98) 100%)",
                    border: "1px solid rgba(120,155,255,0.85)",
                    color: "#ffffff",
                    boxShadow:
                      "0 0 16px rgba(120,155,255,0.55), 0 0 34px rgba(0,120,255,0.28), inset 0 0 18px rgba(255,255,255,0.08)",

                  width: isPhoneLandscape ? "40px" : isPhone ? "44px" : "46px",
                  height: isPhoneLandscape ? "40px" : isPhone ? "44px" : "46px",

                  minWidth: isPhoneLandscape ? "40px" : isPhone ? "44px" : "46px",
                  minHeight: isPhoneLandscape ? "40px" : isPhone ? "44px" : "46px",

                  maxWidth: isPhone ? "44px" : "46px",
                  maxHeight: isPhone ? "44px" : "46px",

                  borderRadius: "999px",

                  aspectRatio: "1 / 1",

                  flexShrink: 0,

                  alignSelf: "center",

                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",

                  padding: 0,

                  lineHeight: 1,

                  fontSize: isPhone ? "12px" : "13px",
                }}
              >

                  <svg
                    className="avaaio-send-icon"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 12L20 4L15 20L11 13L3 12Z"
                      fill="currentColor"
                    />
                  </svg>

            </button>
          </div>

          {!(keyboardOpen && isPhoneLandscape) && (
          <div
            className="avaaio-console-disclaimer"
            style={{
              width: "100%",
              textAlign: "center",
              fontSize: isPhoneLandscape ? "10px" : "10px",
              opacity: 0.55,
              color: themeColors.mutedText,
              marginTop: keyboardOpen ? "2px" : "10px",
              marginBottom: keyboardOpen ? "0px" : "6px",
              letterSpacing: "0.04em",
              lineHeight: 1.2,
            pointerEvents: "auto",
              userSelect: "none",
            }}
          >

            {interfaceCopy.chat.disclaimer}
          </div>
        )}
{isPhone && (
          <div
              className="avaaio-console-footer-row"
              style={{
                ...footerRowStyle,
                display: "flex",
                visibility: "visible",
                opacity: 1,
                marginTop: "2px",
                paddingTop: "0px",
                paddingBottom: "0px",
              }}
            >

            <button
              type="button"
              className="console-btn logout"
              onClick={() => {
                markInteractionAndUnlock();
                void handleLogout();
              }}
              style={{
                ...actionButtonStyle,
              }}
            >
              {interfaceCopy.chat.logout}
            </button>
          </div>
)}

        </div>
      </div>


        <ChatHistoryPanel
          open={showHistoryPanel}
          messages={messages}
          isPhone={isPhone}
          themeColors={themeColors}
          onClose={() => setShowHistoryPanel(false)}
          onClearAll={clearAllChatHistory}
        />

        {/* AVA LOGOUT FADE OVERLAY */}
        {isLoggingOutVisual && (
          <div
            aria-hidden="true"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 2147483646,
              background: "rgba(0,0,0,0.92)",
            pointerEvents: "auto",
              opacity: 1,
              transition: "opacity 160ms ease",
            }}
          />
        )}

        {showSettings && (
          <div
            ref={settingsRef}
            className="settings-panel right"
            style={{
              position: "fixed",
              right: isPhone ? "12px" : "24px",
              left: isPhone ? "12px" : "auto",
              bottom: isPhoneLandscape ? `${bottomDockHeight - 6}px` : `${bottomDockHeight + 12}px`,
              top: "auto",
              zIndex: 9999,
            pointerEvents: "auto",
              maxWidth: isPhone ? "none" : "420px",
            }}
          >
            <h3>{interfaceCopy.chat.menuTitle}</h3>

            <ul>

              <li>
              </li>

              <li>
                <button className="settings-link-btn" onClick={() => go("/profile")}>
                  {interfaceCopy.chat.profile}
                </button>
              </li>
              <li>
                <button className="settings-link-btn" onClick={() => go("/account")}>
                  {interfaceCopy.chat.account}
                </button>
              </li>
              <li>
                <button className="settings-link-btn" onClick={() => goCommunity()}>
                  {interfaceCopy.chat.entertainment}
                </button>
              </li>
              <li>
                <button className="settings-link-btn" onClick={() => goMedia()}>
                  {interfaceCopy.chat.games}
                </button>
              </li>
              <li>
                <button className="settings-link-btn" onClick={() => go("/theme")}>
                  {interfaceCopy.chat.themeColors}
                </button>
              </li>

              <li>
                <button className="logout-btn" onClick={handleLogout}>
                  {interfaceCopy.chat.logoutReturn}
                </button>
              </li>
            </ul>
          </div>
        )}

</>
    );
};

export default ChatBox;
